import $ from 'jquery';
import scrollIntoView from 'scroll-into-view-if-needed';
import { fromEvent, debounce, timer, ReplaySubject, iif, from, of, merge } from 'rxjs';
import { z } from 'zod';
import { filter, mergeMap, distinct } from 'rxjs/operators';

const PROJECT_CLASSES = {
  AVOID_CHANGE_DETECTION: "u1st-avoid-change-detection",
  TRIGGER_ELEMENT: "u1st-trigger-element",
  PREV_TABINDEX: "u1st-prev-tabindex",
  PREV_ARIA_HIDDEN: "u1st-prev-ariaHidden",
  SR_ONLY: "u1st-sr-only",
  SKIP_LINK: "u1st_skipLink",
  DISPLAY_BLOCK: "u1st-display-block",
  DISPLAY_NONE: "u1st-display-none",
  CAROUSEL: "u1st-carousel",
  ALERT: "u1st-alert",
  TEXT_NODE_PARENT: "u1st-textNodeParent",
  MAX_HEIGHT: "u1st-maxHeight",
  TABBABLE_ELEMENT: "u1st-tabbable-element",
  FOCUSED_ELEMENT: "u1st-focused",
  ACTIVE_ELEMENT: "u1st-active",
  EXCLUDE_ATTRIBUTE: "u1-exclude",
  HIDE_ATTRIBUTE: "u1-hide"
};
const PROJECT_ATTRIBUTES = {
  ADDED_DESCRIPTION: "u1st-added-description",
  FONT_SIZE: "u1st-font-size",
  RADIO_INDEX: "u1st-radio-index",
  CELL_INDEX: "u1st-cell-index",
  ROW_INDEX: "u1st-row-index",
  CALENDAR_INDEX: "u1st-calendar-index"
};
const COMMON_SELECTORS = {
  TABBABLE_ELEMENTS_SELECTOR: `a[href]:not([tabindex='-1']),
  button:not([tabindex='-1']),
  input:not([tabindex='-1']),
  select:not([tabindex='-1']),
  textarea:not([tabindex='-1']),
  [contenteditable]:not([tabindex='-1']),
  [tabindex]:not([tabindex='-1'])`,
  FOCUSABLE_ELEMENTS_SELECTOR: `a[href],button,input,select,textarea,[contenteditable]`,
  HEADING_ELEMENTS_SELECTOR: `h1,h2,h3,h4,h5,h6`,
  U1_ELEMENTS: `[u1-button], [u1-link], [u1-item], [u1-trigger]`
};
const ARIA = {
  LABELLEDBY: `aria-labelledby`,
  MODAL: `aria-modal`,
  HIDDEN: `aria-hidden`,
  ACTIVEDESCENDANT: `aria-activedescendant`,
  EXPANDED: `aria-expanded`,
  HASPOPUP: `aria-haspopup`,
  SELECTED: `aria-selected`,
  LABEL: `aria-label`,
  REQUIRED: `aria-required`,
  INVALID: `aria-invalid`,
  DESCRIBEDBY: `aria-describedby`,
  OWNS: `aria-owns`,
  CONTROLS: `aria-controls`,
  AUTOCOMPLETE: `aria-autocomplete`,
  LIVE: `aria-live`,
  ATOMIC: `aria-atomic`,
  DISABLED: `aria-disabled`,
  PRESSED: `aria-pressed`,
  LEVEL: "aria-level",
  ROLEDESCRIPTION: `aria-roledescription`,
  CHECKED: `aria-checked`,
  SORT: `aria-sort`,
  CURRENT: `aria-current`
};
const ATTRIBUTES = {
  ROLE: "role",
  TABINDEX: "tabindex",
  ID: "id",
  FOR: "for",
  PLACEHOLDER: "placeholder",
  VALUE: "value",
  TYPE: "type",
  ALT: "alt",
  TITLE: "title"
};
const TAG_NAMES = {
  INPUT: "input",
  TEXTAREA: "textarea",
  SELECT: "select",
  LABEL: "label",
  DIV: "div",
  SPAN: "span",
  A: "a",
  OBJECT: "object",
  H1: "h1",
  TR: "tr",
  TABLE: "table"
};
const KEYBOARD_EVENT_CODE$1 = {
  TAB: "Tab",
  ENTER: "Enter",
  ESC: "Escape",
  SPACE: "Space",
  PAGEUP: "PageUp",
  PAGEDOWN: "PageDown",
  END: "End",
  HOME: "Home",
  LEFT: "ArrowLeft",
  UP: "ArrowUp",
  RIGHT: "ArrowRight",
  DOWN: "ArrowDown"
};
const EVENT_TYPES = {
  CLICK: "click",
  KEYDOWN: "keydown",
  FOCUS_IN: "focusin",
  FOCUS_OUT: "focusout"
};
const ROLE$1 = {
  FORM: `form`,
  SEARCH: `search`,
  ALERT: `alert`,
  STATUS: `status`,
  LINK: `link`,
  LISTBOX: `listbox`,
  OPTION: `option`,
  BUTTON: `button`,
  GROUP: `group`,
  TAB: `tab`,
  TABLIST: `tablist`,
  TABPANEL: `tabpanel`,
  RADIO: `radio`,
  RADIOGROUP: `radiogroup`,
  CHECKBOX: `checkbox`,
  IMG: `img`,
  DIALOG: `dialog`,
  GRID: `grid`,
  GRIDCELL: `gridcell`,
  COLUMNHEADER: `columnheader`,
  ROWHEADER: `rowheader`,
  ROW: `row`,
  CELL: `cell`,
  TABLE: `table`,
  HEADING: `heading`,
  REGION: `region`,
  TOOLTIP: `tooltip`,
  METER: `meter`,
  PRESENTATION: `presentation`
};
const DIRECTION = {
  START: "start",
  END: "end"
};
const MENU_IFRAME_ID = "u1st-accessibility-menu";
const U1_ACCESSIBILITY_BUTTON = "Accessibility Button";
const CLICKABLE_HTML_ELEMENTS = "a, button, input, textarea, select, [tabindex='0']";

class MenuMessages {
  static send(msg) {
    var _a;
    const iframe = document.getElementById(MENU_IFRAME_ID);
    (_a = iframe == null ? void 0 : iframe.contentWindow) == null ? void 0 : _a.postMessage(msg, "*");
  }
}

class Logger {
  constructor() {
    var _a;
    this.env = (_a = "production") == null ? void 0 : _a.trim();
    this.logCollection = [];
    window.addEventListener("beforeunload", () => this.sendLogs());
  }
  static getInstance() {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  info(msg) {
    if (this.VerifyEnv("development" /* Development */)) {
      console.info(msg);
    }
  }
  error(msg) {
    if (this.VerifyEnv("development" /* Development */)) {
      console.error(msg);
    } else if (this.VerifyEnv("production" /* Production */)) {
      this.logCollection.push(msg.toString());
    }
  }
  sendLogs() {
    if (this.logCollection.length > 0 && this.VerifyEnv("production" /* Production */)) {
      MenuMessages.send({
        action: "sendLog",
        url: location.href,
        logMsg: JSON.stringify(this.logCollection)
      });
    }
  }
  VerifyEnv(requiredEnv) {
    return this.env === requiredEnv;
  }
}

function catchable(_, __, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function(...args) {
    try {
      return originalMethod.apply(this, args);
    } catch (error) {
      const logger = Logger.getInstance();
      logger.error(error);
    }
  };
}

var __defProp$8 = Object.defineProperty;
var __getOwnPropDesc$7 = Object.getOwnPropertyDescriptor;
var __decorateClass$7 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$7(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$8(target, key, result);
  return result;
};
const _ChangeDetection = class {
  constructor(elementVisibility, domProvider) {
    this.observer = null;
    this.logger = Logger.getInstance();
    this.elementVisibility = elementVisibility;
    this.activeElementsDictionary = [];
    this.domProvider = domProvider;
    this.contextDictionary = {};
    this.config = {
      attributes: true,
      attributeFilter: ["class", "style"],
      childList: true,
      subtree: true
    };
  }
  static getInstance(elementVisibility, domProvider) {
    if (!_ChangeDetection.instance) {
      _ChangeDetection.instance = new _ChangeDetection(elementVisibility, domProvider);
    }
    return _ChangeDetection.instance;
  }
  reset() {
    _ChangeDetection.instance = void 0;
  }
  createObserver() {
    this.observer = this.domProvider.setInterval(() => {
      this.handle();
    }, 1e3);
  }
  register(contextSelector, selector, name, onVisible, onHidden, avoidMarkingDetected) {
    const sub = this.contextDictionary[contextSelector];
    const subSelector = sub && sub[selector];
    const subName = subSelector && subSelector[name];
    const data = {
      [name]: {
        onVisible,
        onHidden,
        avoidMarkingDetected
      }
    };
    if (subName) {
      return;
    } else if (subSelector) {
      subSelector[name] = data[name];
    } else if (sub) {
      sub[selector] = data;
    } else {
      this.contextDictionary[contextSelector] = {
        [selector]: data
      };
    }
    if (!this.observer) {
      this.createObserver();
    }
  }
  subscribe(contextSelector, name, selector, onVisible, onHidden, avoidMarkingDetected) {
    if (Array.isArray(selector)) {
      selector.forEach(
        (selector2) => this.register(contextSelector, selector2, name, onVisible, onHidden, avoidMarkingDetected)
      );
    } else
      this.register(contextSelector, selector, name, onVisible, onHidden, avoidMarkingDetected);
  }
  handle() {
    const subArray = Object.entries(this.contextDictionary);
    this.onRemove();
    for (const [contextSelector, subscribersData] of subArray) {
      if (this.domProvider.querySelector(contextSelector)) {
        for (const [selector, data] of Object.entries(subscribersData)) {
          const elements = this.domProvider.querySelectorAll(selector, contextSelector);
          for (const element of elements) {
            const markAsDetected = this.isDetected(element);
            const isVisible = this.elementVisibility.isVisible(element);
            if (element && !markAsDetected && isVisible || this.shouldInvokeOnFound(data)) {
              this.onVisible(element, data, selector);
            } else if (markAsDetected && !isVisible) {
              this.onHidden(element, data);
            }
          }
        }
      }
    }
  }
  shouldInvokeOnFound(data) {
    const allowedFixers = ["focusTo", "excludeEl", "skipLink"];
    const shouldInvoke = !!allowedFixers.find((fixer) => data[fixer]);
    return shouldInvoke;
  }
  isDetected(element) {
    const markAsDetected = element && element.getAttribute(PROJECT_CLASSES.AVOID_CHANGE_DETECTION);
    return markAsDetected;
  }
  onVisible(element, data, selector) {
    const dataArray = Object.entries(data);
    for (const [name, { onVisible, onHidden, avoidMarkingDetected }] of dataArray) {
      onVisible(element);
      if (!avoidMarkingDetected) {
        this.domProvider.setAttribute(element, PROJECT_CLASSES.AVOID_CHANGE_DETECTION, "true");
      }
      if (onHidden) {
        this.activeElementsDictionary.push({
          selector,
          element,
          onHidden
        });
      }
    }
  }
  onRemove() {
    for (let i = 0; i < this.activeElementsDictionary.length; i++) {
      const activeElement = this.activeElementsDictionary[i];
      if (!this.domProvider.contains(activeElement.element)) {
        try {
          activeElement.onHidden(activeElement.element);
        } catch (e) {
          if (e instanceof Error) {
            this.logger.error(e.message);
          }
        }
        this.activeElementsDictionary.splice(i, 1);
      }
    }
  }
  onHidden(element, data) {
    const isMarkedAsHidden = this.domProvider.getAttribute(element, "aria-hidden");
    if (isMarkedAsHidden === "true") {
      const dataArray = Object.entries(data);
      let onHidenInvoked = false;
      for (const [name, callback] of dataArray) {
        try {
          if (callback.onHidden) {
            callback.onHidden(element);
          }
          onHidenInvoked = true;
        } catch (error) {
        }
      }
      if (onHidenInvoked) {
        this.domProvider.removeAttribute(element, PROJECT_CLASSES.AVOID_CHANGE_DETECTION);
      }
    }
  }
};
let ChangeDetection = _ChangeDetection;
__decorateClass$7([
  catchable
], ChangeDetection.prototype, "handle", 1);
__decorateClass$7([
  catchable
], ChangeDetection.prototype, "onVisible", 1);

const AVOID_AUTO_HIDDEN_ATTRIBUTE = "u1st-avoid-auto-hidden";
const AVOID_AUTO_TABBABLE_ATTRIBUTE = "u1st-avoid-auto-tabbable";
class ElementVisibility {
  isVisible(element, avoidAriaHidden) {
    if (!element)
      return false;
    let isVisible = true;
    const originalElement = element;
    const isInViewPort = this.isInViewPort(originalElement);
    while (element && element.tagName != "HTML") {
      const elementStyle = window.getComputedStyle(element);
      const isClipValid = this.isVisibleClip(elementStyle);
      if ((this.isHidden(elementStyle) || !isInViewPort || !isClipValid) && !element.getAttribute("u1-visible")) {
        isVisible = false;
        break;
      }
      element = element.parentElement;
    }
    if (!avoidAriaHidden && !(originalElement == null ? void 0 : originalElement.getAttribute(AVOID_AUTO_HIDDEN_ATTRIBUTE))) {
      this.setAriaHidden(originalElement, isVisible);
      this.setTabbables(originalElement, isVisible);
    }
    return isVisible;
  }
  isVisibleOnPage(element) {
    if (!element)
      return false;
    let isVisible = true;
    while (element && element.tagName != "HTML") {
      const elementStyle = window.getComputedStyle(element);
      const isClipValid = this.isVisibleClip(elementStyle);
      if (this.isHidden(elementStyle) || !isClipValid) {
        isVisible = false;
        break;
      }
      element = element.parentElement;
    }
    return isVisible;
  }
  isHidden(elementStyle) {
    return elementStyle.display == "none" || elementStyle.visibility == "hidden" || elementStyle.height == "0px" && (elementStyle.overflow == "auto" || elementStyle.overflow == "hidden") || elementStyle.width == "0px" || elementStyle.opacity == "0" || elementStyle.transform == "translateX(0%)";
  }
  isInViewPort(element) {
    const clientRect = element == null ? void 0 : element.getBoundingClientRect();
    return clientRect.right >= 0 && clientRect.left <= (window.innerWidth || document.documentElement.clientWidth);
  }
  isVisibleClip(elementStyle) {
    let clip = elementStyle.clip != "" ? elementStyle.clip : elementStyle.clipPath;
    let isClipValid = true;
    if (clip.startsWith("rect")) {
      clip = clip.split("(")[1].split(")")[0];
      const topClip = Number(clip.split("px")[0]);
      const rightClip = Number(clip.split(",")[1].split("px")[0]);
      const bottomClip = Number(clip.split(",")[2].split("px")[0]);
      const leftClip = Number(clip.split(",")[3].split("px")[0]);
      if (topClip >= bottomClip && leftClip >= rightClip || topClip == 0 && rightClip == 0 && bottomClip == 0 && leftClip == 0) {
        isClipValid = false;
      }
    }
    return isClipValid;
  }
  setAriaHidden(element, isVisible) {
    if (element) {
      if (isVisible) {
        element.removeAttribute(ARIA.HIDDEN);
      } else {
        element.setAttribute(ARIA.HIDDEN, "true");
      }
    }
  }
  setTabbables(element, isVisible) {
    const tabbableElements = Array.from(
      element.querySelectorAll(
        `${COMMON_SELECTORS.FOCUSABLE_ELEMENTS_SELECTOR}, .${PROJECT_CLASSES.TABBABLE_ELEMENT}`
      )
    ).filter((element2) => element2.getAttribute(AVOID_AUTO_TABBABLE_ATTRIBUTE) !== "true");
    const isElementTabbable = element.matches(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR) && element.getAttribute(AVOID_AUTO_TABBABLE_ATTRIBUTE) !== "true";
    if (isElementTabbable)
      tabbableElements.unshift(element);
    tabbableElements.forEach((el) => this.setTabIndex(el, isVisible));
  }
  setTabIndex(element, isVisible) {
    const tabindex = isVisible ? 0 : -1;
    if (!element.matches(COMMON_SELECTORS.FOCUSABLE_ELEMENTS_SELECTOR) || element.getAttribute(ARIA.CHECKED) === "true") {
      element.classList.add(PROJECT_CLASSES.TABBABLE_ELEMENT);
    }
    element.setAttribute(ATTRIBUTES.TABINDEX, tabindex.toString());
  }
  getVisibleElements(selector, context) {
    const contextElement = context != null ? context : document;
    return Array.from(contextElement.querySelectorAll(selector)).filter(
      (element) => this.isVisible(element)
    );
  }
}
const testIsVisibile = (element) => {
  return new ElementVisibility().isVisible(element);
};

var __defProp$7 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$7(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
const camelize = (string) => string.replace(
  /[A-Z]/g,
  (character, index) => index === 0 ? character.toLowerCase() : character
);
function inject(...serviceConstructors) {
  return function(targetConstructor) {
    return class FixerWithServices extends targetConstructor {
      constructor() {
        super(...arguments);
        this.services = serviceConstructors.reduce(
          (servicesAccumulator, ServiceConstructor) => __spreadProps$1(__spreadValues$1({}, servicesAccumulator), {
            [camelize(ServiceConstructor.name)]: new ServiceConstructor()
          }),
          {}
        );
      }
    };
  };
}

function partials(...constructors) {
  return function(targetConstructor) {
    constructors.forEach((constructor) => {
      Object.getOwnPropertyNames(constructor.prototype).forEach((name) => {
        Object.defineProperty(
          targetConstructor.prototype,
          name,
          Object.getOwnPropertyDescriptor(constructor.prototype, name) || /* @__PURE__ */ Object.create(null)
        );
      });
    });
  };
}

var __defProp$6 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDesc$6 = Object.getOwnPropertyDescriptor;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$6(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __decorateClass$6 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$6(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$6(target, key, result);
  return result;
};
var _a;
const _StateManagement = class {
  constructor() {
    this.u1stConfiguration = (_a = window.u1) == null ? void 0 : _a.config;
  }
  static getInstance() {
    if (!_StateManagement.instance) {
      _StateManagement.instance = new _StateManagement();
    }
    return _StateManagement.instance;
  }
  getLang() {
    var _a2;
    return (_a2 = window.u1) == null ? void 0 : _a2.lang;
  }
  getConfigurationStyle(element) {
    var _a2, _b, _c;
    return (_c = (_b = (_a2 = window.u1) == null ? void 0 : _a2.config) == null ? void 0 : _b[element]) == null ? void 0 : _c.style;
  }
};
let StateManagement = _StateManagement;
__decorateClass$6([
  catchable
], StateManagement.prototype, "getConfigurationStyle", 1);
function addDataToWindow(data) {
  window.u1 = __spreadValues(__spreadValues({}, window.u1), data);
}
const setConfiguration = (u1Configuration) => {
  window.u1 = __spreadProps(__spreadValues({}, window.u1), { config: u1Configuration });
};
const setLanguage = (language) => addDataToWindow({ lang: language });

var __async$2 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class Utils$1 {
  static mod(a, n) {
    return (a % n + n) % n;
  }
  static uuid() {
    return "u1st-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c == "x" ? r : r & 3 | 8;
      return v.toString(16);
    });
  }
  static isMobileDevice() {
    if (navigator.userAgent.match(/Android/i) || navigator.userAgent.match(/Phone/i) || navigator.userAgent.match(/Mobile/i)) {
      return true;
    }
    return false;
  }
  static isChrome(navigator2) {
    return navigator2.userAgent.match(/Chrome/i) ? true : false;
  }
  static isSafari(navigator2) {
    return navigator2.userAgent.match(/Safari/i) ? true : false;
  }
  static isFireFox(navigator2) {
    return navigator2.userAgent.match(/Firefox/i) ? true : false;
  }
  static waitForTimeout(domProvider, time) {
    return __async$2(this, null, function* () {
      return new Promise((resolve, reject) => {
        domProvider.setTimeout(() => {
          resolve();
        }, time);
      });
    });
  }
  static isRtl(langCode) {
    const rtlLang = ["he", "ar"];
    return rtlLang.includes(langCode);
  }
  static getOrCreateId(element) {
    element.id || (element.id = this.uuid());
    return element.id;
  }
  static getOrCreateIdSelector(element) {
    return `[id='${this.getOrCreateId(element)}']`;
  }
  static isContainsTabbable(htmlElements) {
    return [...htmlElements].some(
      (htmlElement) => htmlElement.querySelector(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR)
    );
  }
  static isAsc(htmlElements) {
    return this.sortAndCompare(
      htmlElements,
      (cellA, cellB) => cellA == null ? void 0 : cellA.innerText.localeCompare(cellB == null ? void 0 : cellB.innerText)
    );
  }
  static isDesc(htmlElements) {
    return this.sortAndCompare(
      htmlElements,
      (cellA, cellB) => cellB == null ? void 0 : cellB.innerText.localeCompare(cellA == null ? void 0 : cellA.innerText)
    );
  }
  static getFirstTabbableElements(tabbableElements) {
    return tabbableElements.map((focusableCell) => {
      const child = focusableCell.querySelector(
        COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR
      );
      return child != null ? child : focusableCell;
    });
  }
  static getSharedParent(elementsSelector, context) {
    const elToSearch = context != null ? context : document;
    const allElements = elToSearch.querySelectorAll(elementsSelector);
    if (allElements) {
      let isFound = false;
      let parent = allElements[0];
      while (!isFound) {
        parent = parent.parentElement;
        const elsInsideLength = parent == null ? void 0 : parent.querySelectorAll(elementsSelector).length;
        if (elsInsideLength === allElements.length)
          isFound = true;
      }
      return parent;
    }
  }
  // todo: should make smarter approach
  static sortAndCompare(htmlElements, comparator) {
    return [...htmlElements].sort(comparator).every(
      (value, idx) => {
        var _a;
        return (value == null ? void 0 : value.innerText) === ((_a = htmlElements[idx]) == null ? void 0 : _a.innerText);
      }
    );
  }
  static addContextToSelectors(props, container) {
    for (const key in props.selectors) {
      if (key !== "container" && typeof props.selectors[key] === "string") {
        props.selectors[key] = `${container} ${props.selectors[key]}`;
      }
    }
  }
  static findParentWithAttribute(element, attributeName, attributeValue) {
    while (element.parentElement) {
      element = element.parentElement;
      if (element.hasAttribute(attributeName) || attributeValue && element.getAttribute(attributeName) === attributeValue) {
        return element;
      }
    }
    return null;
  }
  static isAssistiveTechEvent(event) {
    return event.offsetX === 0 && event.offsetY === 0;
  }
  static hideElement(element) {
    element.setAttribute(ARIA.HIDDEN, "true");
    element.setAttribute(ATTRIBUTES.TABINDEX, "-1");
  }
  static makeElementTabbable(element) {
    element.setAttribute(ARIA.HIDDEN, "false");
    element.setAttribute(ATTRIBUTES.TABINDEX, "0");
  }
}
Utils$1.removeClientsWCAGAttributes = (elements, attributesToRemove) => {
  const defaultAttributes = ["role", "tabindex"];
  let finalAttributesArray = [];
  if (attributesToRemove) {
    finalAttributesArray = defaultAttributes.concat(attributesToRemove);
  } else {
    finalAttributesArray = defaultAttributes;
  }
  elements.forEach((elm) => {
    if (elm.id.startsWith("u1st")) {
      return;
    }
    finalAttributesArray.forEach((attr) => {
      elm.removeAttribute(attr);
    });
  });
};
//TODO: Refactor this method - change the name to be more specific, add handling of tabindex etc. Decide if it's a Util...
Utils$1.setRolePresentation = (element, elementsToExcludeSelectors) => {
  let elementsInsideMainElement;
  if (elementsToExcludeSelectors) {
    elementsInsideMainElement = Array.prototype.slice.call(
      element.querySelectorAll("*:not(" + elementsToExcludeSelectors + ")"),
      0
    );
  } else {
    elementsInsideMainElement = Array.prototype.slice.call(element.querySelectorAll("*"), 0);
  }
  elementsInsideMainElement.forEach((el) => {
    el.setAttribute("role", "presentation");
  });
};
Utils$1.isNotWhiteSpaceCharacter = (str) => {
  return str.length === 1 && str.match(/\S/) ? true : false;
};

class EventListenerHandler {
  constructor() {
    this.addEventListener = (element, eventType, funcToExecute, useCapture = false) => {
      element.addEventListener(eventType, this.handleEventsMap(element, funcToExecute), useCapture);
    };
    this.removeEventListener = (element, eventType, funcToExecute) => {
      element.removeEventListener(eventType, funcToExecute);
    };
    this.handleEventsMap = (element, funcToExecute) => {
      const eventsMap = this.eventsMap;
      if (!eventsMap.has(element)) {
        eventsMap.set(element, /* @__PURE__ */ new Map());
      }
      const elementMap = eventsMap.get(element);
      const hash = this.hashCode(funcToExecute.toString());
      if (!elementMap.has(hash)) {
        elementMap.set(hash, funcToExecute);
      }
      return elementMap.get(hash);
    };
    this.hashCode = (str) => {
      let hash = 0;
      if (str.length == 0)
        return hash;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      return hash;
    };
    this.eventsMap = /* @__PURE__ */ new Map();
  }
  static Instance() {
    if (!EventListenerHandler._instance) {
      EventListenerHandler._instance = new EventListenerHandler();
    }
    return EventListenerHandler._instance;
  }
  removeFromMap(element, funcToRemove) {
    throw new Error("Method not implemented.");
  }
  eventsMapSizeOnEl(el) {
    var _a, _b;
    return (_b = (_a = this.eventsMap.get(el)) == null ? void 0 : _a.size) != null ? _b : 0;
  }
  reset() {
    this.eventsMap.clear();
  }
}

class DomProvider {
  constructor(domDocument = window.document, domWindow = window) {
    this.domDocument = domDocument;
    this.domWindow = domWindow;
    this.getDomDocument = () => {
      return this.domDocument;
    };
    this.getDocumentElement = () => {
      return this.domDocument.documentElement;
    };
    this.getLang = () => {
      return this.domDocument.documentElement.lang;
    };
    this.getBody = () => {
      return this.domDocument.body;
    };
    this.getHtml = () => {
      return this.getBody().parentElement;
    };
  }
  getWindow() {
    return this.domWindow;
  }
  removeElement(element) {
    element.remove();
  }
  removeElements(elements) {
    elements.forEach((el) => {
      el.remove();
    });
  }
  getHead() {
    return this.domDocument.head;
  }
  getDomWindow() {
    return this.domWindow;
  }
  setTimeout(callback, timer) {
    this.domWindow.setTimeout(() => callback(), timer);
  }
  setInterval(callback, timer) {
    const interval = this.domWindow.setInterval(() => callback(interval), timer);
    return interval;
  }
  getNavigator() {
    return this.domWindow.navigator;
  }
  querySelector(selector, context = this.domDocument.body) {
    if (context === "body")
      context = this.domDocument.body;
    const results = selector === "body" ? $(selector) : $(selector, context);
    return results.length > 0 ? results[0] : null;
  }
  querySelectorAll(selector, context = this.domDocument.body) {
    if (context === "body")
      context = this.domDocument.body;
    const $elements = selector === "body" ? $(selector) : $(selector, context);
    return Array.prototype.slice.call($elements, 0);
  }
  getElementById(id) {
    return this.domDocument.getElementById(id);
  }
  getNextSiblingAttr(element, attribute) {
    return element.nextElementSibling ? element.nextElementSibling.getAttribute(attribute) : null;
  }
  getPrevSiblingAttr(element, attribute) {
    return element.previousElementSibling ? element.previousElementSibling.getAttribute(attribute) : null;
  }
  getAttribute(element, name) {
    return element == null ? void 0 : element.getAttribute(name);
  }
  setAttribute(element, name, value) {
    element == null ? void 0 : element.setAttribute(name, value);
  }
  setAttributeToAll(element, name, value) {
    element.forEach((el) => {
      el.setAttribute(name, value);
    });
  }
  addClass(element, className) {
    element == null ? void 0 : element.classList.add(className);
  }
  removeClass(element, className) {
    element == null ? void 0 : element.classList.remove(className);
  }
  removeAttribute(element, name) {
    element == null ? void 0 : element.removeAttribute(name);
  }
  removeAttributeToAll(element, name) {
    element.forEach((el) => {
      el.removeAttribute(name);
    });
  }
  focusOnElement(element, custom = false) {
    element == null ? void 0 : element.focus();
    if (custom) {
      const u1Focus = new CustomEvent("u1focus");
      element.dispatchEvent(u1Focus);
    }
  }
  matches(element, selector) {
    return $(element).is(selector);
  }
  addSingleEventListener(element, eventType, funcToExecute, useCapture = false) {
    if (element) {
      const eventListenerProvider = EventListenerHandler.Instance();
      const executeAndRemove = (evt) => {
        funcToExecute(evt);
        eventListenerProvider.removeEventListener(element, eventType, executeAndRemove);
      };
      eventListenerProvider.addEventListener(element, eventType, executeAndRemove, useCapture);
    }
  }
  addEventListener(element, eventType, funcToExecute, useCapture = false) {
    if (element) {
      const eventListenerProvider = EventListenerHandler.Instance();
      eventListenerProvider.addEventListener(element, eventType, funcToExecute, useCapture);
    }
  }
  addEventListenerToAll(elements, eventType, funcToExecute, useCapture = false) {
    elements.forEach((el) => {
      this.addEventListener(el, eventType, funcToExecute, useCapture);
    });
  }
  hasClass(element, className) {
    return element.classList.contains(className);
  }
  append(element, context, id) {
    if (this.querySelector(`[id='${id}']`))
      return;
    element.id = id;
    context.append(element);
  }
  appendChild(element, context, id) {
    if (this.querySelector(`[id='${id}']`))
      return;
    element.id = id;
    context.appendChild(element);
  }
  prepend(element, context, id) {
    if (this.querySelector(`[id='${id}']`, context))
      return;
    element.id = id;
    context.prepend(element);
  }
  insertAfter(insertAfter, elmToInsert, id) {
    if (this.querySelector(`[id='${id}']`))
      return;
    elmToInsert.id = id;
    insertAfter.after(elmToInsert);
  }
  insertBefore(insertBefore, elmToInsert, id) {
    if (this.querySelector(`[id='${id}']`))
      return;
    elmToInsert.id = id;
    insertBefore.before(elmToInsert);
  }
  dispatchClickEvent(element) {
    element.click();
  }
  dispatchMouseClickEvent(element) {
    const clickEvent = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(clickEvent);
  }
  dispatchMouseDownEvent(element) {
    const clickEvent = new MouseEvent("mousedown", {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(clickEvent);
  }
  dispatchMouseOverEvent(element) {
    const mouseEvent = new MouseEvent("mouseover", {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(mouseEvent);
  }
  dispatchMouseEvent(element, type) {
    const mouseEvent = new MouseEvent(type, {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(mouseEvent);
  }
  dispatchMouseOutEvent(element) {
    const mouseEvent = new MouseEvent("mouseout", {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(mouseEvent);
  }
  dispatchCustomClickEvent(element, customEvent) {
    const clickEvent = new MouseEvent(customEvent, {
      view: window,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(clickEvent);
  }
  dispatchKeydownEvent(element, key, bubbles = false) {
    element.dispatchEvent(new KeyboardEvent("keydown", { key, bubbles, code: key }));
  }
  getTextContent(element) {
    return element.textContent;
  }
  getInnerText(element) {
    return element.innerText;
  }
  getTagName(element) {
    return element.tagName;
  }
  createElement(tagName) {
    return this.domDocument.createElement(tagName);
  }
  getElementProperty(element, property) {
    return element[property];
  }
  innerText(el, text) {
    el.innerText = text;
  }
  addStyle(styleContent, id) {
    const styleElement = this.domDocument.createElement("style");
    styleElement.innerText = styleContent;
    const headElement = document.head;
    this.append(styleElement, headElement, id);
  }
  closest(el, selector, startFromParent) {
    const $el = $(el);
    const $parent = $el.parent();
    return startFromParent ? $parent && $parent.closest(selector)[0] : $el.closest(selector)[0];
  }
  contains(el, context = this.domDocument.body) {
    return context.contains(el);
  }
  changeValue(el, newVale) {
    if (el.tagName.toLowerCase() !== TAG_NAMES.INPUT)
      return;
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set.call(
      el,
      newVale
    );
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.value = newVale;
  }
  setAttributeWithID(fromElement, toElement, attribute) {
    if (!fromElement || !toElement)
      return;
    let id = this.getAttribute(fromElement, "id");
    if (!id) {
      id = Utils$1.uuid();
      this.setAttribute(fromElement, "id", id);
    }
    this.setAttribute(toElement, attribute, id);
  }
  getActiveElement() {
    return document.activeElement;
  }
  setProperty(el, ruleName, value, isImportant) {
    if (!el)
      return;
    isImportant ? el.style.setProperty(ruleName, value, "important") : el.style.setProperty(ruleName, value);
  }
  removeProperty(el, ruleName) {
    if (!el)
      return;
    el.style.removeProperty(ruleName);
    !el.style.length && this.removeAttribute(el, "style");
  }
  cloneElement(elementToClone) {
    return elementToClone.cloneNode(true);
  }
  insertAdjacentHTML(element, position, text) {
    element.insertAdjacentHTML(position, text);
  }
  insertAdjacentElement(element, position, elToInsert) {
    element.insertAdjacentElement(position, elToInsert);
  }
  hasChildNodes(element) {
    return element.hasChildNodes();
  }
  removeEventListener(element, eventType, callback) {
    element.removeEventListener(eventType, callback);
  }
  hasAttribute(element, attributeName) {
    return element.hasAttribute(attributeName);
  }
  title() {
    return this.domDocument.title;
  }
  scrollIntoView(element, options = {
    behavior: "smooth",
    block: "nearest",
    inline: "nearest"
  }) {
    scrollIntoView(element, options);
  }
  setCustomValidity(element, value) {
    element == null ? void 0 : element.setCustomValidity(value);
  }
  getPreviousSiblingWithSelector(element, selector) {
    let sibling = element.previousElementSibling;
    while (sibling) {
      if (sibling.matches(selector)) {
        return sibling;
      }
      sibling = sibling.previousElementSibling;
    }
    return null;
  }
}

const ACCESSIBILITY_BUTTON_ID = "u1-accessibility-button";
const ACCESSIBILITY_ICON = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='25' height='30' viewBox='0 0 25 30'%3E%3Cg id='Group_2' data-name='Group 2' transform='translate(57 -21.604)'%3E%3Ccircle id='Ellipse_2' data-name='Ellipse 2' cx='3.5' cy='3.5' r='3.5' transform='translate(-48 21.604)' fill='%23fff'/%3E%3C/g%3E%3Cpath id='Path_7' data-name='Path 7' d='M443.339,303.731l-11-1-1,1,9,3v5l-3,13h2l4-10h1l4,10h2l-3-13v-5l9-3-1-1Z' transform='translate(-431.339 -294.731)' fill='%23fff'/%3E%3C/svg%3E%0A`;
const U1_SHOULD_ACTIVATE_ACCESSIBILITY_KEY = "u1_should-activate-accessibility";
const _AccessibilityButton = class {
  constructor() {
    this.getBackgroundColor = () => {
      var _a;
      const isActive = _AccessibilityButton.getShouldActivateAccessibility();
      const defaultColor = "linear-gradient(180deg, #4A00CB 0%, #7914C5 100%)";
      const customColor = (_a = this.stateManagement.getConfigurationStyle("button")) == null ? void 0 : _a.background;
      return this.getAccessibilityButtonStyle(customColor != null ? customColor : defaultColor, isActive ? 1 : 0.8);
    };
    this.getAccessibilityButtonStyle = (bgColor, opacity = 1) => `
    #u1-accessibility-button {
      border: none;
      border-radius: 50px;
      width: 50px;
      height: 50px;
      background-size: 100%;
      background-repeat: no-repeat;
      background-position: center;
      position: fixed !important;
      background: ${bgColor} !important;
      opacity: ${opacity} !important;
      bottom: 20px;
      left: 20px;
      z-index: 2147483647 !important;
      cursor: pointer;
      padding: .5rem .5rem;
    }
  
    #u1-accessibility-button:before {
      content: url("${ACCESSIBILITY_ICON}");
    }
`;
    this.domProvider = new DomProvider();
    this.stateManagement = StateManagement.getInstance();
    this.button = this.domProvider.createElement("button");
    if (_AccessibilityButton.getShouldShow()) {
      this.domProvider.setAttribute(this.button, ARIA.LABEL, U1_ACCESSIBILITY_BUTTON);
      !this.domProvider.getElementById(ACCESSIBILITY_BUTTON_ID) && this.domProvider.append(this.button, document.body, ACCESSIBILITY_BUTTON_ID);
      this.domProvider.addEventListener(this.button, "click", () => this.toggleAccessibility());
      this.addStyle();
    }
  }
  toggleAccessibility() {
    const shouldActivateAccessibility = _AccessibilityButton.getShouldActivateAccessibility();
    localStorage.setItem(
      U1_SHOULD_ACTIVATE_ACCESSIBILITY_KEY,
      String(!shouldActivateAccessibility)
    );
    location.reload();
  }
  addStyle() {
    const style = this.getBackgroundColor();
    this.domProvider.addStyle(style, `${ACCESSIBILITY_BUTTON_ID}-style`);
  }
  static getShouldActivateAccessibility() {
    const serializedShouldActivateAccessibility = localStorage.getItem(
      U1_SHOULD_ACTIVATE_ACCESSIBILITY_KEY
    );
    return serializedShouldActivateAccessibility === null ? false : JSON.parse(serializedShouldActivateAccessibility);
  }
};
let AccessibilityButton = _AccessibilityButton;
AccessibilityButton.initialize = () => {
  if (_AccessibilityButton.instance === void 0) {
    _AccessibilityButton.instance = new _AccessibilityButton();
  }
};
AccessibilityButton.getShouldShow = () => {
  var _a, _b, _c, _d;
  return (_d = (_c = (_b = (_a = window == null ? void 0 : window.u1stConfiguration) == null ? void 0 : _a.desktop) == null ? void 0 : _b.button) == null ? void 0 : _c.display) != null ? _d : false;
};

var AccessibilityButton$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  AccessibilityButton: AccessibilityButton
});

const allowedLocalHostnames = ["localhost", "127.0.0.1"];
const API_PREFIX = "prod" ;
const RuntimeUtils = (() => {
  let isDomainValid = false;
  const queue = [];
  const isClientSide = () => typeof window !== "undefined";
  const handleDomainIsValid = () => {
    isDomainValid = true;
    while (queue.length > 0) {
      const callback = queue.shift();
      if (callback !== void 0) {
        callback();
      }
    }
  };
  const handleRuntimeCheck = () => {
    if (allowedLocalHostnames.includes(window.location.hostname)) {
      handleDomainIsValid();
    } else {
      fetch(`https://${API_PREFIX}-backend.user1st.com/api/v1/projects/permissions/runtime`, {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json"
        }
      }).then((response) => response.json()).then((json) => {
        if (JSON.parse(json.isValid)) {
          handleDomainIsValid();
        } else {
          console.error(
            "There is an issue with your domain. Please login to the U1 dashboard for details."
          );
        }
      });
    }
  };
  const runRuntimeCheck = () => {
    if (AccessibilityButton.getShouldShow()) {
      if (AccessibilityButton.getShouldActivateAccessibility()) {
        handleRuntimeCheck();
      }
    } else {
      handleRuntimeCheck();
    }
  };
  if (isClientSide()) {
    if (document.readyState === "complete") {
      runRuntimeCheck();
    } else {
      window.addEventListener("load", runRuntimeCheck);
    }
  }
  return {
    isClientSide,
    runIfDomainIsValid: (callback) => {
      if (isDomainValid) {
        callback();
      } else {
        queue.push(callback);
      }
    }
  };
})();

class VisualFocusFixer {
  constructor(domProvider, elementVisbiilty) {
    this.defaultColor = "black";
    this.secondaryDefaultColor = "white";
    this.defaultWidth = 3;
    this.domProvider = domProvider;
    this.elementVisibility = elementVisbiilty;
    this.stateManagement = StateManagement.getInstance();
  }
  fix() {
    const targetElement = this.domProvider.getDocumentElement();
    const activateOnTab = (e) => {
      const eventKey = e.key;
      if (eventKey === "Tab") {
        this.setVisualFocus();
        this.domProvider.removeEventListener(targetElement, "keydown", activateOnTab);
      }
    };
    this.domProvider.addEventListener(targetElement, "keydown", activateOnTab);
  }
  setVisualFocus() {
    var _a, _b;
    const customStyle = this.getCustomStyle();
    const primaryColor = (_a = customStyle == null ? void 0 : customStyle.color) != null ? _a : this.defaultColor;
    const secondaryColor = (_b = customStyle == null ? void 0 : customStyle.secondaryColor) != null ? _b : this.secondaryDefaultColor;
    document.body.style.setProperty("--outline-color", primaryColor);
    document.body.style.setProperty("--secondary-outline-color", secondaryColor);
    if (customStyle == null ? void 0 : customStyle.doubleBorder) {
      this.setDoubleBorder(secondaryColor);
    } else {
      this.setRegularBorder(customStyle);
    }
  }
  setRegularBorder(customStyle) {
    var _a;
    const customWidth = customStyle == null ? void 0 : customStyle.width;
    const outlineWidth = !customWidth ? this.defaultWidth : customWidth <= 2 ? 2 : customWidth;
    document.body.style.setProperty("--outline-width", `${String(outlineWidth)}px`);
    document.body.style.setProperty("--outline-offset", `${String((_a = customStyle == null ? void 0 : customStyle.offset) != null ? _a : 2)}px`);
    (customStyle == null ? void 0 : customStyle.customProperty) && document.body.style.setProperty("--custom-property", customStyle == null ? void 0 : customStyle.customProperty);
  }
  setDoubleBorder(outerBorderColor) {
    const boxShadowProp = `0 0 0 4px ${outerBorderColor}`;
    document.body.style.setProperty("--outline-width", `2px`);
    document.body.style.setProperty("--outline-offset", `0px`);
    document.body.style.setProperty("--outer-border", boxShadowProp);
  }
  getCustomStyle() {
    return this.stateManagement.getConfigurationStyle("visualFocus");
  }
}
const visualFocusApi = () => new VisualFocusFixer(new DomProvider(), new ElementVisibility()).fix();

const FOCUS_TO_ATTRIBUTE = "u1-focus-to";
const FOCUSED_ATTR = `u1-focused`;
const ALL_FOCUS_TO_ATTRIBUTES = `[data-${FOCUS_TO_ATTRIBUTE}]:not([${FOCUSED_ATTR}]), [${FOCUS_TO_ATTRIBUTE}]:not([${FOCUSED_ATTR}])`;
class FocusToFixer {
  constructor(context, domProvider, changeDetection) {
    this.focusOnElement = () => {
      const nextElToFocus = this.domProvider.querySelector(ALL_FOCUS_TO_ATTRIBUTES);
      if (nextElToFocus) {
        this.domProvider.setAttribute(nextElToFocus, FOCUSED_ATTR, "true");
        this.domProvider.setAttribute(nextElToFocus, ATTRIBUTES.TABINDEX, "0");
        this.domProvider.focusOnElement(nextElToFocus);
        this.domProvider.setTimeout(() => {
          this.domProvider.setAttribute(nextElToFocus, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
          this.domProvider.removeAttribute(nextElToFocus, FOCUS_TO_ATTRIBUTE);
          this.domProvider.removeAttribute(nextElToFocus, FOCUSED_ATTR);
          this.domProvider.removeAttribute(nextElToFocus, ATTRIBUTES.TABINDEX);
          const anchor = this.domProvider.getElementById("u1-anchor");
          anchor == null ? void 0 : anchor.remove();
        }, 500);
      }
    };
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.context = context;
  }
  fix() {
    this.changeDetection.subscribe(
      this.context,
      "focusTo",
      ALL_FOCUS_TO_ATTRIBUTES,
      () => this.focusOnElement()
    );
  }
  createAnchorAndFocusTo(focusTo) {
    let anchor = this.domProvider.getElementById("u1-anchor");
    if (!anchor) {
      anchor = document.createElement("a");
      anchor.id = "u1-anchor";
      anchor.classList.add("u1st-sr-only");
      focusTo == null ? void 0 : focusTo.prepend(anchor);
    }
    this.domProvider.setAttribute(anchor, FOCUS_TO_ATTRIBUTE, "true");
  }
}
const focusToApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  new FocusToFixer(context, domProvider, changeDetection).fix();
};

const EXCLUDED_ATTRIBUTE = "u1-excluded";
const ALL_EXCLUDE_ATTRIBUTES = `[${PROJECT_CLASSES.EXCLUDE_ATTRIBUTE}='true']:not([${EXCLUDED_ATTRIBUTE}]), [data-${PROJECT_CLASSES.EXCLUDE_ATTRIBUTE}='true']:not([${EXCLUDED_ATTRIBUTE}])`;
const ALL_HIDE_ATTRIBUTES = `[${PROJECT_CLASSES.HIDE_ATTRIBUTE}='true']:not([${EXCLUDED_ATTRIBUTE}]), [data-${PROJECT_CLASSES.HIDE_ATTRIBUTE}='true']:not([${EXCLUDED_ATTRIBUTE}])`;
class ExcludeElFixer {
  constructor(context, domProvider, changeDetection) {
    this.hideElement = (elToHide) => {
      this.domProvider.setAttribute(elToHide, ARIA.HIDDEN, "true");
      this.domProvider.setAttribute(elToHide, ATTRIBUTES.TABINDEX, "-1");
      this.markExcludedEl(elToHide);
    };
    this.excludeElement = (elToExclude) => {
      this.domProvider.setAttribute(elToExclude, ATTRIBUTES.ROLE, ROLE$1.PRESENTATION);
      this.domProvider.setAttribute(elToExclude, ATTRIBUTES.TABINDEX, "-1");
      this.markExcludedEl(elToExclude);
    };
    this.markExcludedEl = (elToExclude) => {
      this.domProvider.setAttribute(elToExclude, EXCLUDED_ATTRIBUTE, "true");
      this.domProvider.setAttribute(elToExclude, AVOID_AUTO_HIDDEN_ATTRIBUTE, "true");
      this.domProvider.setAttribute(elToExclude, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
    };
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.context = context;
  }
  fix() {
    this.changeDetection.subscribe(
      this.context,
      "hideEl",
      ALL_HIDE_ATTRIBUTES,
      (visibleElement) => {
        setTimeout(() => {
          this.hideElement(visibleElement);
        }, 1e3);
      }
    );
    this.changeDetection.subscribe(
      this.context,
      "excludeEl",
      ALL_EXCLUDE_ATTRIBUTES,
      (visibleElement) => {
        setTimeout(() => {
          this.excludeElement(visibleElement);
        }, 1e3);
      }
    );
  }
}
const excludeElApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  new ExcludeElFixer(context, domProvider, changeDetection).fix();
};

const STATUS_ID = "u1-status-element";
const STATUS_ATTR = "u1-status";

class StatusFixer {
  constructor(context, domProvider, changeDetection) {
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.context = context;
    const statusElement = this.domProvider.getElementById(STATUS_ID);
    if (statusElement) {
      this.statusEl = statusElement;
    } else {
      this.statusEl = this.domProvider.createElement("div");
      this.domProvider.setAttribute(this.statusEl, ATTRIBUTES.ROLE, ROLE$1.STATUS);
      this.domProvider.setAttribute(this.statusEl, ATTRIBUTES.ID, STATUS_ID);
      this.domProvider.addClass(this.statusEl, PROJECT_CLASSES.SR_ONLY);
      this.domProvider.insertAdjacentElement(
        this.domProvider.getBody(),
        "beforeend",
        this.statusEl
      );
    }
  }
  fix() {
    this.changeDetection.subscribe(
      this.context,
      "status",
      `[${STATUS_ATTR}], [data-${STATUS_ATTR}]`,
      (statusEl) => this.onStatusShow(statusEl)
    );
  }
  onStatusShow(statusEl) {
    const statusElLabel = this.domProvider.getAttribute(statusEl, ARIA.LABEL);
    this.announceStatus(statusElLabel != null ? statusElLabel : statusEl.innerText);
    this.domProvider.removeAttribute(statusEl, STATUS_ATTR);
    this.domProvider.removeAttribute(statusEl, `data-${STATUS_ATTR}`);
    this.domProvider.removeAttribute(statusEl, PROJECT_CLASSES.AVOID_CHANGE_DETECTION);
  }
  announceStatus(status) {
    this.domProvider.innerText(this.statusEl, status.trim());
    this.domProvider.setTimeout(() => {
      this.domProvider.innerText(this.statusEl, "");
    }, 3e3);
  }
}
const statusApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  new StatusFixer(context, domProvider, changeDetection).fix();
};

const en = {
  OPEN_MENU_SKIP_LINK: "Open Accessibility Menu",
  ACCESSIBILITY_BUTTON: "Accessibility",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const he = {
  OPEN_MENU_SKIP_LINK: "\u05E4\u05EA\u05D7 \u05EA\u05E4\u05E8\u05D9\u05D8 \u05E0\u05D2\u05D9\u05E9\u05D5\u05EA",
  ACCESSIBILITY_BUTTON: "\u05E0\u05D2\u05D9\u05E9\u05D5\u05EA",
  NEXT_MONTH: "\u05D7\u05D5\u05D3\u05E9 \u05D4\u05D1\u05D0",
  NEXT_YEAR: "\u05E9\u05E0\u05D4 \u05D4\u05D1\u05D0\u05D4",
  PREV_MONTH: "\u05D7\u05D5\u05D3\u05E9 \u05E7\u05D5\u05D3\u05DD",
  PREV_YEAR: "\u05E9\u05E0\u05D4 \u05E7\u05D5\u05D3\u05DE\u05EA",
  CHOOSE_DATE: "\u05D1\u05D7\u05D9\u05E8\u05EA \u05EA\u05D0\u05E8\u05D9\u05DA",
  MAIN_SKIP_LINK: "\u05D3\u05DC\u05D2 \u05DC\u05EA\u05D5\u05DB\u05DF \u05DE\u05E8\u05DB\u05D6\u05D9",
  SEARCH_SKIP_LINK: "\u05D3\u05DC\u05D2 \u05DC\u05D7\u05D9\u05E4\u05D5\u05E9",
  NAVIGATION_SKIP_LINK: "\u05D3\u05DC\u05D2 \u05DC\u05E0\u05D9\u05D5\u05D5\u05D8",
  ZOOM_IN: "\u05D4\u05D2\u05D3\u05DC \u05D8\u05E7\u05E1\u05D8",
  ZOOM_OUT: "\u05D4\u05E7\u05D8\u05DF \u05D8\u05E7\u05E1\u05D8",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "\u05E9\u05E7\u05D5\u05E4\u05D9\u05EA \u05E7\u05D5\u05D3\u05DE\u05EA",
  NEXT_SLIDE: "\u05E9\u05E7\u05D5\u05E4\u05D9\u05EA \u05D4\u05D1\u05D0\u05D4",
  CHOOSE_DISPLAY: "\u05D1\u05D7\u05E8 \u05E9\u05E7\u05E3 \u05DC\u05D4\u05E6\u05D2\u05D4",
  OF: "\u05DE\u05EA\u05D5\u05DA",
  SLIDE: "\u05E9\u05E7\u05D5\u05E4\u05D9\u05EA",
  CAROUSEL: "\u05E1\u05DC\u05D9\u05D9\u05D3\u05E8 \u05E9\u05E7\u05D5\u05E4\u05D9\u05D5\u05EA",
  CLOSE: "\u05E1\u05D2\u05D9\u05E8\u05D4",
  PREV_PAGE: "\u05E2\u05DE\u05D5\u05D3 \u05E7\u05D5\u05D3\u05DD",
  NEXT_PAGE: "\u05E2\u05DE\u05D5\u05D3 \u05D4\u05D1\u05D0",
  PREV_SKIP: "\u05E7\u05E4\u05D5\u05E5 {{skipAmount}} \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD \u05D0\u05D7\u05D5\u05E8\u05D4",
  NEXT_SKIP: "\u05E7\u05E4\u05D5\u05E5 {{skipAmount}} \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD \u05E7\u05D3\u05D9\u05DE\u05D4",
  FEW: "\u05DE\u05E1\u05E4\u05E8",
  PAGE: "\u05E2\u05DE\u05D5\u05D3",
  SLIDES: "\u05E0\u05D9\u05D5\u05D5\u05D8 \u05E9\u05E7\u05D5\u05E4\u05D9\u05D5\u05EA",
  LOADING: "\u05D1\u05D8\u05E2\u05D9\u05E0\u05D4, \u05D0\u05E0\u05D0 \u05D4\u05DE\u05EA\u05DF",
  SKIP_TO: "\u05D3\u05DC\u05D2 \u05D0\u05DC",
  MAIN_CONTENT: "\u05EA\u05D5\u05DB\u05DF \u05E8\u05D0\u05E9\u05D9",
  NAVIGATION: "\u05EA\u05E4\u05E8\u05D9\u05D8 \u05E0\u05D9\u05D5\u05D5\u05D8",
  FOOTER: "\u05EA\u05D7\u05EA\u05D9\u05EA \u05D4\u05E2\u05DE\u05D5\u05D3"
};

const da = {
  OPEN_MENU_SKIP_LINK: "",
  ACCESSIBILITY_BUTTON: "TILG\xC6NGELIGHED",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const de = {
  OPEN_MENU_SKIP_LINK: "Zug\xE4nglichkeitsmen\xFC \xF6ffnen",
  ACCESSIBILITY_BUTTON: "Zug\xE4nglichkeit",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const es = {
  OPEN_MENU_SKIP_LINK: "Ir al men\xFA accesibilidad",
  ACCESSIBILITY_BUTTON: "Accesibilidad",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const fr = {
  OPEN_MENU_SKIP_LINK: "Aller au menu d\u2019accessibilit\xE9",
  ACCESSIBILITY_BUTTON: "Accessibilit\xE9",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const pt = {
  OPEN_MENU_SKIP_LINK: "Ativar o menu de acessibilidade",
  ACCESSIBILITY_BUTTON: "Acessibilidade",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const sv = {
  OPEN_MENU_SKIP_LINK: "",
  ACCESSIBILITY_BUTTON: "Tillg\xE4nglighet",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const nl = {
  OPEN_MENU_SKIP_LINK: "Klik voor toegankelijkheidsmenu",
  ACCESSIBILITY_BUTTON: "Toegankelijkheid",
  NEXT_MONTH: "next month",
  NEXT_YEAR: "next year",
  PREV_MONTH: "previous month",
  PREV_YEAR: "previous year",
  CHOOSE_DATE: "choose date",
  MAIN_SKIP_LINK: "Skip to Main",
  SEARCH_SKIP_LINK: "Skip to Search",
  NAVIGATION_SKIP_LINK: "Skip to Navigation",
  ZOOM_IN: "Zoom-In",
  ZOOM_OUT: "Zoom-Out",
  DESKTOP_SNOOZE_TEXT: `To make this website accessible to screen reader, press combination of alt and 1 keys.
  To stop getting this message, press the combination of alt and 2 keys.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "Adjust the page for a screen reader",
  MOBILE_DISABLE_SNOOZE_TEXT: "Turn off screen reader adjustment suggestion",
  PREV_SLIDE: "previous slide",
  NEXT_SLIDE: "next slide",
  CHOOSE_DISPLAY: "Choose slide to display",
  OF: "of",
  SLIDE: "slide",
  CAROUSEL: "carousel",
  CLOSE: "close",
  PREV_PAGE: "previous page",
  NEXT_PAGE: "next page",
  PREV_SKIP: "skip {{skipAmount}} pages back",
  NEXT_SKIP: "skip {{skipAmount}} pages forward",
  FEW: "few",
  PAGE: "page",
  SLIDES: "slides navigation",
  LOADING: "loading, please wait",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

const ar = {
  OPEN_MENU_SKIP_LINK: "\u0641\u062A\u062D \u0642\u0627\u0626\u0645\u0629 \u0625\u0645\u0643\u0627\u0646\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644",
  ACCESSIBILITY_BUTTON: "\u0625\u0645\u0643\u0627\u0646\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644",
  NEXT_MONTH: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0642\u0627\u062F\u0645",
  NEXT_YEAR: "\u0627\u0644\u0639\u0627\u0645 \u0627\u0644\u0642\u0627\u062F\u0645",
  PREV_MONTH: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0645\u0627\u0636\u0649",
  PREV_YEAR: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0645\u0627\u0636\u0649",
  CHOOSE_DATE: "\u0627\u062E\u062A\u064A\u0627\u0631 \u0627\u0644\u062A\u0627\u0631\u064A\u062E",
  MAIN_SKIP_LINK: "\u0627\u0646\u062A\u0642\u0644 \u0625\u0644\u0649 \u0627\u0644\u0645\u062D\u062A\u0648\u0649 \u0627\u0644\u0631\u0626\u064A\u0633\u064A",
  SEARCH_SKIP_LINK: "\u062A\u062E\u0637\u064A \u0625\u0644\u0649 \u0627\u0644\u0628\u062D\u062B",
  NAVIGATION_SKIP_LINK: "\u0627\u0646\u062A\u0642\u0644 \u0625\u0644\u0649 \u0627\u0644\u062A\u0646\u0642\u0644",
  ZOOM_IN: "\u062A\u0643\u0628\u064A\u0631",
  ZOOM_OUT: "\u0627\u0644\u062A\u0635\u063A\u064A\u0631",
  DESKTOP_SNOOZE_TEXT: `\u0644\u062C\u0639\u0644 \u0647\u0630\u0627 \u0627\u0644\u0645\u0648\u0642\u0639 \u0641\u064A \u0645\u062A\u0646\u0627\u0648\u0644 \u0642\u0627\u0631\u0626 \u0627\u0644\u0634\u0627\u0634\u0629\u060C \u0627\u0636\u063A\u0637 \u0639\u0644\u0649 \u0645\u062C\u0645\u0648\u0639\u0629 \u0645\u0646 \u0627\u0644\u0645\u0641\u0627\u062A\u064A\u062D alt \u0648 1.
   \u0644\u0625\u064A\u0642\u0627\u0641 \u062A\u0644\u0642\u064A \u0647\u0630\u0647 \u0627\u0644\u0631\u0633\u0627\u0644\u0629\u060C \u0627\u0636\u063A\u0637 \u0639\u0644\u0649 \u0645\u062C\u0645\u0648\u0639\u0629 \u0627\u0644\u0645\u0641\u0627\u062A\u064A\u062D alt \u0648 2.`,
  MOBILE_ACTIVATE_SR_SNOOZE_TEXT: "\u0627\u0636\u0628\u0637 \u0627\u0644\u0635\u0641\u062D\u0629 \u0644\u0642\u0627\u0631\u0626 \u0627\u0644\u0634\u0627\u0634\u0629",
  MOBILE_DISABLE_SNOOZE_TEXT: "\u0642\u0645 \u0628\u0625\u064A\u0642\u0627\u0641 \u062A\u0634\u063A\u064A\u0644 \u0627\u0642\u062A\u0631\u0627\u062D \u062A\u0639\u062F\u064A\u0644 \u0642\u0627\u0631\u0626 \u0627\u0644\u0634\u0627\u0634\u0629",
  PREV_SLIDE: "\u0627\u0644\u0634\u0631\u064A\u062D\u0629 \u0627\u0644\u0633\u0627\u0628\u0642\u0629",
  NEXT_SLIDE: "\u0627\u0644\u0634\u0631\u064A\u062D\u0629 \u0627\u0644\u062A\u0627\u0644\u064A\u0629",
  CHOOSE_DISPLAY: "\u0627\u062E\u062A\u0631 \u0634\u0631\u064A\u062D\u0629 \u0644\u0639\u0631\u0636\u0647\u0627",
  OF: "\u0645\u0646",
  SLIDE: "\u0627\u0644\u0634\u0631\u064A\u062D\u0629 ",
  CAROUSEL: "\u062F\u0627\u0626\u0631\u064A",
  CLOSE: "\u063A\u0644\u0642",
  PREV_PAGE: "\u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0633\u0627\u0628\u0642\u0629",
  NEXT_PAGE: "\u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0642\u0627\u062F\u0645\u0629",
  PREV_SKIP: "\u062A\u062E\u0637\u064A {{skipAmount}} \u0635\u0641\u062D\u0627\u062A \u0644\u0644\u062E\u0644\u0641",
  NEXT_SKIP: "\u062A\u062E\u0637\u064A {{skipAmount}} \u0635\u0641\u062D\u0627\u062A \u0625\u0644\u0649 \u0627\u0644\u0623\u0645\u0627\u0645",
  FEW: "\u0642\u0644\u064A\u0644",
  PAGE: "\u0635\u0641\u062D\u0629",
  SLIDES: "\u0645\u0644\u0627\u062D\u0629 \u0627\u0644\u0634\u0631\u0627\u0626\u062D",
  LOADING: "\u062C\u0627\u0631\u064A \u0627\u0644\u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0631\u062C\u0627\u0621\u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
  SKIP_TO: "Skip to",
  MAIN_CONTENT: "main content",
  NAVIGATION: "navigation",
  FOOTER: "footer"
};

var TranslateText = /* @__PURE__ */ ((TranslateText2) => {
  TranslateText2[TranslateText2["OPEN_MENU_SKIP_LINK"] = 0] = "OPEN_MENU_SKIP_LINK";
  TranslateText2[TranslateText2["ACCESSIBILITY_BUTTON"] = 1] = "ACCESSIBILITY_BUTTON";
  TranslateText2[TranslateText2["NEXT_MONTH"] = 2] = "NEXT_MONTH";
  TranslateText2[TranslateText2["NEXT_YEAR"] = 3] = "NEXT_YEAR";
  TranslateText2[TranslateText2["PREV_MONTH"] = 4] = "PREV_MONTH";
  TranslateText2[TranslateText2["PREV_YEAR"] = 5] = "PREV_YEAR";
  TranslateText2[TranslateText2["CHOOSE_DATE"] = 6] = "CHOOSE_DATE";
  TranslateText2[TranslateText2["MAIN_SKIP_LINK"] = 7] = "MAIN_SKIP_LINK";
  TranslateText2[TranslateText2["SEARCH_SKIP_LINK"] = 8] = "SEARCH_SKIP_LINK";
  TranslateText2[TranslateText2["NAVIGATION_SKIP_LINK"] = 9] = "NAVIGATION_SKIP_LINK";
  TranslateText2[TranslateText2["ZOOM_IN"] = 10] = "ZOOM_IN";
  TranslateText2[TranslateText2["ZOOM_OUT"] = 11] = "ZOOM_OUT";
  TranslateText2[TranslateText2["DESKTOP_SNOOZE_TEXT"] = 12] = "DESKTOP_SNOOZE_TEXT";
  TranslateText2[TranslateText2["MOBILE_ACTIVATE_SR_SNOOZE_TEXT"] = 13] = "MOBILE_ACTIVATE_SR_SNOOZE_TEXT";
  TranslateText2[TranslateText2["MOBILE_DISABLE_SNOOZE_TEXT"] = 14] = "MOBILE_DISABLE_SNOOZE_TEXT";
  TranslateText2[TranslateText2["PREV_SLIDE"] = 15] = "PREV_SLIDE";
  TranslateText2[TranslateText2["NEXT_SLIDE"] = 16] = "NEXT_SLIDE";
  TranslateText2[TranslateText2["CHOOSE_DISPLAY"] = 17] = "CHOOSE_DISPLAY";
  TranslateText2[TranslateText2["OF"] = 18] = "OF";
  TranslateText2[TranslateText2["SLIDE"] = 19] = "SLIDE";
  TranslateText2[TranslateText2["CAROUSEL"] = 20] = "CAROUSEL";
  TranslateText2[TranslateText2["CLOSE"] = 21] = "CLOSE";
  TranslateText2[TranslateText2["PREV_PAGE"] = 22] = "PREV_PAGE";
  TranslateText2[TranslateText2["NEXT_PAGE"] = 23] = "NEXT_PAGE";
  TranslateText2[TranslateText2["PREV_SKIP"] = 24] = "PREV_SKIP";
  TranslateText2[TranslateText2["NEXT_SKIP"] = 25] = "NEXT_SKIP";
  TranslateText2[TranslateText2["FEW"] = 26] = "FEW";
  TranslateText2[TranslateText2["PAGE"] = 27] = "PAGE";
  TranslateText2[TranslateText2["SLIDES"] = 28] = "SLIDES";
  TranslateText2[TranslateText2["LOADING"] = 29] = "LOADING";
  TranslateText2[TranslateText2["SKIP_TO"] = 30] = "SKIP_TO";
  TranslateText2[TranslateText2["MAIN_CONTENT"] = 31] = "MAIN_CONTENT";
  TranslateText2[TranslateText2["NAVIGATION"] = 32] = "NAVIGATION";
  TranslateText2[TranslateText2["FOOTER"] = 33] = "FOOTER";
  return TranslateText2;
})(TranslateText || {});
class Translate {
  constructor(stateManagement) {
    this.stateManagement = stateManagement;
    this.defaultLang = "en";
    this.availableLangs = {
      en,
      he,
      da,
      de,
      es,
      fr,
      pt,
      sv,
      nl,
      ar
    };
    this.getValue = (value) => {
      var _a, _b;
      const currLangDictionary = (_a = this.availableLangs[this.langDetected]) != null ? _a : this.availableLangs[this.defaultLang];
      const label = TranslateText[value];
      const text = (_b = currLangDictionary[label]) != null ? _b : "";
      return text;
    };
    this.isRtl = () => {
      return Utils$1.isRtl(this.stateManagement.getLang());
    };
    var _a;
    this.langDetected = ((_a = stateManagement.getLang()) != null ? _a : this.defaultLang).toLowerCase();
  }
}

const props = [
  {
    selectors: ["nav", '[role="navigation"]'],
    description: TranslateText.NAVIGATION
  },
  {
    selectors: ["main", '[role="main"]'],
    description: TranslateText.MAIN_CONTENT
  },
  {
    selectors: ["footer", '[role="contentinfo"]'],
    description: TranslateText.FOOTER
  }
];
const landmarksWithSkipLinks = /* @__PURE__ */ new Set();
class SkipLinkFixer {
  constructor() {
    this.domProvider = new DomProvider();
    this.elementVisibility = new ElementVisibility();
    this.changeDetection = ChangeDetection.getInstance(this.elementVisibility, this.domProvider);
  }
  // This set tracks fixed skip links outside of change detection. Change detection only allows an element to be "fixed" once, or to be fixed on each change detection cycle (see ChangeDetection's shouldInvokeOnFound method).
  init() {
    props.forEach(
      ({ description, selectors }) => this.changeDetection.subscribe(
        "body",
        `skipLink`,
        selectors,
        (landmarkElement) => {
          if (!landmarksWithSkipLinks.has(landmarkElement)) {
            landmarksWithSkipLinks.add(landmarkElement);
            this.fix(landmarkElement, description);
          }
        },
        // this ensures skip link removal for landmarks that are no longer on the page
        (landmarkElement) => {
          const skipLinkSelector = `[href="#${landmarkElement.id}"]`;
          const skipLinkElement = this.domProvider.querySelector(skipLinkSelector);
          if (skipLinkElement) {
            skipLinkElement.remove();
          } else {
            console.warn(
              `Skip link with selector "${skipLinkSelector}" not found for Landmark element. This should never happen.`
            );
          }
        }
      )
    );
  }
  fix(landmarkElement, description) {
    const landmarkElementId = Utils$1.getOrCreateId(landmarkElement);
    const skipLinkElement = this.domProvider.createElement("a");
    skipLinkElement.classList.add("u1st-skip-link");
    const translateService = new Translate(StateManagement.getInstance());
    if (translateService.isRtl()) {
      skipLinkElement.classList.add("rtl");
    }
    if (skipLinkElement instanceof HTMLAnchorElement) {
      skipLinkElement.href = `#${landmarkElementId}`;
    }
    skipLinkElement.textContent = `${translateService.getValue(
      TranslateText.SKIP_TO
    )} ${translateService.getValue(description)}`;
    const customStyle = this.getCustomStyle();
    if (customStyle) {
      skipLinkElement.addEventListener(
        "focus",
        () => Object.entries(customStyle).forEach(
          ([key, value]) => skipLinkElement.style.setProperty(
            // convert camelCase to kebab-case
            key.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase(),
            value
          )
        )
      );
      skipLinkElement.addEventListener("blur", () => skipLinkElement.setAttribute("style", ""));
    }
    this.domProvider.insertAdjacentElement(
      this.domProvider.getBody(),
      "afterbegin",
      skipLinkElement
    );
  }
  getCustomStyle() {
    var _a, _b, _c;
    return (_c = (_b = (_a = window.u1) == null ? void 0 : _a.config) == null ? void 0 : _b.skipLink) == null ? void 0 : _c.style;
  }
}

const skipLinkApi = () => new SkipLinkFixer().init();

var __defProp$5 = Object.defineProperty;
var __getOwnPropDesc$5 = Object.getOwnPropertyDescriptor;
var __decorateClass$5 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$5(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$5(target, key, result);
  return result;
};
class FixerAbstract {
  constructor(context, domProvider, changeDetection, props, waitForElement, onClose) {
    this.services = {};
    this.logger = Logger.getInstance();
    this.stateManagement = StateManagement.getInstance();
    this.domProvider = domProvider;
    this.observer = changeDetection;
    this.props = props;
    this.context = context;
    this.waitForElementSelector = waitForElement != null ? waitForElement : props.waitForFixer;
    this.init(onClose);
  }
  subscribe(fixerName, elementSelector, onClose) {
    this.observer.subscribe(
      this.context,
      fixerName,
      elementSelector,
      (visibleElement) => this.onFixerLoaded(visibleElement),
      onClose
    );
  }
  init(onClose) {
    const fixerName = Object.getPrototypeOf(this).constructor.name;
    if (typeof this.waitForElementSelector === "string")
      this.subscribe(fixerName, this.waitForElementSelector, onClose);
    else {
      this.waitForElementSelector.forEach(
        (elementSelector) => this.subscribe(fixerName, elementSelector, onClose)
      );
    }
  }
  queryAndFix(elementSelector, visibleElement) {
    const fixerElement = this.domProvider.querySelector(elementSelector);
    if (fixerElement) {
      if (Object.getPrototypeOf(this).isFreeFixer === true || "production" === "development") {
        this.tryFix(visibleElement);
      } else {
        RuntimeUtils.runIfDomainIsValid(this.tryFix.bind(this, visibleElement));
      }
    }
  }
  tryFix(visibleElement) {
    this.fix(visibleElement);
  }
  onFixerLoaded(visibleElement) {
    if (typeof this.waitForElementSelector === "string")
      this.queryAndFix(this.waitForElementSelector, visibleElement);
    else {
      this.waitForElementSelector.forEach((elementSelector) => {
        this.queryAndFix(elementSelector, visibleElement);
      });
    }
  }
}
__decorateClass$5([
  catchable
], FixerAbstract.prototype, "init", 1);
__decorateClass$5([
  catchable
], FixerAbstract.prototype, "tryFix", 1);

class CanvasEvents {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleEvents(el) {
    let userTxt = "";
    this.domProvider.addEventListener(el, "keydown", (evt) => {
      const e = evt;
      if (/^[A-Za-z0-9]$/.test(e.key) || e.key === "Backspace" || e.code === "Space") {
        const ctx = el.getContext("2d");
        if (!ctx)
          return;
        userTxt = this.handleInput(e.key, userTxt);
        ctx.font = "30px Arial";
        ctx.clearRect(0, 0, el.width, el.height);
        ctx.fillText(userTxt, 10, 50);
      }
    });
  }
  handleInput(key, userTxt) {
    if (key === "Backspace")
      return userTxt.slice(0, userTxt.length - 1);
    if (key === "Space")
      return userTxt += " ";
    return userTxt += key;
  }
}

class CanvasText {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleBtn(el) {
    this.domProvider.setAttribute(el, "tabindex", "0");
    this.domProvider.setAttribute(el, "role", "button");
    this.domProvider.setAttribute(el, "aria-label", "clear text");
  }
  handleAttrs(el) {
    this.domProvider.setAttribute(el, "tabindex", "0");
    this.domProvider.setAttribute(el, "aria-label", "signature");
  }
}

class CanvasFixer extends FixerAbstract {
  constructor(context, props, domProvider, elementVisibility, changeDetection, canvasText, canvasEvents) {
    super(context, domProvider, changeDetection, props, props.selectors.element);
    this.domProvider = domProvider;
    this.elementVisibility = elementVisibility;
    this.changeDetection = changeDetection;
    this.canvasText = canvasText;
    this.canvasEvents = canvasEvents;
  }
  fix() {
    const el = this.domProvider.querySelector(this.props.selectors.element);
    if (!el)
      return;
    const btnEl = this.domProvider.querySelector(this.props.selectors.clearBtn);
    this.canvasText.handleAttrs(el);
    this.canvasEvents.handleEvents(el);
    if (btnEl)
      this.canvasText.handleBtn(btnEl);
  }
}
const CanvasApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibilty = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(elementVisibilty, domProvider);
  const canvasText = new CanvasText(domProvider);
  const canvasEvents = new CanvasEvents(domProvider);
  return (props) => new CanvasFixer(
    context,
    props,
    domProvider,
    elementVisibilty,
    changeDetection,
    canvasText,
    canvasEvents
  );
};

class AccordionRoles {
  constructor(domProvider, elementVisibility) {
    this.domProvider = domProvider;
    this.elementVisibility = elementVisibility;
  }
  assignOnLoadAccordionAttr(header, content, disabledSelector, headerContentMatch) {
    var _a;
    this.handleHeaderId(header);
    const tagName = (_a = this.domProvider.getTagName(header)) == null ? void 0 : _a.toLowerCase();
    if (tagName != "button") {
      this.domProvider.setAttribute(header, "role", "button");
      this.domProvider.setAttribute(header, "tabindex", "0");
    }
    this.setContentRoles(content, header);
    if (headerContentMatch) {
      const headerExpand = this.elementVisibility.isVisible(content);
      this.domProvider.setAttribute(header, ARIA.EXPANDED, String(headerExpand));
      this.assignOnLoadAriaControlsAttr(header, content);
    } else {
      this.domProvider.setAttribute(header, ARIA.EXPANDED, "false");
    }
    if (disabledSelector && this.domProvider.matches(header, disabledSelector)) {
      this.domProvider.setAttribute(header, ARIA.DISABLED, "true");
    }
  }
  setContentRoles(content, header) {
    this.domProvider.setAttribute(content, ATTRIBUTES.ROLE, ROLE$1.REGION);
    const headerId = Utils$1.getOrCreateId(header);
    this.domProvider.setAttribute(content, ARIA.LABELLEDBY, headerId);
  }
  handleHeaderId(header) {
    Utils$1.getOrCreateId(header);
  }
  assignOnLoadAriaControlsAttr(header, content) {
    let contentId = this.domProvider.getAttribute(content, "id");
    if (contentId == null) {
      contentId = Utils$1.uuid();
      this.domProvider.setAttribute(content, "id", contentId);
    }
    this.domProvider.setAttribute(header, ARIA.CONTROLS, contentId);
  }
}

class AccordionEvents {
  constructor(context, domProvider, changeDetection, accordionRole) {
    this.context = context;
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.accordionHeaders = new Array();
    this.accordionContent = new Array();
    this.headerStack = new Array();
    this.collapsesOthers = false;
    this.disabledHeaders = new Array();
    this.accordionRole = accordionRole;
  }
  initAccordionEvents(props) {
    const { collapsesOthers, selectors } = props;
    const { contentSelector, disabledElementsSelector, headerSelector } = selectors;
    this.accordionHeaders = this.domProvider.querySelectorAll(headerSelector, this.context);
    this.accordionContent = this.domProvider.querySelectorAll(contentSelector, this.context);
    this.collapsesOthers = collapsesOthers;
    this.disabledSelector = disabledElementsSelector;
    this.disabledHeaders = disabledElementsSelector ? this.accordionContent : new Array();
  }
  setAccordionEvents(contentSelector) {
    this.domProvider.addEventListenerToAll(
      this.accordionHeaders,
      "keydown",
      (event) => this.handleKeyboardEvent(event),
      true
    );
    this.domProvider.addEventListenerToAll(
      this.accordionHeaders,
      "click",
      (event) => this.handleMouseClick(event)
    );
    this.changeDetection.subscribe(
      this.context,
      "contentVisibility",
      contentSelector,
      (visibleContent) => this.accordionContentVisible(visibleContent),
      (hiddenContent) => this.accordionContentHidden(hiddenContent)
    );
  }
  handleKeyboardEvent(event) {
    const { code } = event;
    if (code === "Space" || code === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget;
      this.domProvider.dispatchClickEvent(target);
    }
  }
  handleMouseClick(event) {
    const target = event.currentTarget;
    this.headerStack.push(target);
  }
  accordionContentVisible(content) {
    this.collapsesOthers && this.collapseAllContents();
    if (this.headerStack.length > 0) {
      const header = this.headerStack.pop();
      this.setLabelledby(content, header);
      if (this.domProvider.getAttribute(content, ATTRIBUTES.ID) === null) {
        this.setContentId(content, header);
      }
      this.assignExpandedEvent(header, content, true);
      this.assignNewContentEvent(header, content);
    }
  }
  accordionContentHidden(content) {
    this.collapsesOthers && this.collapseAllContents();
    let header = this.headerStack.pop();
    if (this.isInDifferentPanel(content, header)) {
      header && this.headerStack.push(header);
      header = this.getCorrectHeader(content);
    }
    this.domProvider.setAttribute(header, ARIA.EXPANDED, "false");
    if (this.headerStack.length > 0) {
      header && this.assignExpandedEvent(header, content, false);
    }
  }
  isInDifferentPanel(content, header) {
    var _a;
    const headerId = (_a = this.domProvider.getAttribute(content, ARIA.LABELLEDBY)) != null ? _a : "";
    return this.domProvider.getAttribute(header, ATTRIBUTES.ID) !== headerId;
  }
  getCorrectHeader(content) {
    var _a;
    const headerId = (_a = this.domProvider.getAttribute(content, ARIA.LABELLEDBY)) != null ? _a : "";
    return this.domProvider.getElementById(headerId);
  }
  setLabelledby(content, header) {
    var _a;
    const headerId = (_a = this.domProvider.getAttribute(header, ATTRIBUTES.ID)) != null ? _a : "";
    this.domProvider.setAttribute(content, ARIA.LABELLEDBY, headerId);
  }
  setContentId(content, header) {
    const ariaControlsId = this.domProvider.getAttribute(header, ARIA.CONTROLS);
    if (ariaControlsId !== null) {
      this.domProvider.setAttribute(content, ATTRIBUTES.ID, ariaControlsId);
    }
  }
  collapseAllContents() {
    this.domProvider.setAttributeToAll(this.accordionHeaders, ARIA.EXPANDED, "false");
    this.domProvider.setAttributeToAll(this.disabledHeaders, ARIA.EXPANDED, "false");
    this.domProvider.setAttributeToAll(this.accordionContent, ARIA.HIDDEN, "true");
  }
  assignExpandedEvent(header, content, expandedValue) {
    const disabledSelector = this.disabledSelector;
    if (disabledSelector && this.domProvider.matches(header, disabledSelector)) {
      this.domProvider.setAttribute(header, ARIA.DISABLED, String(expandedValue));
    }
    this.domProvider.setAttribute(header, ARIA.EXPANDED, String(expandedValue));
    this.domProvider.setAttribute(content, ARIA.HIDDEN, String(!expandedValue));
  }
  assignNewContentEvent(header, content) {
    if (content != void 0 && this.domProvider.getAttribute(header, ARIA.CONTROLS) == null) {
      let contentId = this.domProvider.getAttribute(content, "id");
      if (contentId == null) {
        contentId = Utils$1.uuid();
        this.domProvider.setAttribute(content, "id", contentId);
      }
      this.domProvider.setAttribute(header, ARIA.CONTROLS, contentId);
    }
    this.accordionRole.setContentRoles(content, header);
  }
}

class AccordionFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, accordionRoles, accordionEvents) {
    super(context, domProvider, changeDetection, props, props.selectors.headerSelector);
    this.accordionRoles = accordionRoles;
    this.accordionEvents = accordionEvents;
  }
  fix() {
    this.accordionEvents.initAccordionEvents(this.props);
    const { headerSelector, contentSelector, disabledElementsSelector } = this.props.selectors;
    const accordionHeaders = this.domProvider.querySelectorAll(headerSelector, this.context);
    const accordionContent = this.domProvider.querySelectorAll(contentSelector, this.context);
    const headerContentMatch = accordionHeaders.length == accordionContent.length;
    for (let i = 0; i < accordionHeaders.length; i++) {
      this.accordionRoles.assignOnLoadAccordionAttr(
        accordionHeaders[i],
        accordionContent[i],
        disabledElementsSelector,
        headerContentMatch
      );
    }
    this.accordionEvents.setAccordionEvents(contentSelector);
  }
}
const AccordionApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const accordionRoles = new AccordionRoles(domProvider, elementVisibility);
  const accordionEvents = new AccordionEvents(
    context,
    domProvider,
    changeDetection,
    accordionRoles
  );
  return (props) => {
    new AccordionFixer(
      context,
      props,
      domProvider,
      changeDetection,
      accordionRoles,
      accordionEvents
    );
  };
};

const free = (constructor) => {
  constructor.prototype.isFreeFixer = true;
};

var __defProp$4 = Object.defineProperty;
var __getOwnPropDesc$4 = Object.getOwnPropertyDescriptor;
var __decorateClass$4 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$4(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$4(target, key, result);
  return result;
};
let ButtonFixer = class extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection) {
    super(context, domProvider, changeDetection, props, props.selectors.element);
    this.buttons = [];
  }
  fix() {
    const focusTo = this.props.selectors.focusTo;
    const label = this.props.label;
    this.buttons = this.domProvider.querySelectorAll(this.props.selectors.element);
    this.buttons && this.buttons.forEach((element) => {
      const isButton = element instanceof HTMLButtonElement;
      if (!isButton) {
        this.domProvider.setAttribute(element, "role", ROLE$1.BUTTON);
        this.domProvider.setAttribute(element, "tabindex", "0");
        this.domProvider.addEventListener(
          element,
          "keydown",
          (e) => this.addMouseEvent(e, element)
        );
      }
      if (label)
        this.domProvider.setAttribute(element, "aria-label", label);
      const focusToId = focusTo != null ? focusTo : this.domProvider.getAttribute(element, "u1-to");
      if (focusToId) {
        const id = focusToId.replace("#", "");
        this.domProvider.addEventListener(element, "click", () => {
          const focusToEl = this.domProvider.getElementById(id);
          this.domProvider.setAttribute(focusToEl, FOCUS_TO_ATTRIBUTE, "true");
        });
      }
    });
  }
  addMouseEvent(e, el) {
    const evt = e;
    if (evt.code === "Enter" || evt.code === "Space") {
      this.domProvider.dispatchMouseClickEvent(el);
      evt.preventDefault();
    }
  }
};
ButtonFixer = __decorateClass$4([
  free
], ButtonFixer);

class ToggleStateService {
  constructor(domProvider, clickOnly) {
    this.domProvider = domProvider;
    this.clickOnly = clickOnly;
  }
  fix(element, stateProps, attributeName, siblings) {
    if (stateProps.stateHandler) {
      const callback = (mutationsList) => {
        mutationsList.forEach(() => {
          if (stateProps.stateHandler && stateProps.stateHandler(element)) {
            this.handleState(element, true, attributeName, siblings);
          } else {
            this.handleState(element, false, attributeName, siblings);
          }
        });
      };
      const config = { attributes: true, childList: true, subtree: true };
      const observer = new MutationObserver(callback);
      observer.observe(element, config);
      return;
    }
    const { trueState, falseState } = stateProps.selectors;
    const isTrue = trueState ? this.domProvider.matches(element, trueState) : false;
    const isFalse = falseState ? this.domProvider.matches(element, falseState) : false;
    if (isTrue && !isFalse || !isTrue && isFalse) {
      this.handleState(element, isTrue, attributeName, siblings);
    } else if (trueState !== void 0) {
      const isChecked = element.querySelector(trueState) !== null;
      this.handleState(element, isChecked, attributeName, siblings);
    }
  }
  handleState(element, value, attributeName, siblings) {
    this.domProvider.setAttribute(element, attributeName, String(value));
    !this.clickOnly && this.domProvider.addEventListener(
      element,
      "keypress",
      (e) => this.handleKeydown(e, attributeName)
    );
    fromEvent(element, "click").pipe(debounce(() => timer(0))).subscribe((event) => {
      this.handleAria(element, attributeName);
      siblings && this.handleSiblingsAria(attributeName, siblings);
      if (event.target !== element) {
        element.focus();
      }
    });
  }
  handleKeydown(e, attribute) {
    const evt = e;
    if (evt.code === "Enter" || evt.code === "Space") {
      this.handleAria(e.target, attribute);
      evt.preventDefault();
    }
  }
  handleAria(el, attribute) {
    const attrValue = this.domProvider.getAttribute(el, attribute);
    if (!attrValue)
      return;
    const updatedValue = !(attrValue === "true");
    this.domProvider.setAttribute(el, attribute, String(updatedValue));
  }
  handleSiblingsAria(attribute, elements) {
    elements == null ? void 0 : elements.forEach((el) => this.domProvider.setAttribute(el, attribute, "false"));
  }
  runHandler(stateHandler) {
    stateHandler();
  }
}

class ToggleButtonFixer extends ButtonFixer {
  constructor(context, props, domProvider, changeDetection) {
    super(context, props, domProvider, changeDetection);
  }
  fix() {
    super.fix();
    const { pressedState, notPressedState, element } = this.props.selectors.toggleSelectors;
    const stateService = new ToggleStateService(this.domProvider, true);
    const stateProps = {
      selectors: { trueState: pressedState, falseState: notPressedState, element },
      stateHandler: this.props.stateHandler
    };
    this.buttons && this.buttons.forEach((element2) => {
      stateService.fix(element2, stateProps, ARIA.PRESSED);
    });
  }
}

const ButtonApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return function(props) {
    new ButtonFixer(context, props, domProvider, changeDetection);
  };
};
const ToggleButtonApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return function(props) {
    new ToggleButtonFixer(context, props, domProvider, changeDetection);
  };
};

class CarouselFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, carouselRoles, movingContent) {
    super(context, domProvider, changeDetection, props, props.selectors.carouselContainer);
    this.carouselRoles = carouselRoles;
    this.movingContent = movingContent;
  }
  fix(visibleElement) {
    this.carouselRoles.handleCarouselRoles(visibleElement, this.props);
    this.movingContent.handleMovingContent(this.props);
  }
}

var Direction = /* @__PURE__ */ ((Direction2) => {
  Direction2[Direction2["Right"] = 1] = "Right";
  Direction2[Direction2["Left"] = -1] = "Left";
  return Direction2;
})(Direction || {});

class MovingContent {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleMovingContent(props) {
    const { carouselContainer, slide, slidePickerButtons, prevButton, nextButton } = props.selectors;
    const slides = this.domProvider.querySelectorAll(slide, carouselContainer);
    const prev = prevButton && this.domProvider.querySelector(prevButton, carouselContainer);
    const next = nextButton && this.domProvider.querySelector(nextButton, carouselContainer);
    const pickers = slidePickerButtons && this.domProvider.querySelectorAll(slidePickerButtons, carouselContainer);
    this.initialProps(slides, props);
    if (prev && next) {
      this.domProvider.addEventListener(
        next,
        "click",
        () => this.handleButtonClick(slides, Direction.Right, pickers, props)
      );
      this.domProvider.addEventListener(
        prev,
        "click",
        () => this.handleButtonClick(slides, Direction.Left, pickers, props)
      );
    }
    if (pickers) {
      pickers.forEach(
        (picker, i) => this.domProvider.addEventListener(
          picker,
          "click",
          () => this.handlePickerClick(slides, i, pickers, props)
        )
      );
    }
  }
  initialProps(slides, props) {
    var _a;
    if ((_a = props.selectors) == null ? void 0 : _a.activeSlides) {
      this.handleMultipleSlides(props);
      this.domProvider.addEventListenerToAll(slides, "click", () => {
        this.handleMultipleSlides(props);
      });
    } else {
      this.domProvider.addClass(slides[0], PROJECT_CLASSES.ACTIVE_ELEMENT);
      for (let i = 1; i < slides.length; i++)
        this.domProvider.setAttribute(slides[i], ARIA.HIDDEN, "true");
    }
  }
  handleButtonClick(slides, direction, pickers, props) {
    var _a;
    const currentIndex = this.getCurrentSlideIndex(slides);
    const nextActiveIndex = this.getNextIndex(currentIndex, slides.length, direction);
    ((_a = props.selectors) == null ? void 0 : _a.activeSlides) ? this.handleMultipleSlides(props) : this.handleSingleSlide(slides[currentIndex], slides[nextActiveIndex]);
    pickers && this.handlePicker(pickers[currentIndex], pickers[nextActiveIndex]);
  }
  handlePickerClick(slides, nextActiveIndex, pickers, props) {
    var _a;
    const currentSlide = slides[this.getCurrentSlideIndex(slides)];
    const currentPicker = pickers[this.getCurrentSlideIndex(slides)];
    ((_a = props.selectors) == null ? void 0 : _a.activeSlides) ? this.handleMultipleSlides(props) : this.handleSingleSlide(currentSlide, slides[nextActiveIndex]);
    this.handlePicker(currentPicker, pickers[nextActiveIndex]);
  }
  getCurrentSlideIndex(slides) {
    return slides.findIndex(
      (slide) => this.domProvider.hasClass(slide, PROJECT_CLASSES.ACTIVE_ELEMENT)
    );
  }
  getNextIndex(currentIndex, length, direction) {
    if (currentIndex + direction >= length)
      return 0;
    if (currentIndex + direction < 0)
      return length - 1;
    return currentIndex + direction;
  }
  handleMultipleSlides(props) {
    this.domProvider.setTimeout(() => {
      if (props.selectors.activeSlides) {
        const { activeSlides, carouselContainer, slide } = props.selectors;
        const currentActiveSlides = this.domProvider.querySelectorAll(
          activeSlides,
          carouselContainer
        );
        currentActiveSlides.forEach((slide2) => Utils$1.makeElementTabbable(slide2));
        const nonActiveSlides = this.domProvider.querySelectorAll(
          `${slide}:not(${activeSlides})`,
          carouselContainer
        );
        nonActiveSlides.forEach((slide2) => Utils$1.hideElement(slide2));
      }
    }, 1e3);
  }
  handleSingleSlide(current, nextActiveSlide) {
    if (current === nextActiveSlide)
      return;
    this.domProvider.removeClass(current, PROJECT_CLASSES.ACTIVE_ELEMENT);
    this.domProvider.setAttribute(current, ARIA.HIDDEN, "true");
    this.domProvider.addClass(nextActiveSlide, PROJECT_CLASSES.ACTIVE_ELEMENT);
    this.domProvider.setAttribute(nextActiveSlide, ARIA.HIDDEN, "false");
  }
  handlePicker(current, nextActivePicker) {
    if (current === nextActivePicker)
      return;
    this.domProvider.setAttribute(current, ARIA.DISABLED, "false");
    this.domProvider.setAttribute(nextActivePicker, ARIA.DISABLED, "true");
  }
}

class CarouselRoles {
  constructor(domProvider, carouselButtons, translateService) {
    this.domProvider = domProvider;
    this.carouselButtons = carouselButtons;
    this.translateService = translateService;
  }
  handleCarouselRoles(carouselContainerElement, props) {
    const { slide, prevButton, nextButton, absoluteCarouselContainerLabel, slidePickerButtons } = props.selectors;
    const slides = this.domProvider.querySelectorAll(slide, carouselContainerElement);
    this.handleCarouselContainer(carouselContainerElement, absoluteCarouselContainerLabel);
    this.handleCarouselSlides(slides);
    prevButton && this.handleCarouselButtons(
      carouselContainerElement,
      prevButton,
      this.translateService.getValue(TranslateText.PREV_SLIDE)
    );
    nextButton && this.handleCarouselButtons(
      carouselContainerElement,
      nextButton,
      this.translateService.getValue(TranslateText.NEXT_SLIDE)
    );
    if (slidePickerButtons) {
      const btnsContainer = Utils$1.getSharedParent(slidePickerButtons, carouselContainerElement);
      this.handleCarouselPickerButtons(
        slidePickerButtons,
        carouselContainerElement,
        btnsContainer,
        slides
      );
    }
  }
  handleCarouselContainer(el, absoluteLabel) {
    this.domProvider.setAttribute(el, ATTRIBUTES.ROLE, ROLE$1.GROUP);
    this.domProvider.setAttribute(
      el,
      ARIA.ROLEDESCRIPTION,
      this.translateService.getValue(TranslateText.CAROUSEL)
    );
    if (absoluteLabel) {
      const labelElement = this.domProvider.querySelector(absoluteLabel);
      this.domProvider.setAttributeWithID(labelElement, el, ARIA.LABELLEDBY);
    }
  }
  handleCarouselSlides(slides) {
    slides.forEach((slide) => {
      this.domProvider.setAttribute(slide, ATTRIBUTES.ROLE, ROLE$1.GROUP);
      this.domProvider.setAttribute(
        slide,
        ARIA.ROLEDESCRIPTION,
        this.translateService.getValue(TranslateText.SLIDE)
      );
    });
    const numOfSlides = slides.length;
    let currentIndex = 1;
    slides.forEach((slide) => {
      const ofValue = this.translateService.getValue(TranslateText.OF);
      const slideText = `${currentIndex} ${ofValue} ${numOfSlides}`;
      this.domProvider.setAttribute(slide, ARIA.LABEL, slideText);
      currentIndex = currentIndex === numOfSlides ? 1 : currentIndex + 1;
    });
  }
  handleCarouselButtons(context, selector, label) {
    const element = this.domProvider.querySelector(selector, context);
    if (element) {
      this.domProvider.setAttribute(element, ARIA.LABEL, label);
      this.carouselButtons.setButton(element);
    }
  }
  handleCarouselPickerButtons(pickerBtnsSelector, carouselContainerElement, pickersContainer, slides) {
    if (pickersContainer) {
      this.domProvider.setAttribute(pickersContainer, ATTRIBUTES.ROLE, ROLE$1.GROUP);
      this.domProvider.setAttribute(
        pickersContainer,
        ARIA.LABEL,
        this.translateService.getValue(TranslateText.SLIDES)
      );
    }
    const pickerButtons = this.domProvider.querySelectorAll(
      pickerBtnsSelector,
      carouselContainerElement
    );
    pickerButtons.forEach((el, i) => {
      this.domProvider.addEventListener(
        el,
        "keyup",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );
      this.carouselButtons.setButton(el);
      this.domProvider.setAttributeWithID(slides[i], el, ARIA.LABELLEDBY);
    });
  }
}

var KEY_CODES = /* @__PURE__ */ ((KEY_CODES2) => {
  KEY_CODES2["TAB"] = "Tab";
  KEY_CODES2["SHIFT_TAB"] = "ShiftLeft";
  KEY_CODES2["ENTER"] = "Enter";
  KEY_CODES2["ESC"] = "Escape";
  KEY_CODES2["SPACE"] = "Space";
  KEY_CODES2["PAGEUP"] = "PageUp";
  KEY_CODES2["PAGEDOWN"] = "PageDown";
  KEY_CODES2["END"] = "End";
  KEY_CODES2["HOME"] = "Home";
  KEY_CODES2["LEFT"] = "ArrowLeft";
  KEY_CODES2["UP"] = "ArrowUp";
  KEY_CODES2["RIGHT"] = "ArrowRight";
  KEY_CODES2["DOWN"] = "ArrowDown";
  return KEY_CODES2;
})(KEY_CODES || {});

class CarouselButtons {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  setButton(element) {
    this.domProvider.setAttribute(element, ATTRIBUTES.ROLE, ROLE$1.BUTTON);
    this.domProvider.setAttribute(element, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.addEventListener(element, "keydown", (e) => {
      this.addMouseEvent(e, element);
    });
  }
  addMouseEvent(e, el) {
    const evt = e;
    if (evt.code === KEY_CODES.ENTER || evt.code === KEY_CODES.SPACE) {
      this.domProvider.dispatchMouseClickEvent(el);
    }
  }
}

const CarouselApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const carouselButtons = new CarouselButtons(domProvider);
  const stateManagement = StateManagement.getInstance();
  const translateService = new Translate(stateManagement);
  const carouselRoles = new CarouselRoles(
    domProvider,
    carouselButtons,
    translateService
  );
  const movingContent = new MovingContent(domProvider);
  return (props) => {
    new CarouselFixer(context, props, domProvider, changeDetection, carouselRoles, movingContent);
  };
};

class CheckboxFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, toggleStateService, elementVisibility) {
    super(context, domProvider, changeDetection, props, props.selectors.element);
    this.toggleStateService = toggleStateService;
    this.elementVisibility = elementVisibility;
  }
  fix(visibleElement) {
    const { checkedState, uncheckedState, exclude, disabled, label } = this.props.selectors;
    this.domProvider.setAttribute(visibleElement, ATTRIBUTES.ROLE, ROLE$1.CHECKBOX);
    this.domProvider.setAttribute(visibleElement, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.addEventListener(
      visibleElement,
      "keydown",
      (e) => this.addKeyboardEvent(e)
    );
    if (checkedState && uncheckedState || this.props.stateHandler) {
      const stateProps = {
        selectors: {
          trueState: checkedState,
          falseState: uncheckedState,
          element: this.props.selectors.element
        },
        stateHandler: this.props.stateHandler
      };
      this.toggleStateService.fix(visibleElement, stateProps, ARIA.CHECKED);
    }
    const elToExclude = this.domProvider.querySelectorAll(
      exclude,
      this.context
    );
    elToExclude.forEach(
      (el) => !this.elementVisibility.isVisible(el) && this.excludeFocusFromEl(el, visibleElement)
    );
    if (disabled) {
      const disabledEls = this.domProvider.querySelectorAll(
        disabled,
        this.context
      );
      disabledEls.forEach((el) => {
        this.domProvider.setAttribute(el, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
        this.domProvider.setAttribute(el, ATTRIBUTES.TABINDEX, "-1");
        this.domProvider.setAttribute(el, ARIA.DISABLED, "true");
      });
    }
    if (label) {
      const labelEl = this.domProvider.querySelector(label, this.context);
      const labelText = labelEl == null ? void 0 : labelEl.innerText.trim();
      labelText && this.domProvider.setAttribute(visibleElement, ARIA.LABEL, labelText);
      this.domProvider.setAttribute(labelEl, ARIA.HIDDEN, "true");
    }
  }
  addKeyboardEvent(e) {
    const evt = e;
    if (evt.code === "Space") {
      this.domProvider.dispatchMouseClickEvent(evt.target);
      evt.preventDefault();
    }
  }
  excludeFocusFromEl(el, checkbox) {
    if (el === checkbox)
      return;
    el.style.visibility = "hidden";
    this.domProvider.setAttribute(el, PROJECT_CLASSES.EXCLUDE_ATTRIBUTE, "true");
  }
}
const CheckboxApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const toggleStateService = new ToggleStateService(domProvider, true);
  return (props) => new CheckboxFixer(
    context,
    props,
    domProvider,
    changeDetection,
    toggleStateService,
    elementVisibility
  );
};

class ScrollService {
  constructor(domProvider = new DomProvider()) {
    this.domProvider = domProvider;
  }
  getScrollableElement(element) {
    while (element && element.tagName != "BODY") {
      if (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }
  isElementInContainerView(element, container) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const isInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
    return isInView;
  }
  scrollElementIntoView(element, container, evtKey) {
    if (element) {
      !this.isElementInContainerView(element, container) && this.domProvider.scrollIntoView(element);
    }
  }
}

class ListboxOption {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleOption(optionsElement, optionId, group) {
    if (optionId) {
      this.domProvider.setAttribute(optionsElement, ATTRIBUTES.ID, optionId);
    }
    if (group) {
      const grouping = this.domProvider.getPreviousSiblingWithSelector(optionsElement, group);
      const groupId = grouping && Utils$1.getOrCreateId(grouping);
      groupId && this.domProvider.setAttribute(optionsElement, ARIA.DESCRIBEDBY, groupId);
    }
    const elementsInsideOption = this.domProvider.querySelectorAll(
      "*",
      optionsElement
    );
    Utils$1.removeClientsWCAGAttributes(elementsInsideOption);
    this.domProvider.setAttribute(optionsElement, ATTRIBUTES.ROLE, ROLE$1.OPTION);
    if (Utils$1.isMobileDevice()) {
      this.domProvider.setAttribute(optionsElement, ATTRIBUTES.TABINDEX, "0");
    }
  }
}

class AttributesHandler {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleAttributes(focusedElement, optionsElements, triggerElement, activeElementID, listboxLabel, evtKey) {
    var _a;
    if (!activeElementID) {
      if (evtKey == "ArrowUp") {
        const lastIndex = optionsElements.length - 1;
        activeElementID = String(this.domProvider.getAttribute(optionsElements[lastIndex], "id"));
      }
      if (evtKey == "ArrowDown") {
        activeElementID = String(this.domProvider.getAttribute(optionsElements[0], "id"));
      }
    }
    const activeElm = this.domProvider.getElementById(
      String(activeElementID)
    );
    if (listboxLabel) {
      const listboxLabelID = (_a = this.domProvider.getAttribute(listboxLabel, "id")) != null ? _a : "";
      this.domProvider.setAttribute(
        triggerElement,
        ARIA.LABELLEDBY,
        listboxLabelID + " " + activeElementID
      );
    }
    this.domProvider.setAttribute(focusedElement, ARIA.ACTIVEDESCENDANT, String(activeElementID));
    const elmWithFocusedClass = this.domProvider.querySelector(".u1st-focused");
    this.domProvider.removeClass(elmWithFocusedClass, PROJECT_CLASSES.FOCUSED_ELEMENT);
    this.domProvider.addClass(activeElm, PROJECT_CLASSES.FOCUSED_ELEMENT);
    this.domProvider.removeAttributeToAll(optionsElements, ARIA.SELECTED);
    this.domProvider.setAttribute(activeElm, ARIA.SELECTED, "true");
  }
}

class NavigationHandler {
  constructor(domProvider, listboxAttributesHandler, scrollService, elementVisibility, shouldWrapOptions) {
    this.domProvider = domProvider;
    this.listboxAttributesHandler = listboxAttributesHandler;
    this.scrollService = scrollService;
    this.elementVisibility = elementVisibility;
    this.shouldWrapOptions = shouldWrapOptions;
  }
  navigateToFirstOrLast({
    focusedElement,
    triggerElement,
    listboxLabel,
    evt,
    focusedOption,
    scrollableElement,
    optionsElements
  }, isLast) {
    const elementId = this.getFirstOrLastOption(optionsElements, isLast);
    optionsElements.length ? this.listboxAttributesHandler.handleAttributes(
      focusedElement,
      optionsElements,
      triggerElement,
      elementId,
      listboxLabel,
      evt.code
    ) : null;
    focusedOption = this.domProvider.querySelector(".u1st-focused");
    scrollableElement ? this.scrollService.scrollElementIntoView(focusedOption, scrollableElement, evt.code) : null;
  }
  getFirstOrLastOption(optionsElements, isLast) {
    return this.getOptionId(
      isLast ? optionsElements[optionsElements.length - 1] : optionsElements[0]
    );
  }
  getNextOptionId(optionsElements, option) {
    return this.getNearOptionId(optionsElements, option, 1);
  }
  getPrevOptionId(optionsElements, option) {
    return this.getNearOptionId(optionsElements, option, -1);
  }
  getNearOptionId(optionsElements, option, indexAddition) {
    const optionIndex = this.getOptionNumber(optionsElements, option);
    let nearOptionIndex = optionIndex + indexAddition;
    if (this.shouldWrapOptions) {
      nearOptionIndex = (nearOptionIndex + optionsElements.length) % optionsElements.length;
    } else {
      if (nearOptionIndex < 0 || nearOptionIndex > optionsElements.length - 1) {
        nearOptionIndex = optionIndex;
      }
    }
    return this.getOptionId(optionsElements[nearOptionIndex]);
  }
  getOptionId(optionElement) {
    return this.domProvider.getAttribute(optionElement, "id");
  }
  getOptionNumber(optionsElements, option) {
    let optionIndex = 0;
    optionsElements.forEach((optionsElement, index) => {
      if (optionsElement === option) {
        optionIndex = index;
      }
    });
    return optionIndex;
  }
  handleDefaultKeycode({
    evt,
    optionsElements,
    focusedElement,
    triggerElement,
    listboxLabel,
    scrollableElement
  }) {
    var _a;
    if (((_a = evt.code) == null ? void 0 : _a.slice(0, 3)) == "Key") {
      const prefix = evt.code.slice(3);
      optionsElements.forEach((optionElement) => {
        if (this.elementVisibility.isVisible(optionElement) && optionElement.innerText.toLowerCase().substring(0, prefix.length) === prefix.toLowerCase()) {
          const optionId = this.getOptionId(optionElement);
          optionsElements.length ? this.listboxAttributesHandler.handleAttributes(
            focusedElement,
            optionsElements,
            triggerElement,
            optionId,
            listboxLabel,
            evt.code
          ) : null;
          const focusedOption = this.domProvider.querySelector(
            ".u1st-focused"
          );
          scrollableElement ? this.scrollService.scrollElementIntoView(focusedOption, scrollableElement, evt.code) : null;
        }
      });
    }
  }
  handleUpOrDown({
    optionsElements,
    focusedElement,
    triggerElement,
    evt,
    listboxLabel,
    focusedOption,
    scrollableElement,
    activeElement
  }, isDown) {
    const ElementID = isDown ? this.getNextOptionId(optionsElements, activeElement) : this.getPrevOptionId(optionsElements, activeElement);
    optionsElements.length ? this.listboxAttributesHandler.handleAttributes(
      focusedElement,
      optionsElements,
      triggerElement,
      ElementID,
      listboxLabel,
      evt.code
    ) : null;
    focusedOption = this.domProvider.querySelector(".u1st-focused");
    scrollableElement ? this.scrollService.scrollElementIntoView(focusedOption, scrollableElement, evt.code) : null;
  }
}

class EventsHandler$1 {
  constructor(domProvider, listboxAttributesHandler, elementVisibility, scrollService) {
    this.domProvider = domProvider;
    this.listboxAttributesHandler = listboxAttributesHandler;
    this.elementVisibility = elementVisibility;
    this.scrollService = scrollService;
  }
  handleEvents(optionsElements, focusedElement, triggerElement, evt, listboxLabel, props) {
    const isCombobox = this.domProvider.getTagName(focusedElement) == "INPUT" || focusedElement === triggerElement;
    const activeElementID = this.domProvider.getAttribute(focusedElement, ARIA.ACTIVEDESCENDANT);
    const activeElement = activeElementID ? this.domProvider.getElementById(activeElementID) : optionsElements[0];
    const scrollableElement = this.scrollService.getScrollableElement(
      isCombobox ? optionsElements[0] : focusedElement
    );
    const eventParams = {
      focusedElement,
      triggerElement,
      listboxLabel,
      evt,
      focusedOption: null,
      scrollableElement,
      optionsElements,
      activeElement
    };
    const navigationHandler = new NavigationHandler(
      this.domProvider,
      this.listboxAttributesHandler,
      this.scrollService,
      this.elementVisibility,
      isCombobox
    );
    switch (evt.code) {
      case KEY_CODES.END:
        navigationHandler.navigateToFirstOrLast(eventParams, true);
        break;
      case KEY_CODES.HOME:
        navigationHandler.navigateToFirstOrLast(eventParams, false);
        break;
      case KEY_CODES.DOWN:
        navigationHandler.handleUpOrDown(eventParams, true);
        break;
      case KEY_CODES.UP:
        navigationHandler.handleUpOrDown(eventParams, false);
        break;
      case KEY_CODES.ENTER:
      case KEY_CODES.SPACE:
        this.domProvider.dispatchClickEvent(activeElement);
        if ((props == null ? void 0 : props.closeOnSelect) !== false) {
          this.domProvider.setTimeout(() => {
            this.closeListbox(focusedElement, triggerElement);
          }, 200);
        }
        break;
      case KEY_CODES.ESC:
        this.closeListbox(focusedElement, triggerElement);
        break;
      case KEY_CODES.TAB:
        if (evt.shiftKey) {
          triggerElement.setAttribute("tabindex", "-1");
          setTimeout(() => {
            triggerElement.setAttribute("tabindex", "0");
          }, 0);
        }
        this.domProvider.dispatchClickEvent(activeElement);
        this.closeListbox(focusedElement, triggerElement);
        break;
      default:
        navigationHandler.handleDefaultKeycode(eventParams);
    }
  }
  closeListbox(focusedElement, triggerElement) {
    var _a;
    const isFocusedElInDom = this.domProvider.contains(focusedElement);
    if (this.elementVisibility.isVisible(focusedElement) && isFocusedElInDom) {
      this.removeFocus();
      this.domProvider.dispatchClickEvent(triggerElement);
    }
    this.domProvider.setAttribute(triggerElement, ARIA.EXPANDED, "false");
    const lastClickledTrigger = (_a = this.domProvider.querySelector(PROJECT_CLASSES.TRIGGER_ELEMENT)) != null ? _a : triggerElement;
    this.domProvider.focusOnElement(lastClickledTrigger);
    this.domProvider.removeAttribute(lastClickledTrigger, PROJECT_CLASSES.TRIGGER_ELEMENT);
  }
  removeFocus() {
    const allFocusedElements = this.domProvider.querySelectorAll(".u1st-focused");
    allFocusedElements == null ? void 0 : allFocusedElements.forEach(
      (elem) => this.domProvider.removeClass(elem, PROJECT_CLASSES.FOCUSED_ELEMENT)
    );
  }
}

class ComboboxListbox {
  constructor(domProvider) {
    this.listboxElement = null;
    this.labelElement = null;
    this.domProvider = domProvider;
  }
  handleListbox(props) {
    const { listbox, label } = props.selectors;
    listbox ? this.listboxElement = this.domProvider.querySelector(listbox) : null;
    label ? this.labelElement = this.domProvider.querySelector(label) : null;
    if (!this.listboxElement)
      return;
    this.domProvider.setAttribute(this.listboxElement, "role", "listbox");
    if (!this.labelElement)
      return;
    const labelText = String(this.domProvider.getTextContent(this.labelElement));
    this.domProvider.setAttribute(this.listboxElement, ARIA.LABELLEDBY, labelText);
  }
}

class ComboboxTextbox {
  constructor(domProvider, eventsHandler, elementVisibility) {
    this.textboxElement = null;
    this.listboxElement = null;
    this.optionsElements = [];
    this.labelElement = null;
    this.domProvider = domProvider;
    this.eventsHandler = eventsHandler;
    this.elementVisibility = elementVisibility;
  }
  handleTextbox(props) {
    const { textbox, listbox, label } = props.selectors;
    const autocompleteType = props.isAutocompleteList ? "list" : "both";
    this.textboxElement = this.domProvider.querySelector(textbox);
    listbox ? this.listboxElement = this.domProvider.querySelector(listbox) : null;
    if (this.textboxElement) {
      this.textboxElement.setAttribute(AVOID_AUTO_HIDDEN_ATTRIBUTE, "true");
      label && (this.labelElement = this.domProvider.querySelector(label));
      if (this.labelElement) {
        const labelIdSelector = Utils$1.getOrCreateId(this.labelElement);
        this.domProvider.setAttribute(this.textboxElement, ARIA.LABELLEDBY, labelIdSelector);
      }
      this.domProvider.setAttribute(this.textboxElement, ARIA.AUTOCOMPLETE, autocompleteType);
      this.domProvider.addEventListener(
        this.textboxElement,
        "keydown",
        (e) => this.textboxKeydownFunction(e, props),
        true
      );
    }
  }
  textboxKeydownFunction(_evt, props) {
    const evt = _evt;
    const isEscape = evt.code == "Escape";
    const isMoveEvent = evt.code == "Tab" || evt.code == "ShiftLeft";
    const isTextboxEvent = evt.code == "Enter" || evt.code == "Space" || evt.code == "ArrowDown" || evt.code == "ArrowUp" || isMoveEvent || isEscape;
    const { options, textbox } = props.selectors;
    this.optionsElements = this.getVisibleOptions(options);
    this.textboxElement = this.domProvider.querySelector(textbox);
    this.labelElement = props.selectors.label ? this.domProvider.querySelector(props.selectors.label) : null;
    isEscape ? this.domProvider.changeValue(this.textboxElement, "") : null;
    const focusedOption = this.domProvider.querySelector(".u1st-focused");
    if (isMoveEvent && focusedOption)
      this.domProvider.dispatchClickEvent(focusedOption);
    if (isTextboxEvent && this.optionsElements && this.textboxElement && this.elementVisibility.isVisible(this.optionsElements[0])) {
      const focusedOption2 = this.domProvider.querySelector(".u1st-focused");
      if (isEscape && focusedOption2) {
        this.removeOptionFocus(this.textboxElement, focusedOption2);
      }
      const isHasLink = this.domProvider.querySelector("a", this.optionsElements[0]) != null;
      if (!isEscape && !isHasLink && evt.code != "Space" && !isMoveEvent) {
        _evt.preventDefault();
        _evt.stopPropagation();
      }
      this.eventsHandler.handleEvents(
        this.optionsElements,
        this.textboxElement,
        this.textboxElement,
        evt,
        this.labelElement
      );
    }
  }
  closeListbox(textbox) {
    const textboxElement = this.domProvider.querySelector(textbox);
    textboxElement ? this.domProvider.focusOnElement(textboxElement) : null;
  }
  getVisibleOptions(optionsSelector) {
    const options = this.domProvider.querySelectorAll(optionsSelector);
    return options.filter((option) => this.elementVisibility.isVisible(option));
  }
  removeOptionFocus(textboxElement, focusedOption) {
    this.domProvider.removeAttribute(textboxElement, ARIA.ACTIVEDESCENDANT);
    this.domProvider.removeAttribute(focusedOption, ARIA.SELECTED);
    this.domProvider.removeClass(focusedOption, PROJECT_CLASSES.FOCUSED_ELEMENT);
  }
}

class ComboboxFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, comboboxTextbox, listboxOption, comboboxListbox, elementVisibility) {
    var _a;
    super(
      context,
      domProvider,
      changeDetection,
      props,
      props.selectors.emptyOption ? [props.selectors.textbox, props.selectors.emptyOption] : props.selectors.textbox
    );
    this.textboxElement = null;
    this.listboxElement = null;
    this.optionsElements = [];
    this.labelElement = null;
    this.emptyOptionElement = null;
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.comboboxTextbox = comboboxTextbox;
    this.listboxOption = listboxOption;
    this.comboboxListbox = comboboxListbox;
    this.elementVisibility = elementVisibility;
    this.optionHasFocus = false;
    this.isAutocompleteList = (_a = props.isAutocompleteList) != null ? _a : false;
  }
  fix() {
    const { selectors } = this.props;
    const { textbox, options, label, emptyOption } = selectors;
    this.textboxElement = this.domProvider.querySelector(textbox);
    this.labelElement = label ? this.domProvider.querySelector(label) : null;
    this.emptyOptionElement = emptyOption ? this.domProvider.querySelector(emptyOption) : null;
    this.handleCombobox();
    this.handleEmptyOption();
    this.comboboxTextbox.handleTextbox(this.props);
    this.changeDetection.subscribe(
      this.context,
      "comboboxOptions",
      options,
      () => this.onOptionsShow()
    );
  }
  handleCombobox() {
    this.domProvider.setAttribute(this.textboxElement, "role", "combobox");
    this.domProvider.setAttribute(this.textboxElement, ARIA.EXPANDED, "false");
    this.domProvider.setAttribute(this.textboxElement, ARIA.HASPOPUP, "listbox");
    this.changeDetection.subscribe(
      this.context,
      "combobox",
      this.props.selectors.listbox,
      // on show
      () => this.domProvider.setAttribute(this.textboxElement, ARIA.EXPANDED, "true"),
      // on hide
      () => this.domProvider.setAttribute(this.textboxElement, ARIA.EXPANDED, "false")
    );
  }
  handleEmptyOption() {
    if (!this.emptyOptionElement)
      return;
    this.domProvider.setAttribute(this.emptyOptionElement, "role", "status");
  }
  onOptionsShow() {
    const { listbox, options } = this.props.selectors;
    this.optionsElements = this.getVisibleOptions(options);
    if (listbox) {
      this.listboxElement = this.domProvider.querySelector(listbox);
      this.comboboxListbox.handleListbox(this.props);
    }
    this.handleOptions();
    if (!this.optionHasFocus) {
      this.setInitialAttributes();
    }
  }
  handleOptions() {
    this.optionHasFocus = !!this.domProvider.querySelector(PROJECT_CLASSES.FOCUSED_ELEMENT);
    if (this.optionsElements.length) {
      this.optionsElements.forEach((option) => {
        const optionsElementsID = this.domProvider.getAttribute(option, "id");
        const hasId = optionsElementsID !== null && optionsElementsID !== "";
        this.listboxOption.handleOption(option, hasId ? null : Utils$1.uuid());
      });
      if (!this.optionHasFocus) {
        this.domProvider.addClass(this.optionsElements[0], PROJECT_CLASSES.FOCUSED_ELEMENT);
        this.domProvider.setAttribute(this.optionsElements[0], ARIA.SELECTED, "true");
      }
    }
  }
  setInitialAttributes() {
    this.domProvider.setAttribute(this.textboxElement, ARIA.EXPANDED, "true");
    this.domProvider.setAttributeWithID(this.listboxElement, this.textboxElement, ARIA.OWNS);
    this.domProvider.setAttributeWithID(this.listboxElement, this.textboxElement, ARIA.CONTROLS);
    this.optionsElements && this.domProvider.setAttributeWithID(
      this.optionsElements[0],
      this.textboxElement,
      ARIA.ACTIVEDESCENDANT
    );
  }
  getVisibleOptions(optionsSelector) {
    const options = this.domProvider.querySelectorAll(optionsSelector);
    return options.filter((option) => this.elementVisibility.isVisible(option));
  }
}
const ComboboxApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const scrollService = new ScrollService();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const attributesHandler = new AttributesHandler(domProvider);
  const eventsHandler = new EventsHandler$1(
    domProvider,
    attributesHandler,
    elementVisibility,
    scrollService
  );
  const comboboxTextbox = new ComboboxTextbox(domProvider, eventsHandler, elementVisibility);
  const listboxOption = new ListboxOption(domProvider);
  const comboboxListbox = new ComboboxListbox(domProvider);
  return (props) => new ComboboxFixer(
    context,
    props,
    domProvider,
    changeDetection,
    comboboxTextbox,
    listboxOption,
    comboboxListbox,
    elementVisibility
  );
};

class DatepickerFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, month, year, calendarService) {
    super(context, domProvider, changeDetection, props, props.container);
    this.props = props;
    this.domProvider = domProvider;
    this.month = month;
    this.year = year;
    this.calendarService = calendarService;
  }
  fix(visibleCalendar) {
    this.month.fix(
      this.props.month,
      visibleCalendar,
      this.calendarService,
      this.props.uniqueClickEvent
    );
    this.year.fix(
      this.props.year,
      visibleCalendar,
      this.calendarService,
      this.props.uniqueClickEvent
    );
    this.calendarService.fix(visibleCalendar);
  }
}

class DatePickerHeader {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  fix(props, context, calendarService, uniqueClickEvent) {
    this.props = props;
    this.context = context;
    this.calendarService = calendarService;
    this.uniqueClickEvent = uniqueClickEvent;
    this.setRoles();
  }
  setDescription(prevText, nextText) {
    this.prevEl && this.domProvider.setAttribute(this.prevEl, ARIA.LABEL, prevText);
    this.nextEl && this.domProvider.setAttribute(this.nextEl, ARIA.LABEL, nextText);
  }
  setRoles() {
    this.props.prevButton && (this.prevEl = this.domProvider.querySelector(
      this.props.prevButton,
      this.context
    ));
    this.props.nextButton && (this.nextEl = this.domProvider.querySelector(
      this.props.nextButton,
      this.context
    ));
    this.prevEl && this.setButton(this.prevEl);
    this.nextEl && this.setButton(this.nextEl);
  }
  setButton(el) {
    this.domProvider.setAttribute(el, "role", ROLE$1.BUTTON);
    this.domProvider.setAttribute(el, "tabindex", "0");
    this.domProvider.addEventListener(el, "keydown", (e) => this.onKeydown(e));
    if (this.uniqueClickEvent) {
      this.domProvider.addEventListener(
        el,
        "click",
        () => this.domProvider.dispatchCustomClickEvent(el, this.uniqueClickEvent)
      );
    }
  }
  onKeydown(e) {
    const evt = e;
    const el = e.target;
    switch (evt.code) {
      case "Enter":
        this.gotoNextCalendarView(el);
        break;
    }
  }
  gotoNextCalendarView(el) {
    var _a;
    this.domProvider.dispatchCustomClickEvent(el, (_a = this.uniqueClickEvent) != null ? _a : "click");
    this.calendarService.fix(this.context, false);
    this.setRoles();
  }
  setAriaLive(uniqueId) {
    var _a;
    const textSelector = (_a = this.props.text) == null ? void 0 : _a.selector;
    if (!textSelector)
      return;
    const textEl = this.domProvider.querySelector(textSelector, this.context);
    this.domProvider.setAttribute(textEl, ARIA.LIVE, "polite");
    this.domProvider.setAttribute(textEl, "role", "status");
    if (!this.domProvider.getAttribute(textEl, "id")) {
      this.domProvider.setAttribute(textEl, "id", uniqueId);
    }
  }
}

class Year extends DatePickerHeader {
  constructor(domProvider, translateService) {
    super(domProvider);
    this.translateService = translateService;
  }
  fix(props, context, calendarService, uniqueClickEvent) {
    super.fix(props, context, calendarService, uniqueClickEvent);
    this.setDescription(
      this.translateService.getValue(TranslateText.PREV_YEAR),
      this.translateService.getValue(TranslateText.NEXT_YEAR)
    );
    this.setAriaLive(Utils$1.uuid());
    if (props.text.isClickable) {
      const yearBtn = this.domProvider.querySelector(props.text.selector, this.context);
      yearBtn && this.setButton(yearBtn);
    }
  }
}

class DialogDescription {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  add(dialogElement, headingSelector, textContent) {
    if (!dialogElement)
      return;
    const generatedId = Utils$1.uuid();
    this.setLabel(dialogElement, generatedId, headingSelector);
    textContent && this.setDescribedBy(dialogElement, generatedId, textContent);
  }
  setLabel(dialogElement, generatedId, headingSelector) {
    const headingElement = headingSelector ? this.domProvider.querySelector(headingSelector, dialogElement) : this.domProvider.querySelector(COMMON_SELECTORS.HEADING_ELEMENTS_SELECTOR, dialogElement);
    if (headingElement) {
      const id = headingElement.id || generatedId;
      this.domProvider.setAttribute(headingElement, "id", id);
      this.domProvider.setAttribute(dialogElement, ARIA.LABELLEDBY, id);
    }
  }
  setDescribedBy(dialogElement, generatedId, textContent) {
    const contentElement = this.domProvider.querySelector(textContent, dialogElement);
    if (contentElement) {
      const id = contentElement.id || generatedId;
      this.domProvider.setAttribute(contentElement, "id", id);
      this.domProvider.setAttribute(dialogElement, ARIA.DESCRIBEDBY, id);
    }
  }
}

var DialogType = /* @__PURE__ */ ((DialogType2) => {
  DialogType2["MODAL"] = "modal";
  DialogType2["NON_MODAL"] = "non_modal";
  return DialogType2;
})(DialogType || {});

var __async$1 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class DialogBounding {
  constructor(domProvider, elementVisibility, closeHandler) {
    this.focusableElements = [];
    this.firstVisibleElement = null;
    this.lastElement = null;
    this.domProvider = domProvider;
    this.elementVisibility = elementVisibility;
    this.closeHandler = closeHandler;
  }
  focusInBound(_0) {
    return __async$1(this, arguments, function* (dialogElement, type = DialogType.MODAL) {
      this.domProvider.querySelectorAll(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR, dialogElement).filter((element) => this.elementVisibility.isVisible(element, true))[0].focus();
      dialogElement.addEventListener("keydown", (e) => {
        if (e.code === "Tab") {
          const tabbableElements = this.domProvider.querySelectorAll(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR, dialogElement).filter((element) => this.elementVisibility.isVisible(element, true));
          const { target } = e;
          if (e.shiftKey) {
            if (target === tabbableElements[0]) {
              e.preventDefault();
              tabbableElements[tabbableElements.length - 1].focus();
            }
          } else {
            if (target === tabbableElements[tabbableElements.length - 1]) {
              if (type === DialogType.MODAL) {
                e.preventDefault();
                e.stopPropagation();
                tabbableElements[0].focus();
              } else {
                this.closeHandler.close();
              }
            }
          }
        }
      });
    });
  }
}

const AVOID_TRIGGER_ATTRIBUTE = "avoid-trigger-element";
class DialogTrigger {
  constructor(domProvider) {
    this.currentTriggerMarked = null;
    this.domProvider = domProvider;
  }
  handle(selector, props) {
    this.selector = selector;
    this.props = props;
    const triggerElements = this.domProvider.querySelectorAll(selector);
    triggerElements.forEach((el) => {
      if (!this.domProvider.getAttribute(el, AVOID_TRIGGER_ATTRIBUTE)) {
        this.domProvider.setAttribute(el, ARIA.HASPOPUP, ROLE$1.DIALOG);
        this.domProvider.setAttribute(el, ATTRIBUTES.ROLE, ROLE$1.BUTTON);
        this.domProvider.setAttribute(el, ATTRIBUTES.TABINDEX, "0");
        this.domProvider.setAttribute(el, AVOID_TRIGGER_ATTRIBUTE, "true");
        this.domProvider.addEventListener(
          el,
          "click",
          (evt) => this.markTrigger(evt.target)
        );
        this.domProvider.addEventListener(el, "keydown", (evt) => this.onKeyPress(evt));
      }
    });
  }
  onKeyPress(e) {
    const evt = e;
    if (evt.code == "Enter") {
      const target = evt.target;
      if (!target)
        return;
      this.markTrigger(target);
      if (!this.domProvider.matches(target, COMMON_SELECTORS.FOCUSABLE_ELEMENTS_SELECTOR)) {
        this.domProvider.dispatchMouseClickEvent(target);
      }
    }
  }
  markTrigger(element) {
    const alreadyMarked = this.domProvider.querySelectorAll(`[${PROJECT_CLASSES.TRIGGER_ELEMENT}]`);
    this.domProvider.removeAttributeToAll(alreadyMarked, PROJECT_CLASSES.TRIGGER_ELEMENT);
    this.domProvider.setAttribute(element, PROJECT_CLASSES.TRIGGER_ELEMENT, "true");
  }
  focusOnTrigger() {
    var _a;
    let elToFocus;
    if (this.props) {
      const customElToFocus = this.domProvider.querySelector(
        this.props.selectors.focusTo
      );
      const lastClickedTrigger = this.domProvider.querySelector(
        `[${PROJECT_CLASSES.TRIGGER_ELEMENT}]`
      );
      const triggerEl = this.domProvider.querySelector(this.props.selectors.trigger);
      elToFocus = (_a = customElToFocus != null ? customElToFocus : lastClickedTrigger) != null ? _a : triggerEl;
    }
    if (elToFocus) {
      this.domProvider.setTimeout(() => {
        this.domProvider.focusOnElement(elToFocus);
        this.domProvider.removeAttribute(elToFocus, PROJECT_CLASSES.TRIGGER_ELEMENT);
      }, 200);
    }
  }
}

class CloseHandler {
  constructor(domProvider, navigationControl, translateService, props) {
    this.translateService = translateService;
    this.domProvider = domProvider;
    this.props = props;
    this.navigationControl = navigationControl;
  }
  handle(dialogElement, props) {
    const { closeBtn } = props.selectors;
    this.props = props;
    this.domProvider.addEventListener(dialogElement, "keydown", (evt) => this.onClose(evt));
    if (closeBtn) {
      const closeBtnElement = this.domProvider.querySelector(closeBtn, dialogElement);
      if (closeBtnElement) {
        this.domProvider.setAttribute(closeBtnElement, "tabindex", "0");
        this.domProvider.setAttribute(closeBtnElement, "role", "button");
        this.domProvider.setAttribute(
          closeBtnElement,
          ARIA.LABEL,
          this.translateService.getValue(TranslateText.CLOSE)
        );
        this.domProvider.addEventListener(
          closeBtnElement,
          "keydown",
          (evt) => this.onCloseButtonPressed(evt)
        );
      }
    }
  }
  onClose(e) {
    const evt = e;
    if (evt.code === "Escape") {
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.close();
    }
  }
  onCloseButtonPressed(e) {
    const evt = e;
    if (evt.code === "Enter") {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.close(evt.target);
    }
  }
  close(closeBtnEl) {
    var _a, _b, _c, _d, _e, _f, _g;
    const triggerSelector = (_b = (_a = this.props) == null ? void 0 : _a.selectors) == null ? void 0 : _b.trigger;
    const closeBtnSelector = (_d = (_c = this.props) == null ? void 0 : _c.selectors) == null ? void 0 : _d.closeBtn;
    const dialogSelector = (_f = (_e = this.props) == null ? void 0 : _e.selectors) == null ? void 0 : _f.dialog;
    if ((_g = this.props) == null ? void 0 : _g.onClose) {
      this.props.onClose();
      return;
    }
    if (closeBtnEl) {
      this.domProvider.dispatchMouseClickEvent(closeBtnEl);
    } else if (closeBtnSelector) {
      const closeBtn = this.domProvider.querySelector(closeBtnSelector, dialogSelector);
      closeBtn && this.domProvider.dispatchMouseClickEvent(closeBtn);
    } else if (triggerSelector) {
      const trigger = this.domProvider.querySelector(triggerSelector);
      trigger && this.domProvider.dispatchMouseClickEvent(trigger);
    }
  }
}

var NavigationType = /* @__PURE__ */ ((NavigationType2) => {
  NavigationType2["Block"] = "block";
  NavigationType2["Release"] = "release";
  return NavigationType2;
})(NavigationType || {});
var NavigationPlatform = /* @__PURE__ */ ((NavigationPlatform2) => {
  NavigationPlatform2["Keyboard"] = "keyboard";
  NavigationPlatform2["Screen_Reader"] = "screenReader";
  return NavigationPlatform2;
})(NavigationPlatform || {});
var KeyboardEvents = /* @__PURE__ */ ((KeyboardEvents2) => {
  KeyboardEvents2["KeyUp"] = "keyup";
  KeyboardEvents2["KeyDown"] = "keydown";
  KeyboardEvents2["KeyPress"] = "keypress";
  return KeyboardEvents2;
})(KeyboardEvents || {});

class NavigationControlUtils {
  static getAttributeName(type) {
    return type == NavigationPlatform.Screen_Reader ? ARIA.HIDDEN : "tabindex";
  }
  static getBackupAttributeName(type) {
    return type == NavigationPlatform.Screen_Reader ? PROJECT_CLASSES.PREV_ARIA_HIDDEN : PROJECT_CLASSES.PREV_TABINDEX;
  }
}

const _NavigationControl = class {
  constructor(domProvider, domExtension) {
    this.domExtension = domExtension;
    this.domProvider = domProvider;
  }
  block(excludeElementSelector) {
    const excludedElements = this.domProvider.querySelectorAll(
      `${excludeElementSelector},${excludeElementSelector} *`
    );
    this.domProvider.setAttributeToAll(
      excludedElements,
      _NavigationControl.u1stNavigationBlockFlag,
      "false"
    );
    this.blockedElements = this.domProvider.querySelectorAll(
      `*:not([${_NavigationControl.u1stNavigationBlockFlag}='false'])`
    );
    setTimeout(
      () => {
        var _a;
        return (_a = this.blockedElements) == null ? void 0 : _a.forEach((el) => {
          this.domProvider.setAttribute(el, _NavigationControl.u1stNavigationBlockFlag, "true");
          this.domProvider.setAttribute(el, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
          this.keyboard(NavigationType.Block, el);
          this.screenReader(NavigationType.Block, el);
        });
      }
    );
  }
  release() {
    const blockedElements = this.domProvider.querySelectorAll(
      `[${_NavigationControl.u1stNavigationBlockFlag}='true']`
    );
    blockedElements == null ? void 0 : blockedElements.forEach((el) => {
      this.keyboard(NavigationType.Release, el);
      this.screenReader(NavigationType.Release, el);
      this.domProvider.removeAttribute(el, AVOID_AUTO_TABBABLE_ATTRIBUTE);
      this.domProvider.removeAttribute(el, _NavigationControl.u1stNavigationBlockFlag);
    });
  }
  keyboard(type, el) {
    if (type == NavigationType.Release) {
      this.releaseAttributes(NavigationPlatform.Keyboard, el);
    }
    if (type == NavigationType.Block) {
      this.blockAttributes(NavigationPlatform.Keyboard, el);
    }
  }
  screenReader(type, el) {
    if (type == NavigationType.Release) {
      this.releaseAttributes(NavigationPlatform.Screen_Reader, el);
    }
    if (type == NavigationType.Block) {
      this.blockAttributes(NavigationPlatform.Screen_Reader, el);
    }
  }
  releaseAttributes(type, el) {
    const attributeName = NavigationControlUtils.getAttributeName(type);
    const backupAttributeToRemove = NavigationControlUtils.getBackupAttributeName(type);
    const prevData = this.domProvider.getAttribute(el, backupAttributeToRemove);
    if (prevData) {
      this.domProvider.removeAttribute(el, backupAttributeToRemove);
      this.domProvider.setAttribute(el, attributeName, prevData);
      return;
    }
    this.domProvider.removeAttribute(el, attributeName);
  }
  blockAttributes(type, el) {
    const attributeName = NavigationControlUtils.getAttributeName(type);
    const backupAttribute = NavigationControlUtils.getBackupAttributeName(type);
    const currentAttr = this.domProvider.getAttribute(el, attributeName);
    const value = attributeName === ARIA.HIDDEN ? "true" : "-1";
    if (currentAttr) {
      this.domProvider.setAttribute(el, backupAttribute, currentAttr);
    }
    this.domProvider.setAttribute(el, attributeName, value);
  }
  stopTabPropagation(elementSelector) {
    const elements = this.domProvider.querySelectorAll(elementSelector);
    const dispatchKeyDownEvent = this.domProvider.dispatchKeydownEvent;
    Object.values(KeyboardEvents).forEach((keyboardEvent) => {
      elements.forEach((el) => {
        this.domProvider.addEventListener(el, keyboardEvent, function _listener(e) {
          const evt = e;
          switch (evt.code) {
            case KEY_CODES.TAB:
              evt.stopPropagation();
              evt.stopImmediatePropagation();
              break;
            case KEY_CODES.ESC:
              Object.values(KeyboardEvents).forEach((keyboardEvent2) => {
                el.removeEventListener(keyboardEvent2, _listener);
              });
              dispatchKeyDownEvent(el, KEY_CODES.ESC, true);
              break;
          }
        });
      });
    });
  }
};
let NavigationControl = _NavigationControl;
NavigationControl.u1stNavigationBlockFlag = "u1st-navigation";

const CONSTS = {
  U1ST_CACHED_DATA: "u1st-cached-data-"
};

class DomExtensionProvider {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  restoreCachedAttributeToAll(elements, attrs) {
    elements.forEach((e) => {
      const elAttributes = e.attributes;
      for (const attr in attrs) {
        const cachedAttrName = `${CONSTS.U1ST_CACHED_DATA}${attr}`;
        if (elAttributes[cachedAttrName]) {
          this.domProvider.setAttribute(e, `${attr}`, elAttributes[cachedAttrName].value);
          this.domProvider.removeAttribute(e, cachedAttrName);
        }
      }
    });
  }
  setCachedAttributeToAll(elements, attrs) {
    elements.forEach((e) => {
      const elAttributes = e.attributes;
      for (const attr in attrs) {
        if (elAttributes[attr]) {
          this.domProvider.setAttribute(
            e,
            `${CONSTS.U1ST_CACHED_DATA}${attr}`,
            elAttributes[attr].value
          );
          this.domProvider.setAttribute(e, attr, attrs[attr]);
        }
      }
    });
  }
  getParentsElements(el) {
    const parents = [];
    while (el.parentNode && el.parentNode.nodeName.toLowerCase() != "html") {
      el = el.parentNode;
      parents.push(el);
    }
    return parents;
  }
  getNodes(context, whatToShow, acceptNode) {
    const treeWalker = this.domProvider.getDomDocument().createTreeWalker(context, whatToShow, acceptNode ? { acceptNode } : null);
    const nodeList = [];
    let currentNode = treeWalker.currentNode;
    while (currentNode) {
      nodeList.push(currentNode);
      currentNode = treeWalker.nextNode();
    }
    return nodeList;
  }
  getImgSrc(element) {
    let src;
    const imgElement = this.domProvider.getTagName(element) == "IMG" ? element : this.domProvider.querySelector("img", element);
    if (imgElement) {
      src = this.domProvider.getAttribute(imgElement, "src");
    } else {
      const elmsInItem = this.domProvider.querySelectorAll("*", element);
      elmsInItem.unshift(element);
      for (let i = 0; i < elmsInItem.length; i++) {
        const inlineImgStyle = elmsInItem[i].style.backgroundImage;
        const bgImg = inlineImgStyle ? inlineImgStyle : window.getComputedStyle(elmsInItem[i]).getPropertyValue("background-image");
        if (bgImg && bgImg.startsWith("url")) {
          const indexEnd = bgImg.length - 2;
          src = bgImg.substring(5, indexEnd);
          break;
        }
      }
    }
    return src;
  }
}

class DialogFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, dialogDescription, dialogBounding, dialogTrigger, elementVisibility, closeHandler, navigationControl) {
    var _a;
    super(
      context,
      domProvider,
      changeDetection,
      props,
      (_a = props.selectors.trigger) != null ? _a : props.selectors.dialog,
      (closedElement) => !props.selectors.trigger && this.onClose(closedElement)
    );
    this.dialogElement = null;
    this.props = props;
    this.dialogDescription = dialogDescription;
    this.dialogBounding = dialogBounding;
    this.changeDetection = changeDetection;
    this.dialogTrigger = dialogTrigger;
    this.elementVisibility = elementVisibility;
    this.closeHandler = closeHandler;
    this.navigationControl = navigationControl;
    this.isMobile = Utils$1.isMobileDevice();
    this.contentObserver = null;
    this.observerOptions = {
      childList: true,
      attributes: true,
      subtree: true,
      attributeFilter: ["tabindex", "u1st-visualfocus"],
      attributeOldValue: true
    };
  }
  fix() {
    const { dialog, trigger, waitForElement } = this.props.selectors;
    if (trigger) {
      this.dialogTrigger.handle(trigger, this.props);
      this.changeDetection.subscribe(
        this.context,
        "dialog",
        waitForElement || dialog,
        (visibleDialog) => this.onOpen(visibleDialog),
        (closedElement) => this.onClose(closedElement)
      );
    } else {
      this.onOpen();
    }
  }
  onClose(closedElement) {
    closedElement && this.domProvider.removeAttribute(closedElement, PROJECT_CLASSES.AVOID_CHANGE_DETECTION);
    this.navigationControl.release();
    this.dialogTrigger.focusOnTrigger();
  }
  onOpen(visibleDialog) {
    var _a;
    const { trigger, dialog, heading, waitForElement, textContent } = this.props.selectors;
    const dialogType = (_a = this.props.type) != null ? _a : DialogType.MODAL;
    const isModalType = dialogType === DialogType.MODAL;
    this.dialogElement = waitForElement ? this.domProvider.querySelector(dialog) : visibleDialog || this.domProvider.querySelector(dialog);
    if (!this.dialogElement)
      return;
    this.fixDialogContent();
    this.closeHandler.handle(this.dialogElement, this.props);
    this.dialogDescription.add(this.dialogElement, heading, textContent);
    if (this.props.blockNavigation || this.isMobile) {
      const excludeSelector = this.props.triggeredByToggle ? `${trigger}, ${dialog}` : dialog;
      this.navigationControl.block(excludeSelector);
      this.navigationControl.stopTabPropagation(excludeSelector);
    }
    this.dialogBounding.focusInBound(this.dialogElement, this.props.type);
    this.domProvider.setAttribute(this.dialogElement, "role", ROLE$1.DIALOG);
    !this.isMobile && this.domProvider.setAttribute(this.dialogElement, ARIA.MODAL, isModalType.toString());
    this.props.onOpen && this.props.onOpen();
  }
  fixDialogContent() {
    const parentWithRoles = this.domProvider.closest(
      this.dialogElement,
      `[${ARIA.MODAL}],[role='dialog']`,
      true
    );
    if (parentWithRoles) {
      this.domProvider.removeAttribute(parentWithRoles, ARIA.MODAL);
      this.domProvider.removeAttribute(parentWithRoles, `role`);
    }
    const isDynamic = this.dialogElement && this.props.selectors.dynamicContent && this.domProvider.querySelector(this.props.selectors.dynamicContent, this.dialogElement) !== null;
    const dialogChildren = this.domProvider.querySelectorAll("*", this.dialogElement);
    dialogChildren.forEach((el) => {
      if (el.getAttribute("role") == ROLE$1.DIALOG) {
        this.domProvider.removeAttribute(el, "role");
      }
      if (el.getAttribute(ARIA.MODAL)) {
        this.domProvider.removeAttribute(el, ARIA.MODAL);
      }
    });
    if (isDynamic && this.dialogElement && this.props.selectors.dynamicContent) {
      const dynamicContent = this.domProvider.querySelector(
        this.props.selectors.dynamicContent,
        this.dialogElement
      );
      if (dynamicContent) {
        this.contentObserver = new MutationObserver(this.dynamicConentCallback.bind(this));
        this.contentObserver.observe(dynamicContent, this.observerOptions);
      }
    }
  }
  dynamicConentCallback(mutations) {
    for (const mutation of mutations) {
      if (mutation.attributeName == "tabindex" && mutation.oldValue == "-1") {
        const currentAvoidFocusOnOpenState = this.props.avoidFocusOnOpen;
        this.props.avoidFocusOnOpen = true;
        this.dialogBounding.focusInBound(this.dialogElement, this.props.type);
        this.props.avoidFocusOnOpen = currentAvoidFocusOnOpenState;
        return;
      }
    }
  }
}
const DialogApi = (context = "body") => {
  const domProvider = new DomProvider();
  const domExtension = new DomExtensionProvider(domProvider);
  const elementVisibility = new ElementVisibility();
  const navigationControl = new NavigationControl(domProvider, domExtension);
  const stateManagement = StateManagement.getInstance();
  const translateService = new Translate(stateManagement);
  const closeHandler = new CloseHandler(
    domProvider,
    navigationControl,
    translateService
  );
  const dialogDescription = new DialogDescription(domProvider);
  const dialogBounding = new DialogBounding(
    domProvider,
    elementVisibility,
    closeHandler
  );
  const changeDetection = ChangeDetection.getInstance(
    new ElementVisibility(),
    domProvider
  );
  const dialogTrigger = new DialogTrigger(domProvider);
  return (props) => new DialogFixer(
    context,
    props,
    domProvider,
    changeDetection,
    dialogDescription,
    dialogBounding,
    dialogTrigger,
    elementVisibility,
    closeHandler,
    navigationControl
  );
};

class DatepickerDialog {
  constructor(domProvider, changeDetection, customTrigger, props) {
    this.domProvider = domProvider;
    this.changeDetection = changeDetection;
    this.customTrigger = customTrigger;
    this.props = props;
  }
  initDialog(context, navigation) {
    var _a, _b;
    if ((_a = this.props.openOnFocus) == null ? void 0 : _a.addCustomTrigger) {
      this.customTrigger.subscribe(
        this.props.openOnFocus.addButtonCss,
        navigation,
        this.props,
        context
      );
    } else {
      const dialogProps = {
        selectors: {
          dialog: this.props.container,
          trigger: this.props.trigger
        },
        type: DialogType.MODAL,
        avoidFocusOnOpen: true
      };
      if ((_b = this.props.disable) == null ? void 0 : _b.arrowNavigation) {
        dialogProps.onClose = () => {
          navigation.closeCalendar();
        };
      } else if (this.props.onClose) {
        dialogProps.onClose = this.props.onClose;
      }
      DialogApi(context)(dialogProps);
    }
  }
}

const U1_DAY = "u1st-day";
const _Navigation = class {
  constructor(domProvider, navigationControl, stateManagement, props, elementVisibility) {
    this.domProvider = domProvider;
    this.navigationControl = navigationControl;
    this.stateManagement = stateManagement;
    this.props = props;
    this.elementVisibility = elementVisibility;
    var _a, _b;
    this.calendar = this.domProvider.querySelector(
      `${props.calendar.table}:not([u1-table-marked])`,
      this.props.container
    );
    this.domProvider.setAttribute(this.calendar, "u1-table-marked", "true");
    ((_a = props.month) == null ? void 0 : _a.prevButton) && (this.prevButton = this.domProvider.querySelector(
      props.month.prevButton,
      this.props.container
    ));
    ((_b = props.month) == null ? void 0 : _b.nextButton) && (this.nextButton = this.domProvider.querySelector(
      props.month.nextButton,
      this.props.container
    ));
  }
  navigate(e, uniqueClickEvent) {
    var _a, _b;
    const evt = e;
    const el = e.target;
    switch (evt.code) {
      case KEYBOARD_EVENT_CODE$1.PAGEUP:
        evt.preventDefault();
        (_a = this.gotoFollowingMonth(1)) == null ? void 0 : _a.then((nextFocus) => {
          this.setNextFocusWithAttr(el, nextFocus, 50);
        });
        break;
      case KEYBOARD_EVENT_CODE$1.PAGEDOWN:
        evt.preventDefault();
        (_b = this.gotoFollowingMonth(-1)) == null ? void 0 : _b.then((nextFocus) => {
          this.setNextFocusWithAttr(el, nextFocus, 50);
        });
        break;
      case KEYBOARD_EVENT_CODE$1.RIGHT:
        this.focusOnFollowingElement(el, this.getDirectionByLanguage(1 /* RIGHT */));
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        break;
      case KEYBOARD_EVENT_CODE$1.LEFT:
        this.focusOnFollowingElement(el, this.getDirectionByLanguage(-1 /* LEFT */));
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        break;
      case KEYBOARD_EVENT_CODE$1.UP:
        this.preventScrolling(evt);
        this.focusOnFollowingElement(el, -7 /* UP */);
        evt.stopPropagation();
        break;
      case KEYBOARD_EVENT_CODE$1.DOWN:
        this.preventScrolling(evt);
        this.focusOnFollowingElement(el, 7 /* DOWN */);
        evt.stopPropagation();
        break;
      case KEYBOARD_EVENT_CODE$1.ENTER:
        evt.preventDefault();
        this.domProvider.dispatchCustomClickEvent(el, uniqueClickEvent != null ? uniqueClickEvent : "click");
        break;
      case KEYBOARD_EVENT_CODE$1.HOME:
        evt.preventDefault();
        this.focusOnFollowingWeekDay(el, DIRECTION.START);
        break;
      case KEYBOARD_EVENT_CODE$1.END:
        evt.preventDefault();
        this.focusOnFollowingWeekDay(el, DIRECTION.END);
        break;
      case KEYBOARD_EVENT_CODE$1.ESC:
        evt.preventDefault();
        this.closeCalendar();
        break;
    }
  }
  preventScrolling(keyboardEvent) {
    keyboardEvent.preventDefault();
  }
  onSelected() {
    this.navigationControl.release();
    this.focusOnTriggerAndClose();
  }
  closeCalendar() {
    const trigger = this.domProvider.querySelector(this.props.trigger);
    if (trigger) {
      this.domProvider.setTimeout(() => {
        if (this.props.openOnFocus) {
          this.domProvider.dispatchClickEvent(this.domProvider.getBody());
        } else {
          const calendar = this.domProvider.querySelector(this.props.calendar.table);
          this.domProvider.focusOnElement(trigger);
          calendar && this.elementVisibility.isVisible(calendar) && this.domProvider.dispatchMouseClickEvent(trigger);
        }
      }, 400);
    }
  }
  focusOnTriggerAndClose() {
    const trigger = this.domProvider.querySelector(`[${PROJECT_CLASSES.TRIGGER_ELEMENT}]`);
    if (trigger) {
      this.domProvider.setTimeout(() => {
        const calendar = this.domProvider.querySelector(this.props.calendar.table);
        this.domProvider.focusOnElement(trigger);
        calendar && this.elementVisibility.isVisible(calendar) && this.domProvider.dispatchMouseClickEvent(trigger);
        this.domProvider.removeAttribute(trigger, PROJECT_CLASSES.TRIGGER_ELEMENT);
      }, 400);
    }
  }
  getDirectionByLanguage(direction) {
    const isRtl = Utils$1.isRtl(this.stateManagement.getLang());
    return isRtl ? direction * -1 : direction;
  }
  focusOnFollowingElement(el, direction) {
    var _a;
    const currentCal = Utils$1.findParentWithAttribute(el, PROJECT_ATTRIBUTES.CALENDAR_INDEX);
    const currCalIndex = currentCal ? Number(this.domProvider.getAttribute(currentCal, PROJECT_ATTRIBUTES.CALENDAR_INDEX)) : void 0;
    let elToFocus = this.getNextElement(el, direction, currentCal);
    if (!elToFocus) {
      (_a = this.gotoFollowingMonth(direction, currCalIndex)) == null ? void 0 : _a.then((data) => {
        elToFocus = data;
        this.setNextFocusWithAttr(el, elToFocus, 50);
        return;
      });
    } else {
      this.setNextFocusWithAttr(el, elToFocus);
    }
  }
  getNextElement(el, direction, currentCal) {
    const elCalendarPlace = this.domProvider.getAttribute(el, U1_DAY);
    return this.domProvider.querySelector(
      `[${U1_DAY}=${Number(elCalendarPlace) + direction}]`,
      currentCal != null ? currentCal : this.calendar
    );
  }
  focusOnFollowingWeekDay(el, direction) {
    if (this.domProvider.getTagName(this.calendar).toLowerCase() === TAG_NAMES.TABLE) {
      const row = this.domProvider.closest(el, TAG_NAMES.TR);
      if (!row)
        return;
      const weekDays = this.domProvider.querySelectorAll(`[${U1_DAY}]`, row);
      const elToFocus = direction == DIRECTION.START ? weekDays[0] : weekDays[weekDays.length - 1];
      if (!elToFocus)
        return;
      this.domProvider.setAttribute(elToFocus, ATTRIBUTES.TABINDEX, "0");
      this.domProvider.setAttribute(el, ATTRIBUTES.TABINDEX, "-1");
      this.domProvider.focusOnElement(elToFocus);
    } else {
      const currTopPos = el.getBoundingClientRect().top;
      let nextTopPos = el.getBoundingClientRect().top || void 0;
      let isEmpty = false;
      while (currTopPos === nextTopPos || !isEmpty) {
        if (direction === DIRECTION.START) {
          const next = this.getNextOrPrevEl(false);
          nextTopPos = next == null ? void 0 : next.getBoundingClientRect().top;
          if (!next.innerText) {
            isEmpty = true;
            break;
          }
          if (nextTopPos !== currTopPos || !next)
            break;
          this.setNewFocus(el, next);
        } else if (direction === DIRECTION.END) {
          const next = this.getNextOrPrevEl(true);
          nextTopPos = next == null ? void 0 : next.getBoundingClientRect().top;
          if (!next.innerText) {
            isEmpty = true;
            break;
          }
          if (nextTopPos !== currTopPos || !next)
            break;
          this.setNewFocus(el, next);
        }
      }
    }
  }
  getNextOrPrevEl(isNext) {
    var _a, _b;
    return isNext ? (_a = this.domProvider.querySelector(`[${U1_DAY}][u1st-visualfocus="true"]`)) == null ? void 0 : _a.nextElementSibling : (_b = this.domProvider.querySelector(`[${U1_DAY}][u1st-visualfocus="true"]`)) == null ? void 0 : _b.previousElementSibling;
  }
  setNewFocus(currEl, focusEl) {
    this.domProvider.setAttribute(focusEl, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.setAttribute(currEl, ATTRIBUTES.TABINDEX, "-1");
    this.domProvider.focusOnElement(focusEl);
  }
  setNextFocusWithAttr(curentEl, nextEl, timeout = 10) {
    this.domProvider.setAttribute(nextEl, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.setAttribute(curentEl, ATTRIBUTES.TABINDEX, "-1");
    this.domProvider.setAttribute(nextEl, ARIA.SELECTED, "true");
    this.domProvider.setAttribute(curentEl, ARIA.SELECTED, "false");
    this.domProvider.setTimeout(() => this.domProvider.focusOnElement(nextEl), timeout);
  }
  gotoFollowingMonth(direction, calIndex) {
    var _a, _b;
    const isNext = direction > 0;
    const followingMonth = isNext ? this.nextButton : this.prevButton;
    const daysSelector = (_a = this.props.calendar) == null ? void 0 : _a.activeDays;
    const tableSelector = (_b = this.props.calendar) == null ? void 0 : _b.table;
    const isDoubleDatepicker = this.props.doubleDatepicker;
    if (!followingMonth)
      return null;
    const isInFirstCalendar = typeof calIndex === "number" && calIndex % 2 === 0;
    const shouldGoToNextCal = isNext && !isInFirstCalendar;
    const shouldGoToPrevCal = !isNext && isInFirstCalendar;
    if (!isDoubleDatepicker || isDoubleDatepicker && (shouldGoToNextCal || shouldGoToPrevCal))
      this.domProvider.dispatchKeydownEvent(followingMonth, KEYBOARD_EVENT_CODE$1.ENTER, true);
    return new Promise((res) => {
      this.domProvider.setTimeout(() => {
        _Navigation.focusOnLastDay = direction < 0;
        const nextCalIndex = typeof calIndex === "number" && (direction > 0 ? calIndex + 1 : calIndex - 1);
        const calendarSelector = isDoubleDatepicker && nextCalIndex ? `${tableSelector}[${PROJECT_ATTRIBUTES.CALENDAR_INDEX}="${nextCalIndex}"]` : tableSelector;
        const activeDays = this.domProvider.querySelectorAll(`${daysSelector}`, calendarSelector);
        if (activeDays.length > 0) {
          const day = _Navigation.focusOnLastDay ? activeDays[activeDays.length - 1] : activeDays[0];
          res(day);
        }
      }, 200);
    });
  }
};
let Navigation$2 = _Navigation;
Navigation$2.focusOnLastDay = false;

class DatePickerDays {
  constructor(domProvider, calendarDialog, navigation, props, doubleDatepicker) {
    this.domProvider = domProvider;
    this.calendarDialog = calendarDialog;
    this.navigation = navigation;
    this.props = props;
    this.doubleDatepicker = doubleDatepicker;
  }
  fix(autoFocus = true, uniqueClickEvent) {
    const { selectedDay, activeDays, disabledDays } = this.props;
    selectedDay && !this.selectedDayElement && (this.selectedDayElement = this.domProvider.querySelector(selectedDay, this.calendarDialog));
    this.activeDaysElement = this.domProvider.querySelectorAll(activeDays, this.calendarDialog);
    this.domProvider.setAttributeToAll(
      this.activeDaysElement,
      AVOID_AUTO_TABBABLE_ATTRIBUTE,
      "true"
    );
    if (disabledDays) {
      const disabledEls = this.domProvider.querySelectorAll(disabledDays);
      this.domProvider.setAttributeToAll(disabledEls, ARIA.DISABLED, "true");
      this.domProvider.setAttributeToAll(disabledEls, ATTRIBUTES.TABINDEX, "-1");
    }
    const tables = this.domProvider.querySelectorAll(this.props.table, this.calendarDialog);
    this.domProvider.setAttributeToAll(tables, ATTRIBUTES.ROLE, "grid");
    this.domProvider.setTimeout(() => {
      const shouldFocus = this.shouldFocusOnSelected(autoFocus);
      this.handleSelectedDay(shouldFocus);
      this.handleActiveDays(uniqueClickEvent);
    }, 50);
  }
  handleSelectedDay(autoFocus) {
    this.selectedDayElement = this.getSelectedDay();
    this.domProvider.setAttribute(this.selectedDayElement, ARIA.SELECTED, "true");
    this.domProvider.setAttribute(this.selectedDayElement, ATTRIBUTES.TABINDEX, "0");
    autoFocus && this.domProvider.focusOnElement(this.selectedDayElement);
    Navigation$2.focusOnLastDay = false;
  }
  handleActiveDays(uniqueClickEvent) {
    this.activeDaysElement.forEach((el, i) => {
      const ariaSelected = this.domProvider.getAttribute(el, ARIA.SELECTED);
      if (!ariaSelected) {
        this.domProvider.setAttribute(el, ATTRIBUTES.TABINDEX, "-1");
      }
      this.domProvider.setAttribute(el, "u1st-day", i.toString());
    });
    this.domProvider.addEventListenerToAll(this.activeDaysElement, "keydown", (e) => {
      this.navigation.navigate(e, uniqueClickEvent);
    });
    this.domProvider.addEventListenerToAll(this.activeDaysElement, "click", (e) => {
      this.doubleDatepicker ? this.navigation.navigate(e, KEYBOARD_EVENT_CODE$1.ENTER) : this.navigation.onSelected();
    });
  }
  setAriaDescribedBy(month, year) {
    var _a, _b;
    const monthId = (_a = this.domProvider.querySelector(month)) == null ? void 0 : _a.id;
    const yearId = (_b = this.domProvider.querySelector(year)) == null ? void 0 : _b.id;
    if (monthId && yearId) {
      this.domProvider.setAttributeToAll(
        this.activeDaysElement,
        ARIA.DESCRIBEDBY,
        `${monthId} ${yearId}`
      );
    }
  }
  getSelectedDay() {
    var _a;
    const dayIndexToFocus = Navigation$2.focusOnLastDay ? this.activeDaysElement.length - 1 : 0;
    return (_a = this.selectedDayElement) != null ? _a : this.activeDaysElement[dayIndexToFocus];
  }
  shouldFocusOnSelected(autoFocus) {
    var _a;
    return autoFocus && !((_a = document.activeElement) == null ? void 0 : _a.getAttribute("u1st-day"));
  }
}

class DatePickerCalendar {
  constructor(domProvider, props, elementVisibility, navigation) {
    this.domProvider = domProvider;
    this.props = props;
    this.elementVisibility = elementVisibility;
    this.navigation = navigation;
  }
  fix(visibleCalendar, shouldAutoFocusDay = true) {
    var _a;
    if (!((_a = this.props.disable) == null ? void 0 : _a.arrowNavigation)) {
      setTimeout(() => {
        var _a2, _b, _c, _d, _e, _f, _g, _h;
        if (this.props.doubleDatepicker) {
          const indexedTables = this.domProvider.querySelectorAll(
            `${this.props.calendar.table}[${PROJECT_ATTRIBUTES.CALENDAR_INDEX}]`
          );
          this.domProvider.removeAttributeToAll(indexedTables, PROJECT_ATTRIBUTES.CALENDAR_INDEX);
          const tablesToIndex = this.elementVisibility.getVisibleElements(
            `${this.props.container} ${this.props.calendar.table}`
          );
          tablesToIndex == null ? void 0 : tablesToIndex.forEach((table, i) => {
            this.domProvider.setAttribute(table, PROJECT_ATTRIBUTES.CALENDAR_INDEX, String(i));
          });
        }
        const days = new DatePickerDays(
          this.domProvider,
          visibleCalendar,
          this.navigation,
          this.props.calendar,
          this.props.doubleDatepicker
        );
        days.fix(shouldAutoFocusDay && !!this.props.trigger, this.props.uniqueClickEvent);
        if (((_b = (_a2 = this.props.month) == null ? void 0 : _a2.text) == null ? void 0 : _b.selector) && ((_d = (_c = this.props.year) == null ? void 0 : _c.text) == null ? void 0 : _d.selector)) {
          days.setAriaDescribedBy(
            (_f = (_e = this.props.month) == null ? void 0 : _e.text) == null ? void 0 : _f.selector,
            (_h = (_g = this.props.year) == null ? void 0 : _g.text) == null ? void 0 : _h.selector
          );
        }
      }, 1);
    }
  }
}

class Month extends DatePickerHeader {
  constructor(domProvider, translateService) {
    super(domProvider);
    this.translateService = translateService;
  }
  fix(props, context, calendarService, uniqueClickEvent) {
    super.fix(props, context, calendarService, uniqueClickEvent);
    this.setDescription(
      this.translateService.getValue(TranslateText.PREV_MONTH),
      this.translateService.getValue(TranslateText.NEXT_MONTH)
    );
    this.setAriaLive(Utils$1.uuid());
    if (props.text.isClickable) {
      const monthBtn = this.domProvider.querySelector(props.text.selector, this.context);
      monthBtn && this.setButton(monthBtn);
    }
  }
}

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const CUSTOM_TRIGGER_CLASS = "u1st-custom-trigger";
const CUSTOM_TRIGGER_STYLE_ID = "u1st-custom-datepicker-trigger";
class CustomTrigger {
  constructor(context, domProvider, changeDetection, elementVisibility, waitForElement, translateService) {
    this.context = context;
    this.domProvider = domProvider;
    this.translateService = translateService;
    this.elementVisibility = elementVisibility;
    this.waitForElement = waitForElement;
    this.changeDetection = changeDetection;
  }
  subscribe(customCss, navigation, props, context) {
    this.changeDetection.subscribe(
      this.context,
      "datepickerCustomTrigger",
      // wait for element is the datepicker trigger element
      this.waitForElement,
      // visible element is the datepicker trigger element
      (visibleElement) => this.create(visibleElement, customCss, navigation, props, context)
    );
  }
  create(visibleElement, customCss, navigation, props, context) {
    var _a;
    this.handleRealTrigger(visibleElement);
    const customTriggerEl = this.domProvider.createElement("div");
    const uniqueId = Utils$1.getOrCreateId(customTriggerEl);
    this.domProvider.insertBefore(visibleElement, customTriggerEl, uniqueId);
    this.domProvider.setAttribute(customTriggerEl, "tabindex", "0");
    this.domProvider.setAttribute(customTriggerEl, "role", "button");
    this.domProvider.setAttribute(customTriggerEl, AVOID_AUTO_HIDDEN_ATTRIBUTE, "true");
    this.domProvider.setAttribute(customTriggerEl, ARIA.HIDDEN, "false");
    this.domProvider.setAttribute(
      customTriggerEl,
      ARIA.LABEL,
      this.translateService.getValue(TranslateText.CHOOSE_DATE)
    );
    this.addStyle(customTriggerEl, customCss, uniqueId);
    this.domProvider.addEventListener(customTriggerEl, "click", () => __async(this, null, function* () {
      yield this.open(customTriggerEl, visibleElement);
    }));
    this.domProvider.addEventListener(
      customTriggerEl,
      "keydown",
      (e) => this.onEnter(e, visibleElement)
    );
    const dialogProps = {
      selectors: {
        dialog: props.container,
        trigger: `[id="${uniqueId}"]`
      },
      type: DialogType.MODAL,
      avoidFocusOnOpen: true
    };
    if ((_a = props.disable) == null ? void 0 : _a.arrowNavigation) {
      dialogProps.onClose = () => {
        navigation.closeCalendar();
      };
    } else if (props.onClose) {
      dialogProps.onClose = props.onClose;
    }
    DialogApi(context)(dialogProps);
  }
  handleRealTrigger(trigger) {
    this.domProvider.setAttribute(trigger, "tabindex", "-1");
    this.domProvider.setAttribute(trigger, AVOID_AUTO_HIDDEN_ATTRIBUTE, "true");
    this.domProvider.setAttribute(trigger, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
    this.domProvider.setAttribute(trigger, ARIA.HIDDEN, "true");
  }
  onEnter(e, visibleElement) {
    const evt = e;
    const el = e.target;
    if (evt.code === "Enter") {
      evt.preventDefault();
      (() => __async(this, null, function* () {
        return yield this.open(el, visibleElement);
      }))();
    }
  }
  open(el, visibleElement) {
    return __async(this, null, function* () {
      this.domProvider.focusOnElement(visibleElement);
      yield Utils$1.waitForTimeout(this.domProvider, 100);
      if (!this.elementVisibility.isVisible(el)) {
        this.domProvider.dispatchClickEvent(visibleElement);
      }
    });
  }
  addStyle(customTriggerEl, customCss, uniqueId) {
    this.domProvider.addClass(customTriggerEl, CUSTOM_TRIGGER_CLASS);
    customCss && this.domProvider.addStyle(
      `[id='${uniqueId}']{${customCss.trim()}}`,
      `${CUSTOM_TRIGGER_STYLE_ID}-${uniqueId}`
    );
  }
}

const mapToOldProps$1 = (props) => {
  const {
    selectors,
    isMonthClickable,
    onClose,
    uniqueClickEvent,
    isYearClickable,
    openOnFocus,
    doubleDatepicker,
    disable
  } = props;
  const { container, month, year, days, trigger, secondaryTrigger } = selectors;
  const { table, day, selected, disabled } = days;
  return {
    container,
    trigger,
    secondaryTrigger,
    year: {
      text: {
        selector: year.label,
        isClickable: isYearClickable
      },
      prevButton: year.prevButton,
      nextButton: year.nextButton
    },
    month: {
      text: {
        selector: month.label,
        isClickable: isMonthClickable
      },
      prevButton: month.prevButton,
      nextButton: month.nextButton
    },
    calendar: {
      table,
      selectedDay: selected,
      activeDays: `${day}:not(${disabled})`,
      disabledDays: disabled
    },
    doubleDatepicker,
    openOnFocus,
    uniqueClickEvent,
    onClose,
    disable
  };
};

const DatepickerApi = (context = "body") => {
  const domProvider = new DomProvider();
  const domExtension = new DomExtensionProvider(domProvider);
  const elementVisibility = new ElementVisibility();
  const translateService = new Translate(StateManagement.getInstance());
  const month = new Month(domProvider, translateService);
  const year = new Year(domProvider, translateService);
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const navigationControl = new NavigationControl(domProvider, domExtension);
  return (props) => {
    if ("selectors" in props) {
      props = mapToOldProps$1(props);
    }
    props = props;
    const customTrigger = new CustomTrigger(
      context,
      domProvider,
      changeDetection,
      elementVisibility,
      props.trigger,
      translateService
    );
    const datepickerDialog = new DatepickerDialog(
      domProvider,
      changeDetection,
      customTrigger,
      props
    );
    const stateManagement = StateManagement.getInstance();
    const navigation = new Navigation$2(
      domProvider,
      navigationControl,
      stateManagement,
      props,
      elementVisibility
    );
    const calendarService = new DatePickerCalendar(
      domProvider,
      props,
      elementVisibility,
      navigation
    );
    props.trigger && datepickerDialog.initDialog(context, navigation);
    new DatepickerFixer(context, props, domProvider, changeDetection, month, year, calendarService);
  };
};

class FormFields {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  handleLabelInputFieldsRelationship(labelSelector, inputSelector, form) {
    const labels = this.domProvider.querySelectorAll(labelSelector, form);
    const inputs = this.domProvider.querySelectorAll(inputSelector, form);
    if (labels.length === inputs.length) {
      labels.forEach((label, index) => {
        const input = inputs[index];
        const inputId = Utils$1.getOrCreateId(input);
        const isNativeLabel = this.domProvider.getTagName(label).toLowerCase() === "label";
        if (isNativeLabel)
          this.domProvider.setAttribute(label, "for", inputId);
        else {
          const labelId = Utils$1.getOrCreateId(label);
          this.domProvider.setAttribute(input, ARIA.LABELLEDBY, labelId);
        }
      });
    }
  }
  handleRequiredFields(selector, form) {
    const requiredFields = this.domProvider.querySelectorAll(selector, form);
    requiredFields.forEach((el) => this.domProvider.setAttribute(el, ARIA.REQUIRED, "true"));
  }
  handleInvalidFields(selector, form) {
    this.cleanupAriaInvalid(form);
    const invalidFields = this.domProvider.querySelectorAll(selector, form);
    invalidFields.forEach((el) => this.domProvider.setAttribute(el, ARIA.INVALID, "true"));
  }
  cleanupAriaInvalid(form) {
    const ariaInvalidSelector = `[${ARIA.INVALID}]`;
    const elementsWithAriaInvalid = this.domProvider.querySelectorAll(ariaInvalidSelector, form);
    elementsWithAriaInvalid.forEach((el) => this.domProvider.removeAttribute(el, ARIA.INVALID));
  }
}

class ErrorMessages {
  constructor(domProvider, statusFixer) {
    this.statusFixer = statusFixer;
    this.errorMessage = [];
    this.errorsArray = [];
    this.domProvider = domProvider;
  }
  clearAlert() {
    this.errorsArray = [];
  }
  populateAlert(errorMessage, form) {
    this.srOnlyErrorMessage = this.domProvider.querySelector(
      `.${PROJECT_CLASSES.SR_ONLY}:not(.${PROJECT_CLASSES.ALERT})`,
      errorMessage.parentElement
    );
    this.srOnlyErrorMessage && (errorMessage = this.srOnlyErrorMessage);
    this.errorMessage.push(errorMessage);
    if (!this.errorMessage || this.errorMessage.length == 0)
      return;
    this.cleanupRedundantMarkup(form);
    const errorToAdd = this.domProvider.getTextContent(errorMessage);
    this.errorsArray.push(errorToAdd);
    const finalText = this.errorsArray.join(",");
    this.statusFixer.announceStatus(finalText);
  }
  updateAlert(errorMessage) {
    this.errorMessage = this.errorMessage.filter((err) => err !== errorMessage);
    let updatedText = "";
    this.errorsArray = [];
    this.errorMessage.forEach((error) => {
      const text = this.domProvider.getTextContent(error) ? this.domProvider.getTextContent(error) : "";
      this.errorsArray.push(text);
      updatedText += text + ",";
    });
    this.statusFixer.announceStatus(updatedText);
  }
  cleanupRedundantMarkup(formElement) {
    const selectorForCleanup = `[role=${ROLE$1.ALERT}],[role=${ROLE$1.STATUS}],[${ARIA.ATOMIC}],[${ARIA.LIVE}]`;
    const elementsToClean = this.domProvider.querySelectorAll(
      selectorForCleanup,
      formElement
    );
    elementsToClean.length && Utils$1.removeClientsWCAGAttributes(elementsToClean, [ARIA.ATOMIC, ARIA.LIVE]);
  }
}

class SubmitButton {
  constructor(domProvider, errorMessages, elementVisibility) {
    this.elementVisibility = elementVisibility;
    this.domProvider = domProvider;
    this.errorMessages = errorMessages;
  }
  handleButton(props, context, formElement) {
    const { form, submitButton, errorMsg } = props.selectors;
    if (submitButton && errorMsg) {
      const submitButtonElement = this.domProvider.querySelector(submitButton, formElement);
      ButtonApi(context)({ selectors: { element: `${form} ${submitButton}` } });
      this.domProvider.addEventListener(submitButtonElement, "click", () => {
        const interval = this.domProvider.setInterval(() => {
          let time = 0;
          if (time >= 5e3)
            clearInterval(interval);
          const visibleErrors = this.elementVisibility.getVisibleElements(errorMsg);
          if (visibleErrors.length) {
            clearInterval(interval);
            this.activateAlert(visibleErrors, formElement);
            this.handlInvalidField(props, formElement, submitButton);
          } else {
            time += 500;
          }
        }, 500);
      });
    }
  }
  activateAlert(visibleErrors, formElement) {
    this.errorMessages.clearAlert();
    visibleErrors.forEach((err) => {
      this.errorMessages.populateAlert(err, formElement);
    });
  }
  handlInvalidField(props, formElement, submitButton) {
    if (props.focusOnInvalidField && props.selectors.invalidField) {
      const nextInvalidField = this.domProvider.querySelector(
        props.selectors.invalidField,
        formElement
      );
      if (nextInvalidField) {
        this.domProvider.focusOnElement(nextInvalidField);
      } else {
        const submitButtonElement = this.domProvider.querySelector(submitButton, formElement);
        submitButtonElement && this.domProvider.focusOnElement(submitButtonElement);
      }
    }
  }
}

class FormFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, formFields, errorMessages, submitButton) {
    super(context, domProvider, changeDetection, props, props.selectors.form);
    this.submitButton = submitButton;
    this.formElement = null;
    this.submitButtonElement = null;
    this.formFields = formFields;
    this.errorMessages = errorMessages;
    this.errorMessageAbsolute = props.selectors.errorMsg;
  }
  fix(visibleForm) {
    const { formLabelAbsolute, requiredField, successMsg, inputField } = this.props.selectors;
    this.formElement = visibleForm;
    if (!this.formElement)
      return;
    this.cleanupRedundantRoles();
    this.submitButton.handleButton(this.props, this.context, this.formElement);
    this.domProvider.getTagName(this.formElement) != "FORM" && this.domProvider.setAttribute(this.formElement, "role", ROLE$1.FORM);
    if (formLabelAbsolute) {
      const labelElement = this.domProvider.querySelector(formLabelAbsolute);
      this.domProvider.setAttributeWithID(labelElement, this.formElement, ARIA.LABELLEDBY);
    }
    requiredField && this.formFields.handleRequiredFields(requiredField, this.formElement);
    if (!this.errorMessageAbsolute)
      return;
    this.observer.subscribe(
      this.context,
      "formErrors",
      this.errorMessageAbsolute,
      (errorMessage) => this.onErrorsShow(errorMessage),
      (errorMessage) => this.onErrorsHide(errorMessage)
    );
    if (successMsg) {
      this.observer.subscribe(
        this.context,
        "formSuccessMsg",
        successMsg,
        (successMsg2) => this.domProvider.setAttribute(successMsg2, STATUS_ATTR, "")
      );
    }
    if (formLabelAbsolute && inputField) {
      this.formFields.handleLabelInputFieldsRelationship(
        formLabelAbsolute,
        inputField,
        this.formElement
      );
    }
  }
  onErrorsShow(detectedError) {
    const invalidField = this.props.selectors.invalidField;
    this.errorMessages.populateAlert(detectedError, this.formElement);
    this.formFields.handleInvalidFields(invalidField, this.formElement);
  }
  onErrorsHide(detectedError) {
    const invalidField = this.props.selectors.invalidField;
    this.errorMessages.updateAlert(detectedError);
    this.formFields.handleInvalidFields(invalidField, this.formElement);
  }
  cleanupRedundantRoles() {
    const parent = this.domProvider.closest(
      this.formElement,
      `[role=${ROLE$1.FORM}],[role=${ROLE$1.SEARCH}]`
    );
    const elementsToClean = this.domProvider.querySelectorAll(
      `[role=${ROLE$1.FORM}]`,
      this.formElement
    );
    parent && elementsToClean.push(parent);
    elementsToClean.length && Utils$1.removeClientsWCAGAttributes(elementsToClean);
  }
}
const FormApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const formFields = new FormFields(domProvider);
  const statusFixer = new StatusFixer(context, domProvider, changeDetection);
  const errorMessages = new ErrorMessages(domProvider, statusFixer);
  const submitButton = new SubmitButton(
    domProvider,
    errorMessages,
    elementVisibility
  );
  return (props) => new FormFixer(
    context,
    props,
    domProvider,
    changeDetection,
    formFields,
    errorMessages,
    submitButton
  );
};

class TableSorts {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  setAriaSorts(tableElements) {
    const { columnHeaders, rows } = tableElements;
    columnHeaders.forEach((columnHeader, idx) => {
      this.isSortable(columnHeader) && this.setSortOrder(rows, columnHeader, idx);
    });
  }
  isSortable(htmlElement) {
    return !!htmlElement.querySelector(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR);
  }
  setSortOrder(rows, columnHeader, columnHeaderIdx) {
    const columnHeaderCells = Array.from(rows).map(
      (row) => row.children[columnHeaderIdx]
    );
    let ariaValue = "none";
    if (Utils$1.isAsc(columnHeaderCells)) {
      ariaValue = "ascending";
    } else if (Utils$1.isDesc(columnHeaderCells)) {
      ariaValue = "descending";
    }
    this.domProvider.setAttribute(columnHeader, ARIA.SORT, ariaValue);
  }
}

class TableRoles {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  setRoles(tableElements, tableType = "table") {
    const { columnHeaders, rowHeaders, cells, table, rows } = tableElements;
    this.setRoleAttributes([table], tableType === "table" ? ROLE$1.TABLE : ROLE$1.GRID);
    this.setRoleAttributes(cells, tableType === "table" ? ROLE$1.CELL : ROLE$1.GRIDCELL);
    this.setRoleAttributes(rows, ROLE$1.ROW);
    this.setRoleAttributes(rowHeaders, ROLE$1.ROWHEADER);
    this.setRoleAttributes(columnHeaders, ROLE$1.COLUMNHEADER);
  }
  setRoleAttributes(htmlElements, role) {
    this.domProvider.setAttributeToAll(htmlElements, ATTRIBUTES.ROLE, role);
  }
}

class Navigation$1 {
  constructor(domProvider, stateManagement) {
    this.domProvider = domProvider;
    this.stateManagement = stateManagement;
  }
  handleNavigation(gridElements, focusableElements, isRowClickable) {
    const directions = this.getDirections(gridElements);
    const { CELL_INDEX, ROW_INDEX } = PROJECT_ATTRIBUTES;
    this.numerationAttributes(focusableElements, CELL_INDEX);
    this.numerationAttributes(gridElements.rows, ROW_INDEX);
    this.domProvider.setAttribute(focusableElements[0], ATTRIBUTES.TABINDEX, "0");
    this.domProvider.addEventListenerToAll(
      focusableElements,
      "keydown",
      (e) => this.navigate(e, gridElements.table, focusableElements[0], directions, isRowClickable)
    );
  }
  navigate(event, table, tableStartingPoint, directions, isRowClickable) {
    const e = event;
    const el = e.target;
    const { RIGHT, LEFT, ENTER, DOWN, TAB, UP, SPACE, HOME, END, PAGEDOWN, PAGEUP } = KEY_CODES;
    switch (e.code) {
      case RIGHT:
        e.preventDefault();
        this.focusOnFollowingElement(el, table, directions.right);
        break;
      case LEFT:
        e.preventDefault();
        this.focusOnFollowingElement(el, table, directions.left);
        break;
      case UP:
        e.preventDefault();
        this.focusOnFollowingElement(el, table, directions.up);
        break;
      case DOWN:
        e.preventDefault();
        this.focusOnFollowingElement(el, table, directions.down);
        break;
      case (ENTER || SPACE): {
        const clickableEl = this.domProvider.querySelector(CLICKABLE_HTML_ELEMENTS, el);
        if (clickableEl) {
          this.domProvider.dispatchMouseClickEvent(clickableEl);
        } else if (isRowClickable) {
          this.domProvider.getAttribute(el, "role") === ROLE$1.GRIDCELL && this.triggerParentClickEvent(el);
        }
        break;
      }
      case TAB:
        el.setAttribute(ATTRIBUTES.TABINDEX, "-1");
        tableStartingPoint.setAttribute(ATTRIBUTES.TABINDEX, "0");
        break;
      case HOME: {
        const currentCellRowIndex = this.getRowIndex(el);
        const firstCellInRow = table.querySelectorAll(
          `[${PROJECT_ATTRIBUTES.ROW_INDEX}='${currentCellRowIndex}'] [${PROJECT_ATTRIBUTES.CELL_INDEX}]`
        )[0];
        this.focusOnCell(el, firstCellInRow);
        break;
      }
      case END: {
        const currentCellRowIndex = this.getRowIndex(el);
        const cellsInRow = table.querySelectorAll(
          `[${PROJECT_ATTRIBUTES.ROW_INDEX}='${currentCellRowIndex}'] [${PROJECT_ATTRIBUTES.CELL_INDEX}]`
        );
        const lastCellInRow = cellsInRow[cellsInRow.length - 1];
        this.focusOnCell(el, lastCellInRow);
        break;
      }
      case PAGEUP: {
        e.preventDefault();
        const { CELL_INDEX, ROW_INDEX } = PROJECT_ATTRIBUTES;
        const currentRow = this.domProvider.closest(el, `[${ROW_INDEX}]`);
        if (currentRow) {
          const currentCellColumn = Array.from(
            currentRow == null ? void 0 : currentRow.querySelectorAll(`[${CELL_INDEX}]`)
          ).findIndex((cell) => cell === el);
          const rows = table.querySelectorAll(
            `[${PROJECT_ATTRIBUTES.ROW_INDEX}]:has([${CELL_INDEX}])`
          );
          const firstCellInColumn = rows[0].querySelectorAll(`[${CELL_INDEX}]`)[currentCellColumn];
          this.focusOnCell(el, firstCellInColumn);
        }
        break;
      }
      case PAGEDOWN: {
        e.preventDefault();
        const { CELL_INDEX, ROW_INDEX } = PROJECT_ATTRIBUTES;
        const currentRow = this.domProvider.closest(el, `[${ROW_INDEX}]`);
        if (currentRow) {
          const currentCellColumn = Array.from(
            currentRow == null ? void 0 : currentRow.querySelectorAll(`[${CELL_INDEX}]`)
          ).findIndex((cell) => cell === el);
          const rows = table.querySelectorAll(
            `[${PROJECT_ATTRIBUTES.ROW_INDEX}]:has([${CELL_INDEX}])`
          );
          const firstCellInColumn = rows[rows.length - 1].querySelectorAll(
            `[${CELL_INDEX}]`
          )[currentCellColumn];
          this.focusOnCell(el, firstCellInColumn);
        }
        break;
      }
    }
  }
  numerationAttributes(htmlElements, attribute) {
    htmlElements.forEach(
      (htmlElement, index) => htmlElement.setAttribute(attribute, (index + 1).toString())
    );
  }
  getDirections(gridElements) {
    const { rows, cells, columnHeaders, rowHeaders } = gridElements;
    let numOfColumns = Math.ceil(
      (columnHeaders.length + rowHeaders.length + cells.length) / rows.length
    );
    if (rowHeaders.length) {
      numOfColumns -= 1;
    }
    const isRtl = Utils$1.isRtl(this.stateManagement.getLang());
    return {
      right: isRtl ? -1 : 1,
      left: isRtl ? 1 : -1,
      up: -numOfColumns,
      down: numOfColumns
    };
  }
  getRowIndex(cellElement) {
    var _a;
    const { ROW_INDEX } = PROJECT_ATTRIBUTES;
    const rowIndex = (_a = this.domProvider.closest(cellElement, `[${ROW_INDEX}]`)) == null ? void 0 : _a.getAttribute(ROW_INDEX);
    return Number(rowIndex);
  }
  getNextElement(el, table, direction) {
    const { CELL_INDEX, ROW_INDEX } = PROJECT_ATTRIBUTES;
    const currentCell = Number(this.domProvider.getAttribute(el, CELL_INDEX));
    if (Math.abs(direction) === 1) {
      const rowIndex = this.getRowIndex(el);
      return table.querySelector(
        `[${ROW_INDEX}='${rowIndex}'] [${CELL_INDEX}='${currentCell + direction}']`
      );
    } else
      return table.querySelector(`[${CELL_INDEX}='${currentCell + direction}']`);
  }
  focusOnCell(currentCell, toFocusCell) {
    this.domProvider.setAttribute(currentCell, ATTRIBUTES.TABINDEX, "-1");
    this.domProvider.setAttribute(toFocusCell, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.setTimeout(() => this.domProvider.focusOnElement(toFocusCell), 10);
  }
  focusOnFollowingElement(el, table, direction) {
    const elToFocus = this.getNextElement(el, table, direction);
    if (!elToFocus)
      return;
    this.focusOnCell(el, elToFocus);
  }
  triggerParentClickEvent(el) {
    const parentRow = this.domProvider.closest(el, `[${PROJECT_ATTRIBUTES.ROW_INDEX}]`);
    this.domProvider.dispatchMouseClickEvent(parentRow);
  }
}

class GridFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, tableSorts, tableRoles, navigation) {
    super(context, domProvider, changeDetection, props, props.selectors.grid);
    this.tableSorts = tableSorts;
    this.tableRoles = tableRoles;
    this.navigation = navigation;
  }
  fix(grid) {
    const gridElements = this.getGridElements(grid);
    const focusableCells = this.getFocusableCells(gridElements);
    const focusableElements = Utils$1.getFirstTabbableElements(focusableCells);
    this.tableRoles.setRoles(gridElements, "grid");
    this.tableSorts.setAriaSorts(gridElements);
    const { columnHeaders } = gridElements;
    this.handleClickableColumns(grid, columnHeaders);
    this.disableAutoTabbable(gridElements);
    this.avoidTabUnnecessaryElements(grid);
    this.navigation.handleNavigation(gridElements, focusableElements, this.props.isRowClickable);
  }
  handleClickableColumns(grid, columnHeaders) {
    columnHeaders && this.domProvider.addEventListenerToAll(columnHeaders, "keydown", (event) => {
      const e = event;
      if (e.code === "Enter" || e.code === "Space") {
        this.domProvider.dispatchMouseClickEvent(e.target);
        this.fix(grid);
      }
    });
  }
  disableAutoTabbable(gridElements) {
    const { columnHeaders, rowHeaders, cells } = gridElements;
    this.domProvider.setAttributeToAll(
      [...cells, ...columnHeaders, ...rowHeaders],
      AVOID_AUTO_TABBABLE_ATTRIBUTE,
      "true"
    );
  }
  avoidTabUnnecessaryElements(grid) {
    this.domProvider.setAttributeToAll(
      [
        ...Array.from(
          grid.querySelectorAll(COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR)
        )
      ],
      ATTRIBUTES.TABINDEX,
      "-1"
    );
  }
  getFocusableCells(gridElements) {
    const { isColumnHeaderFocusable, isRowHeaderFocusable } = this.props;
    const { columnHeaders, rowHeaders, cells } = gridElements;
    const focusableCells = [];
    if (columnHeaders && columnHeaders.length > 0 && (Utils$1.isContainsTabbable(columnHeaders) || isColumnHeaderFocusable))
      focusableCells.push(...columnHeaders);
    if (rowHeaders && rowHeaders.length > 0 && (Utils$1.isContainsTabbable(rowHeaders) || isRowHeaderFocusable))
      focusableCells.push(...rowHeaders);
    focusableCells.push(...cells);
    return focusableCells;
  }
  getGridElements(grid) {
    const { cell, row, columnheader, rowheader } = this.props.selectors;
    return {
      table: grid,
      rows: this.domProvider.querySelectorAll(row, grid),
      cells: this.domProvider.querySelectorAll(cell, grid),
      columnHeaders: columnheader ? this.domProvider.querySelectorAll(columnheader, grid) : [],
      rowHeaders: rowheader ? this.domProvider.querySelectorAll(rowheader, grid) : []
    };
  }
}

const GridApi = (context = "body") => {
  return (props) => {
    const domProvider = new DomProvider();
    const elementVisibility = new ElementVisibility();
    const changeDetection = ChangeDetection.getInstance(
      elementVisibility,
      domProvider
    );
    const stateManagement = StateManagement.getInstance();
    const tableSorts = new TableSorts(domProvider);
    const tableRoles = new TableRoles(domProvider);
    const navigation = new Navigation$1(domProvider, stateManagement);
    new GridFixer(context, props, domProvider, changeDetection, tableSorts, tableRoles, navigation);
  };
};

var __defProp$3 = Object.defineProperty;
var __getOwnPropDesc$3 = Object.getOwnPropertyDescriptor;
var __decorateClass$3 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$3(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$3(target, key, result);
  return result;
};
let LinkFixer = class extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection) {
    super(context, domProvider, changeDetection, props, props.selectors.element);
  }
  fix() {
    const element = this.props.selectors.element;
    const el = this.domProvider.querySelectorAll(element);
    if (!el)
      return;
    el.forEach((element2) => {
      this.domProvider.setAttribute(element2, "role", ROLE$1.LINK);
      this.domProvider.setAttribute(element2, "tabindex", "0");
      this.handleKeydown(element2);
    });
  }
  handleKeydown(el) {
    this.domProvider.addEventListener(el, "keydown", (e) => {
      const evt = e;
      evt.code === "Enter" && this.domProvider.dispatchMouseClickEvent(el);
    });
  }
};
LinkFixer = __decorateClass$3([
  free
], LinkFixer);
const LinkApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return (props) => new LinkFixer(context, props, domProvider, changeDetection);
};

class ListboxTrigger {
  constructor(domProvider, elementVisibility) {
    this.listboxElement = null;
    this.optionsElements = null;
    this.isOpenListboxEvent = false;
    this.triggerKeydownFunction = (_evt, triggerElement, listboxSelector, props) => {
      const evt = _evt;
      this.isOpenListboxEvent = evt.code == "Enter" || evt.code == "Space" || evt.code == "ArrowDown" && !props.disableDownArrowOpen || evt.code == "ArrowUp" && !props.disableDownArrowOpen;
      this.domProvider.setTimeout(
        () => this.dispatchClickOnTrigger(triggerElement, listboxSelector),
        200
      );
    };
    this.domProvider = domProvider;
    this.elementVisibility = elementVisibility;
  }
  handleTrigger(triggerElement, listboxSelector, props, triggerLabel) {
    if (triggerLabel) {
      let labelID = this.domProvider.getAttribute(triggerLabel, "id");
      if (!labelID) {
        labelID = Utils$1.uuid();
        this.domProvider.setAttribute(triggerLabel, "id", labelID);
      }
      this.domProvider.setAttribute(triggerElement, ARIA.LABELLEDBY, labelID);
    }
    this.domProvider.setAttribute(triggerElement, "tabindex", "0");
    this.domProvider.setAttribute(triggerElement, "role", "button");
    this.domProvider.setAttribute(triggerElement, ARIA.HASPOPUP, "listbox");
    this.domProvider.setAttribute(triggerElement, ARIA.EXPANDED, "false");
    this.domProvider.addEventListener(
      triggerElement,
      "keydown",
      (e) => this.triggerKeydownFunction(e, triggerElement, listboxSelector, props)
    );
  }
  dispatchClickOnTrigger(triggerElement, listboxSelector) {
    this.listboxElement = this.domProvider.querySelector(listboxSelector);
    const isElementVisible = this.elementVisibility.isVisible(this.listboxElement);
    if (this.isOpenListboxEvent) {
      this.domProvider.setAttribute(triggerElement, PROJECT_CLASSES.TRIGGER_ELEMENT, "true");
      if (!isElementVisible && triggerElement) {
        if (!triggerElement.onclick && triggerElement.onmouseover)
          this.domProvider.dispatchMouseOverEvent(triggerElement);
        else
          this.domProvider.dispatchMouseClickEvent(triggerElement);
        this.domProvider.setTimeout(
          () => this.secondClickAttempt(triggerElement, listboxSelector),
          200
        );
      }
    }
  }
  secondClickAttempt(triggerElement, listboxSelector) {
    this.listboxElement = this.domProvider.querySelector(listboxSelector);
    !this.elementVisibility.isVisible(this.listboxElement) && triggerElement ? !triggerElement.onclick && triggerElement.onmouseover ? this.domProvider.dispatchMouseOverEvent(triggerElement) : this.domProvider.dispatchClickEvent(triggerElement) : null;
  }
  focusOnTrigger(triggerSelector) {
    const trigger = this.domProvider.querySelector(`${triggerSelector}`);
    if (trigger) {
      this.domProvider.focusOnElement(trigger);
    }
  }
}

class ListboxList {
  constructor(domProvider, elementVisibility, listboxAttributesHandler, listboxEventHandler) {
    this.triggerElement = null;
    this.listboxElement = null;
    this.optionsElements = null;
    this.listboxLabel = null;
    this.listboxKeydownFunction = (_evt, props) => {
      const evt = _evt;
      if (evt.code !== "Tab") {
        evt.preventDefault();
        evt.stopPropagation();
      }
      const isListboxEvent = this.isListboxEvent(evt.code);
      const isCharPressed = this.isCharPressed(evt.code);
      if ((isListboxEvent || isCharPressed) && this.optionsElements && this.listboxElement && this.triggerElement) {
        this.listboxEventHandler.handleEvents(
          this.optionsElements,
          this.listboxElement,
          this.triggerElement,
          evt,
          this.listboxLabel,
          props
        );
      }
    };
    this.domProvider = domProvider;
    this.elementVisibility = elementVisibility;
    this.listboxAttributesHandler = listboxAttributesHandler;
    this.listboxEventHandler = listboxEventHandler;
  }
  handleListbox(listboxElement, triggerElement, optionsSelector, props, listboxLabel) {
    this.listboxElement = listboxElement;
    this.triggerElement = triggerElement;
    this.listboxLabel = listboxLabel;
    if (listboxLabel) {
      const listboxLabelID = this.domProvider.getAttribute(listboxLabel, "id");
      this.domProvider.setAttribute(listboxElement, ARIA.LABELLEDBY, `${listboxLabelID}`);
    }
    const allElementsNotOptions = this.domProvider.querySelectorAll(
      `*:not(${optionsSelector})`,
      this.listboxElement
    );
    if (allElementsNotOptions && allElementsNotOptions.length) {
      const elementsInsideListboxArray = Array.prototype.slice.call(
        allElementsNotOptions,
        0
      );
      Utils$1.removeClientsWCAGAttributes(elementsInsideListboxArray);
    }
    this.optionsElements = this.domProvider.querySelectorAll(optionsSelector, listboxElement);
    const activeDescendantElementId = this.getActiveDescendantElementId(
      this.listboxElement,
      this.optionsElements,
      this.triggerElement
    );
    this.listboxAttributesHandler.handleAttributes(
      this.listboxElement,
      this.optionsElements,
      this.triggerElement,
      activeDescendantElementId,
      listboxLabel,
      void 0
    );
    this.domProvider.setAttribute(this.listboxElement, ATTRIBUTES.ROLE, ROLE$1.LISTBOX);
    this.domProvider.setAttribute(this.listboxElement, ATTRIBUTES.TABINDEX, "-1");
    this.domProvider.addEventListener(
      this.listboxElement,
      "keydown",
      (e) => this.listboxKeydownFunction(e, props),
      true
    );
  }
  getActiveDescendantElementId(listboxElement, optionsElements, triggerElement) {
    var _a;
    const originalActiveDescendantElementId = this.domProvider.getAttribute(
      listboxElement,
      ARIA.ACTIVEDESCENDANT
    );
    const isActiveDescendantElementInDom = originalActiveDescendantElementId !== null && this.domProvider.getElementById(originalActiveDescendantElementId) !== null;
    if (isActiveDescendantElementInDom) {
      return originalActiveDescendantElementId;
    }
    const targetOptionValue = triggerElement.innerText;
    const targetOption = optionsElements.find((option) => option.innerText === targetOptionValue);
    const activeDescendantElementId = (_a = targetOption == null ? void 0 : targetOption.id) != null ? _a : optionsElements[0].id;
    return activeDescendantElementId;
  }
  isListboxEvent(eventCode) {
    return eventCode == "Enter" || eventCode == "Space" || eventCode == "ArrowDown" || eventCode == "ArrowUp" || eventCode == "Escape" || eventCode == "Tab" || eventCode == "Home" || eventCode == "End";
  }
  isCharPressed(eventCode) {
    return eventCode.substring(0, 3) === "Key";
  }
}

class ListboxFixer extends FixerAbstract {
  constructor(context, props, domProvider, listboxTrigger, listboxList, listboxOption, elementVisibility, changeDetection, listboxEventHandler, listboxAttributesHandler, scrollService) {
    super(context, domProvider, changeDetection, props, props.selectors.trigger);
    this.triggerElements = null;
    this.listboxElement = null;
    this.optionsElements = null;
    this.labelElement = null;
    this.isMobile = false;
    this.domProvider = domProvider;
    this.listboxTrigger = listboxTrigger;
    this.listboxList = listboxList;
    this.listboxOption = listboxOption;
    this.elementVisibility = elementVisibility;
    this.changeDetection = changeDetection;
    this.listboxEventHandler = listboxEventHandler;
    this.listboxAttributesHandler = listboxAttributesHandler;
    this.scrollService = scrollService;
  }
  fix() {
    const { listbox, trigger } = this.props.selectors;
    const label = this.props.selectors.label;
    this.triggerElements = this.domProvider.querySelectorAll(trigger);
    if (!this.triggerElements.length) {
      return;
    }
    if (label) {
      this.labelElement = this.domProvider.querySelector(label);
    }
    this.triggerElements.forEach((trigger2) => {
      this.listboxTrigger.handleTrigger(trigger2, listbox, this.props, this.labelElement);
    });
    this.changeDetection.subscribe(
      this.context,
      "listboxList",
      listbox,
      (visibleElement) => this.onListboxShow(visibleElement)
    );
  }
  onListboxShow(visibleElement) {
    const { listbox, options, trigger, group } = this.props.selectors;
    this.optionsElements = this.domProvider.querySelectorAll(options, listbox);
    const lastPressedTrigger = this.domProvider.querySelector(
      `${trigger}[${PROJECT_CLASSES.TRIGGER_ELEMENT}]`
    );
    this.generateIDForOptions(group);
    if (lastPressedTrigger) {
      this.listboxList.handleListbox(
        visibleElement,
        lastPressedTrigger,
        options,
        this.props,
        this.labelElement
      );
      this.domProvider.setAttribute(lastPressedTrigger, ARIA.EXPANDED, "true");
      if (!(lastPressedTrigger == null ? void 0 : lastPressedTrigger.contains(this.listboxElement)) && this.optionsElements && !(lastPressedTrigger == null ? void 0 : lastPressedTrigger.contains(this.optionsElements[0]))) {
        Utils$1.setRolePresentation(lastPressedTrigger);
      }
      const selectedOptionId = this.domProvider.getAttribute(visibleElement, ARIA.ACTIVEDESCENDANT);
      const selectedOptionElement = this.domProvider.querySelector(`[id=${selectedOptionId}]`);
      selectedOptionElement == null ? void 0 : selectedOptionElement.scrollIntoView();
    }
    if (Utils$1.isMobileDevice()) {
      this.domProvider.focusOnElement(this.optionsElements[0]);
    } else {
      this.domProvider.focusOnElement(visibleElement);
    }
  }
  generateIDForOptions(group) {
    var _a;
    (_a = this.optionsElements) == null ? void 0 : _a.forEach((option) => {
      const optionsElementsID = this.domProvider.getAttribute(option, "id");
      this.listboxOption.handleOption(
        option,
        optionsElementsID != null ? null : Utils$1.uuid(),
        group
      );
      Utils$1.setRolePresentation(option);
    });
  }
}
const ListboxApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const scrollService = new ScrollService();
  const listboxTrigger = new ListboxTrigger(domProvider, elementVisibility);
  const listboxOptions = new ListboxOption(domProvider);
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const attributesHandler = new AttributesHandler(domProvider);
  const eventsHandler = new EventsHandler$1(
    domProvider,
    attributesHandler,
    elementVisibility,
    scrollService
  );
  const listboxList = new ListboxList(
    domProvider,
    elementVisibility,
    attributesHandler,
    eventsHandler
  );
  return (props) => new ListboxFixer(
    context,
    props,
    domProvider,
    listboxTrigger,
    listboxList,
    listboxOptions,
    elementVisibility,
    changeDetection,
    eventsHandler,
    attributesHandler,
    scrollService
  );
};

class MenuItemActions {
  constructor(domProvider, menuActions) {
    this.domProvider = domProvider;
    this.menuActions = menuActions;
    this.setFocusToItem = (item) => {
      const siblingsItems = this.getItemSiblings(item);
      for (let i = 0; i < siblingsItems.length; i++) {
        const currentItem = siblingsItems[i];
        this.domProvider.setAttribute(currentItem.element, "tabindex", "-1");
        const popupMenu = currentItem.popupMenu;
        if (popupMenu) {
          this.menuActions.closeMenu(popupMenu, popupMenu.closeMenu);
        }
      }
      this.domProvider.setAttribute(item.element, "tabindex", "0");
      this.domProvider.focusOnElement(item.element);
    };
    this.getFirstItem = (items) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      return items[0];
    };
    this.getLastItem = (items) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      return items[items.length - 1];
    };
    this.getNextItem = (items, item) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      const index = items.indexOf(item);
      if (index === items.length - 1) {
        return items[0];
      } else {
        return items[index + 1];
      }
    };
    this.getPrevItem = (items, item) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      const index = items.indexOf(item);
      if (index === 0) {
        return items[items.length - 1];
      } else {
        return items[index - 1];
      }
    };
    this.getParentItem = (item) => {
      return item.parentMenu.parentMenuItem;
    };
    this.getItemSiblings = (item) => {
      return item.parentMenu.menuItems;
    };
    this.getParentNextItem = (item) => {
      const parentItem = this.getParentItem(item);
      if (parentItem) {
        const parentItemSiblings = this.getItemSiblings(parentItem);
        return this.getNextItem(parentItemSiblings, parentItem);
      }
    };
    this.getParentPrevItem = (item) => {
      const parentItem = this.getParentItem(item);
      if (parentItem) {
        const parentItemSiblings = this.getItemSiblings(parentItem);
        return this.getPrevItem(parentItemSiblings, parentItem);
      }
    };
    this.getNextItemByChar = (items, item, char) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      let startIndex = items.indexOf(item) + 1;
      if (startIndex === items.length) {
        startIndex = 0;
      }
      let nextItemIndex = this.getNextIndexByChar(items, startIndex, char);
      if (nextItemIndex === -1) {
        nextItemIndex = this.getNextIndexByChar(items, 0, char);
      }
      if (nextItemIndex > -1) {
        return items[nextItemIndex];
      }
      return null;
    };
    this.getNextIndexByChar = (items, startIndex, char) => {
      if (items.length === 0) {
        throw new Error(`no items`);
      }
      char = char.toLowerCase();
      for (let i = startIndex; i < items.length; i++) {
        if (char === items[i].firstChar) {
          return i;
        }
      }
      return -1;
    };
  }
}

const isFixed = "u1st-menu-fixed";
const menuId = "u1st-menu-id";
const keyCodes = {
  TAB: "Tab",
  ENTER: "Enter",
  ESC: "Escape",
  SPACE: "Space",
  PAGEUP: "PageUp",
  PAGEDOWN: "PageDown",
  END: "End",
  HOME: "Home",
  LEFT: "ArrowLeft",
  UP: "ArrowUp",
  RIGHT: "ArrowRight",
  DOWN: "ArrowDown"
};

class MenuItemNavigation {
  constructor(menuActions, menuFocusHandler, domProvider) {
    this.menuActions = menuActions;
    this.menuFocusHandler = menuFocusHandler;
    this.domProvider = domProvider;
  }
  handle(event, item, overrideKeydownEvent) {
    let flag = false;
    const char = event.key;
    if (overrideKeydownEvent) {
      flag = overrideKeydownEvent(event, item);
    }
    if (!flag) {
      switch (event.key) {
        case keyCodes.SPACE:
        case keyCodes.ENTER:
        case keyCodes.DOWN:
          if (item.popupMenu) {
            this.menuActions.openMenu(item.popupMenu, item.popupMenu.openMenu);
            this.domProvider.setTimeout(() => {
              this.menuFocusHandler.setFocusToFirstItem(item.popupMenu);
            }, 1e3);
            flag = true;
          }
          break;
        case keyCodes.LEFT:
          this.menuFocusHandler.setFocusToPreviousItem(item);
          flag = true;
          break;
        case keyCodes.RIGHT:
          this.menuFocusHandler.setFocusToNextItem(item);
          flag = true;
          break;
        case keyCodes.UP:
          if (item.popupMenu) {
            this.menuActions.openMenu(item.popupMenu, item.popupMenu.openMenu);
            this.domProvider.setTimeout(() => {
              this.menuFocusHandler.setFocusToLastItem(item.popupMenu);
            }, 1e3);
            flag = true;
          }
          break;
        case keyCodes.HOME:
        case keyCodes.PAGEUP:
          this.menuFocusHandler.setFocusToFirstItem(item.parentMenu);
          flag = true;
          break;
        case keyCodes.END:
        case keyCodes.PAGEDOWN:
          this.menuFocusHandler.setFocusToLastItem(item.parentMenu);
          flag = true;
          break;
        case keyCodes.TAB:
        case keyCodes.ESC:
          if (item.popupMenu) {
            this.menuActions.closeMenu(item.popupMenu, item.popupMenu.closeMenu);
            event.stopPropagation();
          }
          break;
        default:
          flag = this.menuFocusHandler.setFocusByFirstCharacter(item, char);
          break;
      }
      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }
}

class MobileItemNavigation {
  constructor(menuActions, menuFocusHandler, domProvider) {
    this.menuActions = menuActions;
    this.menuFocusHandler = menuFocusHandler;
    this.domProvider = domProvider;
  }
  handle(event, item, overrideKeydownEvent) {
    let flag = false;
    if (overrideKeydownEvent) {
      flag = overrideKeydownEvent(event, item);
    }
    if (!flag && item.popupMenu) {
      const isExpanded = this.domProvider.getAttribute(item.element, "aria-expanded") === "true";
      if (isExpanded) {
        this.menuActions.closeMenu(item.popupMenu, item.popupMenu.openMenu);
      } else {
        this.menuActions.openMenu(item.popupMenu, item.popupMenu.openMenu);
        this.menuFocusHandler.setFocusToFirstItem(item.popupMenu);
      }
    }
  }
}

class PopupenuItemNavigation {
  constructor(menuActions, menuFocusHandler, domProvider) {
    this.menuActions = menuActions;
    this.menuFocusHandler = menuFocusHandler;
    this.domProvider = domProvider;
  }
  handle(event, item, overrideKeydownEvent) {
    let flag = false;
    const char = event.key;
    if (overrideKeydownEvent) {
      flag = overrideKeydownEvent(event, item);
    }
    if (!flag) {
      switch (event.key) {
        case keyCodes.SPACE:
        case keyCodes.ENTER:
          if (item.popupMenu) {
            this.menuActions.openMenu(item.popupMenu, item.popupMenu.openMenu);
            this.menuFocusHandler.setFocusToFirstItem(item.popupMenu);
          } else {
            this.domProvider.dispatchMouseClickEvent(item.element);
          }
          flag = true;
          break;
        case keyCodes.UP:
          this.menuFocusHandler.setFocusToPreviousItem(item);
          flag = true;
          break;
        case keyCodes.DOWN:
          this.menuFocusHandler.setFocusToNextItem(item);
          flag = true;
          break;
        case keyCodes.LEFT:
          this.menuActions.closeMenu(item.parentMenu, item.parentMenu.closeMenu);
          this.menuFocusHandler.setFocusToPreviousParentItem(item);
          flag = true;
          break;
        case keyCodes.RIGHT:
          if (item.popupMenu) {
            this.menuActions.openMenu(item.popupMenu, item.popupMenu.openMenu);
            this.menuFocusHandler.setFocusToFirstItem(item.popupMenu);
          } else {
            this.menuActions.closeMenu(item.parentMenu, item.parentMenu.closeMenu);
            this.menuFocusHandler.setFocusToNextParentItem(item);
          }
          flag = true;
          break;
        case keyCodes.HOME:
        case keyCodes.PAGEUP:
          this.menuFocusHandler.setFocusToFirstItem(item.parentMenu);
          flag = true;
          break;
        case keyCodes.END:
        case keyCodes.PAGEDOWN:
          this.menuFocusHandler.setFocusToLastItem(item.parentMenu);
          flag = true;
          break;
        case keyCodes.ESC:
        case keyCodes.TAB:
          this.menuFocusHandler.setFocusToParentItem(item);
          this.menuActions.closeMenu(item.parentMenu, item.parentMenu.closeMenu);
          flag = true;
          break;
        default:
          flag = this.menuFocusHandler.setFocusByFirstCharacter(item, char);
          break;
      }
      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }
}

class ItemNavigationProvider {
  constructor(menuActions, menuFocusHandler, domProvider) {
    this.menuActions = menuActions;
    this.menuFocusHandler = menuFocusHandler;
    this.domProvider = domProvider;
  }
  createItemNavigation(isMenuItem) {
    if (Utils$1.isMobileDevice()) {
      return new MobileItemNavigation(this.menuActions, this.menuFocusHandler, this.domProvider);
    } else {
      if (isMenuItem) {
        return new MenuItemNavigation(this.menuActions, this.menuFocusHandler, this.domProvider);
      } else {
        return new PopupenuItemNavigation(
          this.menuActions,
          this.menuFocusHandler,
          this.domProvider
        );
      }
    }
  }
}

class MenuActions {
  constructor(domProvider) {
    this.domProvider = domProvider;
    this.defaultOpen = (menu) => menu.parentMenuItem.element.click();
    this.defaultClose = (menu) => menu.parentMenuItem.element.click();
    this.openMenu = (menu, openMenuFunc) => {
      openMenuFunc ? openMenuFunc(menu) : this.defaultOpen(menu);
      this.domProvider.setAttribute(menu.parentMenuItem.element, "aria-expanded", "true");
    };
    this.closeMenu = (menu, closeMenuFunc) => {
      var _a;
      const parentMenuItemElement = (_a = menu.parentMenuItem) == null ? void 0 : _a.element;
      if (parentMenuItemElement && this.domProvider.getAttribute(parentMenuItemElement, "aria-expanded") === "true") {
        closeMenuFunc ? closeMenuFunc(menu) : this.defaultClose(menu);
        this.domProvider.setAttribute(parentMenuItemElement, "aria-expanded", "false");
      }
    };
  }
}

class MenuItem {
  constructor(props, domProvider, menuItemKeyboardEventHandler, customMenuItemNavigator, menuFactory, parentMenu, isPopupMenuItem) {
    this.props = props;
    this.domProvider = domProvider;
    this.menuItemKeyboardEventHandler = menuItemKeyboardEventHandler;
    this.customMenuItemNavigator = customMenuItemNavigator;
    this.menuFactory = menuFactory;
    this.parentMenu = parentMenu;
    this.isPopupMenuItem = isPopupMenuItem;
    this.isMobile = Utils$1.isMobileDevice();
    this.fix();
  }
  fix() {
    var _a, _b;
    const adjustedSelector = this.props.selector.split(",").map((s) => `${s}:not([${isFixed}='true'])`).join(",");
    this.element = (_a = this.domProvider.querySelector(adjustedSelector, this.parentMenu.element)) != null ? _a : void 0;
    if (this.element) {
      this.domProvider.setAttribute(this.element, "type", "button");
      this.domProvider.setAttribute(this.element, isFixed, "true");
      this.domProvider.setAttribute(this.element, "tabIndex", "-1");
      this.domProvider.setAttribute(this.element, "role", "menuitem");
      this.domProvider.setAttribute(this.element, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
      if (this.props.submenu) {
        try {
          this.popupMenu = this.menuFactory.createMenu(this.props.submenu, this);
          this.domProvider.setAttribute(this.element, "aria-haspopup", "true");
          this.domProvider.setAttribute(this.element, "aria-expanded", "false");
        } catch (e) {
          this.domProvider.setAttribute(this.element, "aria-haspopup", "false");
        }
      } else {
        this.domProvider.setAttribute(this.element, "aria-haspopup", "false");
      }
      this.domProvider.addEventListener(
        this.element,
        this.isMobile ? "click" : "keydown",
        (event) => {
          var _a2, _b2, _c;
          return this.menuItemKeyboardEventHandler.handle(
            this.isMobile ? event : event,
            this,
            ((_c = this.props.overrideKeydownEvent) != null ? _c : (_b2 = (_a2 = this.parentMenu) == null ? void 0 : _a2.props) == null ? void 0 : _b2.hasCustomMenuItem) ? this.customMenuItemNavigator.handle.bind(this.customMenuItemNavigator) : void 0
          );
        }
      );
      this.firstChar = (_b = this.element.textContent) == null ? void 0 : _b.trim().substring(0, 1).toLowerCase();
    } else {
      throw new Error(`cannot find MenubarItem with selector: ${this.props.selector}`);
    }
  }
}

class CustomMenuItemNavigator {
  constructor(menuFocusHandler, domProvider, menuActions, elementVisibility) {
    this.menuFocusHandler = menuFocusHandler;
    this.domProvider = domProvider;
    this.menuActions = menuActions;
    this.elementVisibility = elementVisibility;
    this.tabAbleElementsSelectorsNotMenuItems = COMMON_SELECTORS.TABBABLE_ELEMENTS_SELECTOR.split(
      ","
    ).map((s) => `${s}:not([u1st-menu-fixed]):not([disabled='disabled'])`).join(",");
    this.handleElement = (e) => {
      var _a;
      switch (e.key) {
        case keyCodes.TAB: {
          const allElements = this.getAllTabAbleElemetsNotMenuItems(this.contextMenu);
          if (allElements.length && e.target == allElements[allElements.length - 1]) {
            e.stopPropagation();
            e.preventDefault();
            this.menuFocusHandler.setFocusToFirstItem(this.contextMenu);
          }
          if (((_a = this.allTabAbleElemetsNotMenuItems) == null ? void 0 : _a.length) != allElements.length) {
            this.allTabAbleElemetsNotMenuItems = allElements;
            this.domProvider.addEventListenerToAll(
              this.allTabAbleElemetsNotMenuItems,
              "keydown",
              this.handleElement
            );
          }
          break;
        }
        case keyCodes.ESC: {
          e.stopPropagation();
          e.preventDefault();
          this.menuFocusHandler.setFocusToParentItem(this.contextMenu.menuItems[0]);
          this.menuActions.closeMenu(this.contextMenu, this.contextMenu.closeMenu);
          break;
        }
      }
    };
  }
  handle(e, item) {
    if (e.key == keyCodes.TAB) {
      this.contextMenu = item.parentMenu;
      this.allTabAbleElemetsNotMenuItems = this.getAllTabAbleElemetsNotMenuItems(this.contextMenu);
      if (this.allTabAbleElemetsNotMenuItems.length) {
        this.domProvider.addEventListenerToAll(
          this.allTabAbleElemetsNotMenuItems,
          "keydown",
          this.handleElement
        );
        return true;
      }
    }
    return false;
  }
  getAllTabAbleElemetsNotMenuItems(menu) {
    return this.domProvider.querySelectorAll(this.tabAbleElementsSelectorsNotMenuItems, menu.element).filter((e) => this.elementVisibility.isVisible(e));
  }
}

class Menu extends FixerAbstract {
  constructor(context, props, domProvider, domExtension, changeDetectionProvider, menuFactory, customMenuItemNavigator, parentMenuItem) {
    super(
      context,
      domProvider,
      changeDetectionProvider,
      props,
      props.waitForFixer ? props.waitForFixer : props.selector
    );
    this.domExtension = domExtension;
    this.menuFactory = menuFactory;
    this.customMenuItemNavigator = customMenuItemNavigator;
    this.parentMenuItem = parentMenuItem;
    this.menuItems = [];
    this.isPopupMenu = parentMenuItem ? true : false;
    this.openMenu = props.openMenu;
    this.closeMenu = props.closeMenu;
    this.isMobile = Utils$1.isMobileDevice();
  }
  fix(visibleElement) {
    this.element = visibleElement;
    const allChildren = this.domProvider.querySelectorAll("*", this.element);
    if (this.props.hasCustomMenuItem) {
      this.domExtension.restoreCachedAttributeToAll(allChildren, ["tabindex", "role"]);
    } else {
      const attributesRecord = {
        tabindex: "-1",
        role: "none"
      };
      this.domExtension.setCachedAttributeToAll(allChildren, attributesRecord);
    }
    this.domProvider.setAttribute(this.element, "role", this.isPopupMenu ? "menu" : "menubar");
    this.domProvider.setAttribute(this.element, "aria-label", this.props.description);
    this.domProvider.setAttribute(this.element, isFixed, "true");
    let menuItemProps = [];
    if (typeof this.props.menuItems === "string") {
      const itemsLength = this.domProvider.querySelectorAll(
        this.props.menuItems,
        this.element
      ).length;
      for (let i = 0; i < itemsLength; i++) {
        menuItemProps.push({ selector: `${this.props.menuItems}:not([${isFixed}='true'])` });
      }
    } else {
      menuItemProps = this.props.menuItems;
    }
    menuItemProps.forEach((mi) => {
      try {
        this.menuItems.push(
          this.menuFactory.createMenuItem(mi, this, this.isPopupMenu, this.customMenuItemNavigator)
        );
      } catch (e) {
      }
    });
    if (!this.isPopupMenu && !this.isMobile) {
      this.domProvider.setAttribute(this.menuItems[0].element, "tabindex", "0");
    }
    this.props.afterInit && this.props.afterInit(this);
  }
}

class MenuFactory {
  constructor(context, domProvider, domExtension, changeDetectionProvider, itemNavigationProvider, menuFocusHandler, menuActions, elementVisibilityForCustomItemNavigation) {
    this.context = context;
    this.domProvider = domProvider;
    this.domExtension = domExtension;
    this.changeDetectionProvider = changeDetectionProvider;
    this.itemNavigationProvider = itemNavigationProvider;
    this.menuFocusHandler = menuFocusHandler;
    this.menuActions = menuActions;
    this.elementVisibilityForCustomItemNavigation = elementVisibilityForCustomItemNavigation;
  }
  createMenu(props, parentMenuItem) {
    const customMenuItemNavigator = new CustomMenuItemNavigator(
      this.menuFocusHandler,
      this.domProvider,
      this.menuActions,
      this.elementVisibilityForCustomItemNavigation
    );
    return new Menu(
      this.context,
      props,
      this.domProvider,
      this.domExtension,
      this.changeDetectionProvider,
      this,
      customMenuItemNavigator,
      parentMenuItem
    );
  }
  createMenuItem(props, parentMenu, isPopupMenuItem, customMenuItemNavigator) {
    return new MenuItem(
      props,
      this.domProvider,
      this.itemNavigationProvider.createItemNavigation(!isPopupMenuItem),
      customMenuItemNavigator,
      this,
      parentMenu,
      isPopupMenuItem
    );
  }
}

class MenuFocusHandler {
  constructor(menuItemActions) {
    this.menuItemActions = menuItemActions;
    this.setFocusToFirstItem = (menu) => {
      const siblingsItems = menu.menuItems;
      const firstItem = this.menuItemActions.getFirstItem(siblingsItems);
      this.menuItemActions.setFocusToItem(firstItem);
    };
  }
  setFocusToParentItem(item) {
    const parentItem = this.menuItemActions.getParentItem(item);
    if (parentItem) {
      this.menuItemActions.setFocusToItem(parentItem);
    }
  }
  setFocusToPreviousParentItem(item) {
    const prevParentItem = this.menuItemActions.getParentPrevItem(item);
    if (prevParentItem) {
      this.menuItemActions.setFocusToItem(prevParentItem);
    }
  }
  setFocusToNextParentItem(item) {
    const nextParentItem = this.menuItemActions.getParentNextItem(item);
    if (nextParentItem) {
      this.menuItemActions.setFocusToItem(nextParentItem);
    }
  }
  setFocusToNextItem(item) {
    const siblingsItems = this.menuItemActions.getItemSiblings(item);
    const nextItem = this.menuItemActions.getNextItem(siblingsItems, item);
    this.menuItemActions.setFocusToItem(nextItem);
  }
  setFocusToPreviousItem(item) {
    const siblingsItems = this.menuItemActions.getItemSiblings(item);
    const prevItem = this.menuItemActions.getPrevItem(siblingsItems, item);
    this.menuItemActions.setFocusToItem(prevItem);
  }
  setFocusToLastItem(menu) {
    const siblingsItems = menu.menuItems;
    const lastItem = this.menuItemActions.getLastItem(siblingsItems);
    this.menuItemActions.setFocusToItem(lastItem);
  }
  setFocusByFirstCharacter(item, char) {
    const siblingsItems = this.menuItemActions.getItemSiblings(item);
    const nextItem = this.menuItemActions.getNextItemByChar(siblingsItems, item, char);
    if (nextItem) {
      this.menuItemActions.setFocusToItem(nextItem);
      return true;
    }
    return false;
  }
}

class MenuAdapter$1 {
  constructor(domProvider, menuFactory) {
    this.domProvider = domProvider;
    this.menuFactory = menuFactory;
  }
  createMenu(props) {
    if (this.instanceOfIAdjustedMenuProps(props)) {
      props = this.mapMenu(props);
    }
    this.menuFactory.createMenu(props);
  }
  instanceOfIAdjustedMenuProps(data) {
    return Array.isArray(data) && "itemsWithPopupMenuSelector" in data[0];
  }
  mapMenu(props) {
    const currLvlProps = props[0];
    const uuid = Utils$1.uuid();
    const result = this.initMenu(currLvlProps);
    const menuEl = this.domProvider.querySelector(result.selector);
    this.domProvider.setAttribute(menuEl, menuId, uuid);
    const itemsEl = this.domProvider.querySelectorAll(this.getItemSelector(currLvlProps), menuEl);
    itemsEl.forEach((e) => result.menuItems.push(this.mapItem(props, e)));
    return result;
  }
  getItemSelector(props) {
    let selector = "";
    if (props.itemsWithPopupMenuSelector) {
      selector += props.itemsWithPopupMenuSelector;
    }
    if (props.itemsWithoutPopupMenuSelector) {
      if (selector !== "") {
        selector += ",";
      }
      selector += props.itemsWithoutPopupMenuSelector;
    }
    return selector;
  }
  mapItem(props, element) {
    const currLvlProps = props[0];
    const item = {
      overrideKeydownEvent: currLvlProps.overrideKeydownEvent
    };
    let selector;
    let submenu;
    if (currLvlProps.itemsWithPopupMenuSelector && $(element).is(currLvlProps.itemsWithPopupMenuSelector)) {
      selector = currLvlProps.itemsWithPopupMenuSelector;
      submenu = this.mapMenu(props.filter((p) => p !== currLvlProps));
    } else {
      selector = currLvlProps.itemsWithoutPopupMenuSelector;
    }
    item.selector = selector;
    item.submenu = submenu;
    return item;
  }
  initMenu(props) {
    const waitForFixer = props.absoluteMenuSelector;
    let selector = props.absoluteMenuSelector.split(",").filter((s) => s.trim() !== "").map((s) => `${s}:not([${menuId}])`).join(",");
    selector += "";
    return {
      waitForFixer,
      selector,
      afterInit: props.afterInit,
      openMenu: props.openMenu,
      closeMenu: props.closeMenu,
      description: "",
      hasCustomMenuItem: props.hasCustomMenuItem,
      menuItems: []
    };
  }
}

function MenuApi$1(context = "body") {
  const domProvider = new DomProvider();
  const domExtension = new DomExtensionProvider(domProvider);
  const elementVisibility = new class {
    isVisible(element, avoidAriaHidden) {
      return !!element;
    }
    getVisibleElements(selector, context2) {
      return [];
    }
  }();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const menuActions = new MenuActions(domProvider);
  const menuItemActions = new MenuItemActions(domProvider, menuActions);
  const menuFocusHandler = new MenuFocusHandler(menuItemActions);
  const itemNavigationProvider = new ItemNavigationProvider(
    menuActions,
    menuFocusHandler,
    domProvider
  );
  const elementVisibilityForCustomItemNavigation = new ElementVisibility();
  const menuFactory = new MenuFactory(
    context,
    domProvider,
    domExtension,
    changeDetection,
    itemNavigationProvider,
    menuFocusHandler,
    menuActions,
    elementVisibilityForCustomItemNavigation
  );
  const menuAdapter = new MenuAdapter$1(domProvider, menuFactory);
  return (props) => {
    menuAdapter.createMenu(props);
  };
}

class MenuFixer extends FixerAbstract {
  constructor(context = "body", domProvider, changeDetectionProvider, props, elementVisibility, rolesAndAttributes, navigation, elementsService) {
    super(context, domProvider, changeDetectionProvider, props, props.selectors.menu);
    this.elementVisibility = elementVisibility;
    this.rolesAndAttributes = rolesAndAttributes;
    this.navigation = navigation;
    this.elementsService = elementsService;
  }
  fix(visibleElement) {
    this.props.selectors.submenus && this.observer.subscribe(
      "body",
      MenuFixer.name,
      this.props.selectors.submenus,
      (visibleElement2) => this.fixSubmenu(visibleElement2),
      (visibleElement2) => this.destroySubmenu(visibleElement2)
    );
    this.fixMenu(visibleElement);
  }
  fixMenu(menuElement) {
    const elements = this.getMenuElements(menuElement, this.props.selectors);
    this.rolesAndAttributes.initMenu(elements);
    this.navigation.init(elements, this.props);
    this.elementsService.makeFirstItemTabbable(menuElement);
  }
  fixSubmenu(submenuElement) {
    const elements = this.getSubmenuElements(submenuElement, this.props.selectors);
    this.rolesAndAttributes.initSubmenu(elements);
    this.navigation.init(elements, this.props);
    this.rolesAndAttributes.setSubmenuState(submenuElement, true);
    this.elementsService.focusOnFirstItem(submenuElement);
  }
  destroySubmenu(submenuElement) {
    this.rolesAndAttributes.destroy(submenuElement);
    this.rolesAndAttributes.setSubmenuState(submenuElement, false);
  }
  getMenuElements(menuElement, selectors) {
    return {
      menu: menuElement,
      submenus: [],
      items: this.elementVisibility.getVisibleElements(selectors.items, menuElement)
    };
  }
  getSubmenuElements(submenuElement, selectors) {
    return {
      menu: null,
      submenus: [submenuElement],
      items: this.elementVisibility.getVisibleElements(selectors.items, submenuElement)
    };
  }
}

const ROLE = {
  NAVIGATION: "navigation",
  MENUBAR: "menubar",
  MENUITEM: "menuitem",
  NONE: "none",
  MENU: "menu",
  BUTTON: "button"
};
var KEYBOARD_EVENT_CODE = /* @__PURE__ */ ((KEYBOARD_EVENT_CODE2) => {
  KEYBOARD_EVENT_CODE2["TAB"] = "Tab";
  KEYBOARD_EVENT_CODE2["ENTER"] = "Enter";
  KEYBOARD_EVENT_CODE2["ESC"] = "Escape";
  KEYBOARD_EVENT_CODE2["SPACE"] = " ";
  KEYBOARD_EVENT_CODE2["PAGEUP"] = "PageUp";
  KEYBOARD_EVENT_CODE2["PAGEDOWN"] = "PageDown";
  KEYBOARD_EVENT_CODE2["END"] = "End";
  KEYBOARD_EVENT_CODE2["HOME"] = "Home";
  KEYBOARD_EVENT_CODE2["LEFT"] = "ArrowLeft";
  KEYBOARD_EVENT_CODE2["UP"] = "ArrowUp";
  KEYBOARD_EVENT_CODE2["RIGHT"] = "ArrowRight";
  KEYBOARD_EVENT_CODE2["DOWN"] = "ArrowDown";
  return KEYBOARD_EVENT_CODE2;
})(KEYBOARD_EVENT_CODE || {});
var SELECTORS = /* @__PURE__ */ ((SELECTORS2) => {
  SELECTORS2["TRIGGER"] = "u1st-trigger";
  SELECTORS2["MENU_X"] = "u1st-x";
  SELECTORS2["MENU_Y"] = "u1st-y";
  return SELECTORS2;
})(SELECTORS || {});
var OPEN_BY = /* @__PURE__ */ ((OPEN_BY2) => {
  OPEN_BY2["MOUSE_OVER"] = "mouseover";
  OPEN_BY2["MOUSE_OUT"] = "mouseout";
  OPEN_BY2["MOUSE_ENTER"] = "mouseenter";
  OPEN_BY2["MOUSE_LEAVE"] = "mouseleave";
  OPEN_BY2["FOCUS"] = "focus";
  OPEN_BY2["CLICK"] = "click";
  OPEN_BY2["FUNCTION"] = "function";
  return OPEN_BY2;
})(OPEN_BY || {});

class RolesAndAttributes {
  constructor(domProvider, props, context) {
    this.domProvider = domProvider;
    this.props = props;
    this.context = context;
  }
  initMenu(elements) {
    this.setRoles(elements);
    this.setAttributes(elements, this.props.menuDescription);
    this.setMenuNumbering(elements.items);
  }
  initSubmenu(elements) {
    this.setRoles(elements);
    this.setAttributes(elements, this.props.menuDescription);
    this.setSubmenuNumbering(elements.items);
  }
  destroy(htmlElement) {
    this.cleanLevels(htmlElement);
  }
  addTriggerSelector(item) {
    this.domProvider.setAttribute(item, SELECTORS.TRIGGER, "");
  }
  removeTriggerSelector(item) {
    this.domProvider.removeAttribute(item, SELECTORS.TRIGGER);
  }
  cleanTriggerSelectors(context) {
    const triggeredItems = this.domProvider.querySelectorAll(
      `[${SELECTORS.TRIGGER}]`,
      context ? context : this.context
    );
    this.domProvider.removeAttributeToAll(triggeredItems, SELECTORS.TRIGGER);
  }
  moveFocus(fromElement, toElement) {
    if (this.props.menubar) {
      this.moveTabbable(fromElement, toElement);
    }
    this.domProvider.focusOnElement(toElement, true);
  }
  addTabbable(element) {
    this.domProvider.setAttribute(element, ATTRIBUTES.TABINDEX, "0");
  }
  setSubmenuState(submenu, open) {
    this.domProvider.setAttribute(submenu, ARIA.EXPANDED, `${open}`);
  }
  removeTabbable(element) {
    this.domProvider.setAttribute(element, ATTRIBUTES.TABINDEX, "-1");
  }
  moveTabbable(fromElement, toElement) {
    this.removeTabbable(fromElement);
    this.addTabbable(toElement);
  }
  setRoles(elements) {
    const { menu, submenus, items } = elements;
    if (this.props.menubar) {
      this.domProvider.setAttribute(menu, ATTRIBUTES.ROLE, ROLE.MENUBAR);
      this.domProvider.setAttributeToAll(items, ATTRIBUTES.ROLE, ROLE.MENUITEM);
    } else {
      const triggers = this.props.selectors.triggers;
      triggers && items.forEach(
        (item) => this.domProvider.matches(item, triggers) && this.domProvider.setAttribute(item, ATTRIBUTES.ROLE, ROLE.BUTTON)
      );
      this.domProvider.setAttribute(menu, ATTRIBUTES.ROLE, ROLE.NAVIGATION);
    }
    this.props.selectors.submenus && this.domProvider.setAttributeToAll(submenus, ATTRIBUTES.ROLE, ROLE.MENU);
  }
  setAttributes(elements, menuDescription) {
    const { menu, items, submenus } = elements;
    const { triggers } = this.props.selectors;
    menuDescription && this.domProvider.setAttribute(menu, ARIA.LABEL, menuDescription);
    if (this.props.menubar) {
      this.domProvider.setAttributeToAll(items, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
      this.domProvider.setAttributeToAll(items, ATTRIBUTES.TABINDEX, "-1");
    } else {
      this.domProvider.setAttributeToAll(items, ATTRIBUTES.TABINDEX, "0");
    }
    if (triggers) {
      items.forEach(
        (item) => this.domProvider.matches(item, triggers) && this.domProvider.setAttribute(item, ARIA.HASPOPUP, "true")
      );
    }
    this.props.selectors.submenus && submenus.forEach((submenu) => this.setSubmenuState(submenu, false));
  }
  setSubmenuNumbering(items) {
    const menuLevel = this.getMaxLevel() + 1;
    this.setNumbering(items, menuLevel);
  }
  setMenuNumbering(items) {
    this.setNumbering(items, 0);
  }
  setNumbering(items, y) {
    items.forEach((item, index) => {
      this.domProvider.setAttribute(item, SELECTORS.MENU_Y, y.toString());
      this.domProvider.setAttribute(item, SELECTORS.MENU_X, index.toString());
    });
  }
  cleanLevels(htmlElement) {
    const elements = Array.from(
      this.domProvider.querySelectorAll(`[${SELECTORS.MENU_X}], [${SELECTORS.MENU_Y}]`, htmlElement)
    );
    elements.forEach((element) => {
      this.domProvider.removeAttribute(element, SELECTORS.MENU_X);
      this.domProvider.removeAttribute(element, SELECTORS.MENU_Y);
    });
  }
  getMaxLevel() {
    let maxLevel = 0;
    const { menu, submenus } = this.props.selectors;
    const items = this.domProvider.querySelectorAll(
      `[${SELECTORS.MENU_Y}]`,
      menu + "," + submenus
    );
    items.forEach((item) => {
      const itemLevel = Number(this.domProvider.getAttribute(item, SELECTORS.MENU_Y));
      maxLevel = itemLevel > maxLevel ? itemLevel : maxLevel;
    });
    return maxLevel;
  }
}

var EventType$1 = /* @__PURE__ */ ((EventType2) => {
  EventType2["KEYDOWN"] = "keydown";
  EventType2["KEYUP"] = "keyup";
  EventType2["KEYPRESS"] = "keypress";
  EventType2["CLICK"] = "click";
  return EventType2;
})(EventType$1 || {});

class Navigation {
  constructor(domProvider, elementsService, stateManagement) {
    this.domProvider = domProvider;
    this.elementsService = elementsService;
    this.stateManagement = stateManagement;
  }
  init(elements, props) {
    const { menubar, selectors, customEvent } = props;
    const { triggers } = selectors;
    const { items } = elements;
    const eventType = customEvent != null ? customEvent : EventType$1.KEYDOWN;
    this.domProvider.addEventListenerToAll(
      items,
      eventType,
      (evt) => {
        const keyboardEvent = evt;
        keyboardEvent.stopPropagation();
        keyboardEvent.stopImmediatePropagation();
        this.preventScrolling(keyboardEvent);
        if (menubar) {
          this.navigateMenubarItem(
            keyboardEvent.target,
            keyboardEvent.key
          );
        } else {
          this.navigateMenuItem(keyboardEvent);
        }
      },
      true
    );
    if (!menubar && triggers) {
      items == null ? void 0 : items.forEach((item) => {
        if (this.domProvider.matches(item, triggers)) {
          this.domProvider.addEventListener(
            item,
            "click",
            (evt) => {
              if (Utils$1.isAssistiveTechEvent(evt)) {
                evt.preventDefault();
                evt.stopPropagation();
                this.elementsService.triggerItem(item, true);
              }
            },
            true
          );
        }
      });
    }
  }
  navigateMenuItem(keyboardEvent) {
    const item = keyboardEvent.target;
    const action = keyboardEvent.key;
    const isReversed = Utils$1.isRtl(this.stateManagement.getLang());
    const { DOWN, SPACE, ESC, UP, LEFT, RIGHT, ENTER, TAB } = KEYBOARD_EVENT_CODE;
    switch (action) {
      case TAB:
        if (keyboardEvent.shiftKey) {
          if (isReversed) {
            this.elementsService.focusNextItem(item, isReversed, keyboardEvent);
          } else {
            this.elementsService.focusPrevItem(item, isReversed, keyboardEvent);
          }
        } else {
          if (isReversed) {
            this.elementsService.focusPrevItem(item, isReversed, keyboardEvent);
          } else {
            this.elementsService.focusNextItem(item, isReversed, keyboardEvent);
          }
        }
        break;
      case UP:
        this.elementsService.focusPrevItem(item);
        break;
      case LEFT:
        this.elementsService.focusPrevItem(item);
        break;
      case DOWN:
        this.elementsService.focusNextItem(item);
        break;
      case RIGHT:
        this.elementsService.focusNextItem(item, isReversed);
        break;
      case SPACE:
      case ENTER:
        this.elementsService.triggerItem(item, true);
        break;
      case ESC:
        this.handleEsc(item);
        break;
    }
  }
  navigateMenubarItem(item, action) {
    const { DOWN, SPACE, ESC, UP, LEFT, RIGHT, ENTER, TAB } = KEYBOARD_EVENT_CODE;
    switch (action) {
      case UP:
        this.handleUp(item);
        break;
      case DOWN:
        this.handleDown(item);
        break;
      case RIGHT:
        this.handleLeftRight(item, true);
        break;
      case LEFT:
        this.handleLeftRight(item, false);
        break;
      case SPACE:
      case ENTER:
        this.elementsService.triggerItem(item, true);
        break;
      case ESC:
      case TAB:
        this.handleEsc(item);
        break;
    }
  }
  handleUp(item) {
    if (!this.elementsService.isInsideHorizontalMenu(item)) {
      this.elementsService.focusPrevItem(item);
    }
  }
  handleDown(item) {
    const isVertical = !this.elementsService.isInsideHorizontalMenu(item);
    const shouldTriggerDown = isVertical || this.elementsService.hasSubmenu(item);
    if (shouldTriggerDown) {
      if (this.elementsService.isInsideHorizontalMenu(item)) {
        this.elementsService.triggerItem(item, true);
      } else {
        this.elementsService.focusNextItem(item);
      }
    }
  }
  handleLeftRight(item, isRight) {
    const isReversed = Utils$1.isRtl(this.stateManagement.getLang());
    if (this.elementsService.isInsideHorizontalMenu(item)) {
      if (isRight) {
        this.elementsService.focusNextItem(item, isReversed);
      } else {
        this.elementsService.focusPrevItem(item, isReversed);
      }
    } else {
      const shouldGoRight = isRight != isReversed;
      if (shouldGoRight && this.elementsService.hasSubmenu(item)) {
        this.elementsService.openSubmenu(item);
      } else if (!shouldGoRight && this.elementsService.isItemTriggeredBySubmenuItem(item)) {
        this.handleEsc(item);
      } else {
        const shouldJumpRight = isRight != isReversed;
        this.elementsService.jumpToNextMainMenu(item, shouldJumpRight);
      }
    }
  }
  handleEsc(item) {
    this.elementsService.closeSubmenu(item, true);
  }
  preventScrolling(keyboardEvent) {
    const code = keyboardEvent.key;
    const { DOWN, UP, LEFT, RIGHT, ENTER, SPACE, ESC } = KEYBOARD_EVENT_CODE;
    if (code === DOWN || code === UP || code === LEFT || code === RIGHT || code === ENTER || code === SPACE || code === ESC) {
      keyboardEvent.preventDefault();
    }
  }
}

class ElementsService {
  constructor(rolesAndAttributes, domProvider, props, context) {
    this.rolesAndAttributes = rolesAndAttributes;
    this.domProvider = domProvider;
    this.props = props;
    this.context = context;
  }
  hasSubmenu(item) {
    return this.domProvider.matches(item, this.props.selectors.triggers);
  }
  openSubmenu(item) {
    this.triggerItem(item, true);
  }
  closeSubmenu(item, shouldMoveFocus) {
    var _a;
    const submenuTriggerItem = this.getTriggeredItemByItem(item);
    if (!submenuTriggerItem) {
      throw new Error("Submenu must have a trigger element");
    }
    this.rolesAndAttributes.cleanTriggerSelectors((_a = submenuTriggerItem.parentElement) != null ? _a : void 0);
    this.triggerItem(submenuTriggerItem, false);
    if (shouldMoveFocus) {
      this.rolesAndAttributes.moveFocus(item, submenuTriggerItem);
    }
    return submenuTriggerItem;
  }
  isInsideHorizontalMenu(item) {
    let isHorizontal = false;
    const parentMenu = this.getParentMenu(item);
    if (parentMenu) {
      isHorizontal = this.isHorizontal(parentMenu);
    }
    return isHorizontal;
  }
  focusPrevItem(item, isReversed, event) {
    const itemToFocus = this.getPrevItem(item, isReversed, event);
    this.rolesAndAttributes.cleanTriggerSelectors(item);
    if (itemToFocus) {
      event == null ? void 0 : event.preventDefault();
      this.rolesAndAttributes.moveFocus(item, itemToFocus);
    }
  }
  focusNextItem(item, isReversed, event) {
    const itemToFocus = this.getNextItem(item, isReversed, event);
    this.rolesAndAttributes.cleanTriggerSelectors(item);
    if (itemToFocus) {
      event == null ? void 0 : event.preventDefault();
      this.rolesAndAttributes.moveFocus(item, itemToFocus);
    }
  }
  jumpToNextMainMenu(item, shouldJumpRight) {
    while (this.extractXY(item)[1] !== 0) {
      item = this.closeSubmenu(item, true);
    }
    const nextItem = shouldJumpRight ? this.getNextItem(item) : this.getPrevItem(item);
    if (nextItem) {
      if (this.hasSubmenu(nextItem)) {
        this.triggerItem(nextItem, true);
      } else {
        this.rolesAndAttributes.moveFocus(item, nextItem);
      }
    }
  }
  isItemTriggeredBySubmenuItem(item) {
    const submenuTriggerItem = this.getTriggeredItemByItem(item);
    if (!submenuTriggerItem) {
      return false;
    }
    const [, y] = this.extractXY(submenuTriggerItem);
    return y !== 0;
  }
  focusOnFirstItem(menu) {
    const firstItem = this.domProvider.querySelector(
      `[${SELECTORS.MENU_X}="${0}"]`,
      menu
    );
    this.rolesAndAttributes.moveFocus(menu, firstItem);
  }
  makeFirstItemTabbable(menu) {
    const firstItem = this.domProvider.querySelector(
      `[${SELECTORS.MENU_X}="${0}"]`,
      menu
    );
    if (firstItem) {
      this.rolesAndAttributes.addTabbable(firstItem);
    }
  }
  triggerItem(item, toOpen) {
    const openBy = this.openBy(item);
    const { FOCUS, MOUSE_OVER, MOUSE_OUT, MOUSE_ENTER, MOUSE_LEAVE, CLICK } = OPEN_BY;
    const { openByFunc, closeByFunc } = this.props;
    if (toOpen && openByFunc) {
      openByFunc(item);
    } else if (!toOpen && closeByFunc) {
      closeByFunc(item);
    } else {
      switch (openBy) {
        case FOCUS:
          this.domProvider.focusOnElement(item);
          break;
        case MOUSE_OVER:
          if (toOpen) {
            this.domProvider.dispatchMouseEvent(item, MOUSE_OVER);
          } else {
            this.domProvider.dispatchMouseEvent(item, MOUSE_OUT);
          }
          break;
        case MOUSE_ENTER:
          if (toOpen) {
            this.domProvider.dispatchMouseEvent(item, MOUSE_ENTER);
          } else {
            this.domProvider.dispatchMouseEvent(item, MOUSE_LEAVE);
          }
          break;
        case CLICK:
          this.domProvider.dispatchMouseClickEvent(item);
          break;
      }
    }
    if (toOpen) {
      this.rolesAndAttributes.addTriggerSelector(item);
    } else {
      this.rolesAndAttributes.removeTriggerSelector(item);
    }
  }
  getNextItem(item, isReversed, event) {
    const [currentX, currentY] = this.extractXY(item);
    const newIndex = isReversed ? currentX - 1 : currentX + 1;
    const itemAmount = this.getItemAmount(currentY, item);
    const isLastItem = currentX === itemAmount - 1;
    return isLastItem && (event == null ? void 0 : event.key) === KEYBOARD_EVENT_CODE.TAB ? null : this.getItem(item, currentX, currentY, newIndex, isReversed ? -1 : 1);
  }
  getPrevItem(item, isReversed, event) {
    const [currentX, currentY] = this.extractXY(item);
    const newIndex = isReversed ? currentX + 1 : currentX - 1;
    const isFirstItem = currentX === 0;
    return isFirstItem && (event == null ? void 0 : event.key) === KEYBOARD_EVENT_CODE.TAB && event.shiftKey ? null : this.getItem(item, currentX, currentY, newIndex, isReversed ? 1 : -1);
  }
  shouldGoBackToTrigger(currentX, itemAmount, nextIndex) {
    const isFirstItem = currentX === 0;
    const isLastItem = currentX === itemAmount - 1;
    return nextIndex > 0 && isLastItem || nextIndex < 0 && isFirstItem;
  }
  getItem(item, currentX, currentY, newIndex, nextIndex) {
    let x, y, context;
    const itemAmount = this.getItemAmount(currentY, item);
    const isNavigation = !this.props.menubar;
    if (isNavigation && this.shouldGoBackToTrigger(currentX, itemAmount, nextIndex)) {
      context = this.props.selectors.menu;
      if (currentY > 0) {
        const [triggerX, triggerY] = this.extractTriggerXY();
        x = triggerX + nextIndex;
        y = triggerY;
        this.closeSubmenu(item, false);
      } else {
        x = currentX;
        y = currentY;
      }
    } else {
      x = Utils$1.mod(newIndex, itemAmount);
      y = currentY;
    }
    return this.getItemWithXY(x, y, item, context);
  }
  extractTriggerXY() {
    const trigger = this.domProvider.querySelector(`[${SELECTORS.TRIGGER}]`, this.context);
    return this.extractXY(trigger);
  }
  getItemAmount(y, item) {
    const context = this.getParentMenu(item);
    const items = this.domProvider.querySelectorAll(
      `[${SELECTORS.MENU_Y}="${y}"]`,
      context
    );
    return items.length;
  }
  extractXY(item) {
    const x = Number(this.domProvider.getAttribute(item, SELECTORS.MENU_X));
    const y = Number(this.domProvider.getAttribute(item, SELECTORS.MENU_Y));
    return [x, y];
  }
  getTriggeredItemByItem(item) {
    const [, y] = this.extractXY(item);
    return this.getTriggeredItem(y - 1);
  }
  getTriggeredItem(y) {
    return this.domProvider.querySelector(
      `[${SELECTORS.TRIGGER}][${SELECTORS.MENU_Y}="${y}"]`,
      this.context
    );
  }
  getParentMenu(item) {
    return this.domProvider.closest(
      item,
      `[${ATTRIBUTES.ROLE}="${ROLE.MENUBAR}"], [${ATTRIBUTES.ROLE}="${ROLE.MENU}"], [${ATTRIBUTES.ROLE}="${ROLE.NAVIGATION}"]`
    );
  }
  getItemWithXY(x, y, item, contextSelector) {
    const context = contextSelector != null ? contextSelector : this.getParentMenu(item);
    const itemToFocus = this.domProvider.querySelectorAll(`[${SELECTORS.MENU_Y}="${y}"]`, context).filter((item2) => {
      return this.domProvider.getAttribute(item2, SELECTORS.MENU_X) === x.toString();
    })[0];
    return itemToFocus;
  }
  isHorizontal(menu) {
    if (!this.props.selectors.horizontalMenu) {
      return false;
    }
    return this.domProvider.matches(menu, this.props.selectors.horizontalMenu);
  }
  openBy(item) {
    const { openByFocus, openByMouseover, openByMouseenter } = this.props.selectors;
    const { FOCUS, MOUSE_OVER, MOUSE_ENTER, CLICK } = OPEN_BY;
    if (openByFocus && this.domProvider.matches(item, openByFocus)) {
      return FOCUS;
    } else if (openByMouseover && this.domProvider.matches(item, openByMouseover)) {
      return MOUSE_OVER;
    } else if (openByMouseenter && this.domProvider.matches(item, openByMouseenter)) {
      return MOUSE_ENTER;
    }
    return CLICK;
  }
}

function MenuApi(context = "body") {
  return (props) => {
    const domProvider = new DomProvider();
    const elementVisibility = new ElementVisibility();
    const changeDetection = ChangeDetection.getInstance(
      elementVisibility,
      domProvider
    );
    const rolesAndAttributes = new RolesAndAttributes(
      domProvider,
      props,
      context
    );
    const elementsService = new ElementsService(
      rolesAndAttributes,
      domProvider,
      props,
      context
    );
    const stateManagement = StateManagement.getInstance();
    const navigation = new Navigation(domProvider, elementsService, stateManagement);
    new MenuFixer(
      context,
      domProvider,
      changeDetection,
      props,
      elementVisibility,
      rolesAndAttributes,
      navigation,
      elementsService
    );
  };
}

class EventsHandler {
  constructor(domProvider, stateManagement) {
    this.domProvider = domProvider;
    this.stateManagement = stateManagement;
  }
  navigate(e, context, length) {
    this.context = context;
    this.numOfRadios = length;
    const evt = e;
    const el = e.target;
    const currentAriaChecked = this.domProvider.getAttribute(el, ARIA.CHECKED);
    switch (evt.code) {
      case "ArrowRight":
        this.focusOnFollowingElement(el, evt, this.getDirectionByLanguage(1 /* NEXT */));
        break;
      case "ArrowDown":
        this.focusOnFollowingElement(el, evt, 1 /* NEXT */);
        break;
      case "ArrowLeft":
        this.focusOnFollowingElement(el, evt, this.getDirectionByLanguage(-1 /* PREVIOUS */));
        break;
      case "ArrowUp":
        this.focusOnFollowingElement(el, evt, -1 /* PREVIOUS */);
        break;
      case "Enter":
      case "Space":
        !currentAriaChecked || currentAriaChecked == "false" ? this.triggerClick(el, evt) : evt.preventDefault();
        break;
    }
  }
  getDirectionByLanguage(direction) {
    const isRtl = Utils$1.isRtl(this.stateManagement.getLang());
    return isRtl ? direction * -1 : direction;
  }
  focusOnFollowingElement(currentEl, evt, direction) {
    const currentIndex = this.domProvider.getAttribute(currentEl, PROJECT_ATTRIBUTES.RADIO_INDEX);
    if (!currentIndex)
      return;
    let nextIndex = Number(currentIndex) + direction;
    if (nextIndex > Number(this.numOfRadios) - 1)
      nextIndex = 0;
    else if (nextIndex < 0)
      nextIndex = Number(this.numOfRadios) - 1;
    const elToFocus = this.domProvider.querySelector(
      `[${PROJECT_ATTRIBUTES.RADIO_INDEX}=${nextIndex}]`,
      this.context
    );
    if (!elToFocus)
      return;
    this.domProvider.setAttribute(elToFocus, ATTRIBUTES.TABINDEX, "0");
    this.domProvider.setAttribute(currentEl, ATTRIBUTES.TABINDEX, "-1");
    this.domProvider.setTimeout(() => this.domProvider.focusOnElement(elToFocus), 10);
    this.triggerClick(elToFocus, evt);
  }
  triggerClick(el, evt) {
    this.domProvider.dispatchClickEvent(el);
    evt.preventDefault();
  }
}

var __defProp$2 = Object.defineProperty;
var __getOwnPropDesc$2 = Object.getOwnPropertyDescriptor;
var __decorateClass$2 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$2(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$2(target, key, result);
  return result;
};
let RadioFixer = class extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, toggleStateService, eventsHandler, elementVisibility) {
    super(context, domProvider, changeDetection, props, props.selectors.radioGroup);
    this.toggleStateService = toggleStateService;
    this.eventsHandler = eventsHandler;
    this.elementVisibility = elementVisibility;
  }
  fix(visibleElement) {
    const radios = this.domProvider.querySelectorAll(
      this.props.selectors.radioButton,
      visibleElement
    );
    this.handleRadio(visibleElement, radios);
    if (this.props.dynamic) {
      this.handleDynamic(visibleElement, radios);
    }
  }
  handleRadio(visibleElement, radios) {
    const { checkedState, uncheckedState, exclude } = this.props.selectors;
    const elsToExclude = this.domProvider.querySelectorAll(exclude, visibleElement);
    this.domProvider.setAttributeToAll(elsToExclude, PROJECT_CLASSES.EXCLUDE_ATTRIBUTE, "true");
    this.domProvider.setAttribute(visibleElement, ATTRIBUTES.ROLE, ROLE$1.RADIOGROUP);
    const stateProps = {
      selectors: {
        trueState: checkedState,
        falseState: uncheckedState,
        element: this.props.selectors.element
      }
    };
    radios.forEach((element, i) => {
      this.domProvider.setAttribute(element, ATTRIBUTES.ROLE, ROLE$1.RADIO);
      this.domProvider.setAttribute(element, PROJECT_ATTRIBUTES.RADIO_INDEX, i.toString());
      this.domProvider.setAttribute(element, ATTRIBUTES.TABINDEX, "-1");
      this.domProvider.setAttribute(element, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
      this.domProvider.setAttribute(element, AVOID_AUTO_HIDDEN_ATTRIBUTE, "true");
      this.domProvider.addEventListener(
        element,
        "keydown",
        (e) => this.eventsHandler.navigate(e, visibleElement, radios.length)
      );
      const siblings = radios.filter((el) => element !== el);
      checkedState && uncheckedState && this.toggleStateService.fix(element, stateProps, ARIA.CHECKED, siblings);
      this.domProvider.addEventListener(element, "click", (e) => {
        radios.forEach((element2) => {
          this.domProvider.setAttribute(
            element2,
            ATTRIBUTES.TABINDEX,
            element2 === e.target ? "0" : "-1"
          );
        });
      });
    });
    this.handleFocusTabindex(radios, this.getCheckedRadio(radios));
    const nativeInputs = this.domProvider.querySelectorAll(
      TAG_NAMES.INPUT,
      visibleElement
    );
    this.handleExcludeFocusFromNativeInputs(nativeInputs, radios);
  }
  handleFocusTabindex(elements, defaultChecked) {
    const focused = defaultChecked != null ? defaultChecked : elements[0];
    this.domProvider.setAttribute(focused, ATTRIBUTES.TABINDEX, "0");
  }
  getCheckedRadio(radios) {
    for (let i = 0; i < radios.length; i++) {
      const attr = this.domProvider.getAttribute(radios[i], ARIA.CHECKED);
      if (!attr)
        return radios[i];
    }
    return null;
  }
  handleExcludeFocusFromNativeInputs(inputs, radios) {
    inputs.length > 0 && radios.length > 0 && !Array(inputs).some((input) => Array(radios).includes(input)) && inputs.forEach(
      (input) => input && !this.elementVisibility.isVisible(input) && (input.style.visibility = "hidden")
    );
  }
  handleDynamic(visibleElement, radios) {
    this.domProvider.addEventListenerToAll(radios, "click", (e) => {
      const clickedRadioIndex = this.domProvider.getAttribute(
        e.target,
        PROJECT_ATTRIBUTES.RADIO_INDEX
      );
      if (clickedRadioIndex) {
        this.domProvider.setTimeout(() => {
          const newRadios = this.domProvider.querySelectorAll(
            this.props.selectors.radioButton,
            visibleElement
          );
          this.fix(visibleElement);
          newRadios.forEach((radio, i) => {
            const isClickable = i === Number(clickedRadioIndex);
            const tabindex = isClickable ? "0" : "-1";
            this.domProvider.setAttribute(radio, ATTRIBUTES.TABINDEX, tabindex);
            isClickable && this.domProvider.setTimeout(() => this.domProvider.focusOnElement(radio), 100);
          });
        }, 100);
      }
    });
  }
};
RadioFixer = __decorateClass$2([
  free
], RadioFixer);
const RadioApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const toggleStateService = new ToggleStateService(domProvider, true);
  const eventsHandler = new EventsHandler(domProvider, StateManagement.getInstance());
  return (props) => new RadioFixer(
    context,
    props,
    domProvider,
    changeDetection,
    toggleStateService,
    eventsHandler,
    elementVisibility
  );
};

class TableFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, tableSorts, tableRoles) {
    super(context, domProvider, changeDetection, props, props.selectors.table);
    this.changeDetection = changeDetection;
    this.tableSorts = tableSorts;
    this.tableRoles = tableRoles;
    this.handtleTableElements = (tableElements) => {
      this.tableRoles.setRoles(tableElements, "table");
      this.tableSorts.setAriaSorts(tableElements);
    };
  }
  fix(table) {
    const { cell, row, columnheader, rowheader } = this.props.selectors;
    const tableElements = {
      table,
      rows: this.domProvider.querySelectorAll(row, table),
      cells: this.domProvider.querySelectorAll(cell, table),
      columnHeaders: this.domProvider.querySelectorAll(columnheader, table),
      rowHeaders: this.domProvider.querySelectorAll(rowheader, table)
    };
    this.handtleTableElements(tableElements);
    this.changeDetection.subscribe(this.context, "table-row", this.props.selectors.row, () => {
      this.handtleTableElements(tableElements);
    });
  }
}
const TableApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return (props) => {
    const tableSorts = new TableSorts(domProvider);
    const tableRoles = new TableRoles(domProvider);
    new TableFixer(context, props, domProvider, changeDetection, tableSorts, tableRoles);
  };
};

class FieldsAndMethods extends FixerAbstract {
}

class AttributeInitialization extends FieldsAndMethods {
  initializeIds() {
    [...this.tabElements, ...this.tabPanelElements, this.descriptorElement].filter(
      (element) => element instanceof HTMLElement && this.domProvider.getAttribute(element, "id") === null
    ).forEach((element) => this.domProvider.setAttribute(element, "id", Utils$1.uuid()));
  }
  initializeRoles() {
    this.tabElements.forEach(this.setRole(ROLE$1.TAB));
    this.tabPanelElements.forEach(this.setRole(ROLE$1.TABPANEL));
    this.setRole(ROLE$1.TABLIST)(this.tabListElement);
  }
  initializeTabListLabel() {
    this.descriptorElement instanceof HTMLElement && this.domProvider.setAttribute(
      this.tabListElement,
      "aria-labeledby",
      this.domProvider.getAttribute(this.descriptorElement, "id")
    );
  }
  initializeActiveElementStates() {
    this.setTabIndexes(this.tabElements, this.tabPanelElements);
    this.setTabControlStates();
  }
  initializeTabRelationships(isConditionalRendering, activeTabIndex) {
    if (!isConditionalRendering) {
      this.tabElements.forEach((tabElement, i) => {
        this.domProvider.setAttribute(
          tabElement,
          "aria-controls",
          this.domProvider.getAttribute(this.tabPanelElements[i], "id")
        );
      });
    } else {
      this.domProvider.setAttribute(
        this.tabElements[activeTabIndex],
        "aria-controls",
        this.domProvider.getAttribute(this.tabPanelElements[0], "id")
      );
    }
  }
}

class KeyboardNavigation extends FieldsAndMethods {
  setTabElementTabIndexAndFocus() {
    this.setTabIndexes(this.tabElements);
    this.domProvider.focusOnElement(this.tabElements[this.activeTabIndex]);
  }
  handleClickEvents({ target }, isDelayedUpdate = false) {
    const tabElementIndex = this.tabElements.indexOf(target);
    this.activeTabIndex = tabElementIndex > 0 ? tabElementIndex : this.tabElements.indexOf(this.getParentTabElement(target));
    this.activeTabPanelIndex = this.activeTabIndex;
    const updateTabControls = () => {
      this.setTabElementTabIndexAndFocus();
      this.setTabIndexes(this.tabPanelElements);
      this.setTabControlStates();
    };
    if (isDelayedUpdate) {
      setTimeout(updateTabControls, 1);
    } else {
      updateTabControls();
    }
  }
  handleNextNavigationKeyPress() {
    this.activeTabIndex = this.activeTabIndex === this.lastTabIndex ? 0 : this.activeTabIndex + 1;
  }
  handlePreviousNavigationKeyPress() {
    this.activeTabIndex = this.activeTabIndex === 0 ? this.lastTabIndex : this.activeTabIndex - 1;
  }
  getHandleKeyDownEvents([previousNavigationKey, nextNavigationKey]) {
    return (event) => {
      const { code } = event;
      if (code === previousNavigationKey || code === nextNavigationKey) {
        event.stopPropagation();
        code === previousNavigationKey ? this.handlePreviousNavigationKeyPress() : this.handleNextNavigationKeyPress();
        this.setTabElementTabIndexAndFocus();
      }
      if (code === keyCodes.ENTER || code === keyCodes.SPACE) {
        this.domProvider.dispatchClickEvent(event.target);
        event.preventDefault();
      }
    };
  }
  addKeyDownKeyboardHandler() {
    const horizontalKeys = Utils$1.isRtl(this.stateManagement.getLang()) ? [keyCodes.RIGHT, keyCodes.LEFT] : [keyCodes.LEFT, keyCodes.RIGHT];
    const navigationKeys = this.isVertical ? [keyCodes.UP, keyCodes.DOWN] : horizontalKeys;
    this.domProvider.addEventListenerToAll(
      this.tabElements,
      "keydown",
      this.getHandleKeyDownEvents(navigationKeys)
    );
  }
  addClickHandler(isDelayedUpdate = false) {
    this.domProvider.addEventListenerToAll(
      this.tabElements,
      "click",
      (e) => this.handleClickEvents(e, isDelayedUpdate)
    );
  }
  // below logic ensures that the active tab is set to the one corresponding to the open tab panel
  addFocusOutHandler() {
    this.domProvider.addEventListenerToAll(this.tabElements, "focusout", (e) => {
      const { relatedTarget } = e;
      if (relatedTarget instanceof HTMLElement && this.tabElements.indexOf(relatedTarget) === -1 || relatedTarget == null) {
        this.activeTabIndex = this.activeTabPanelIndex;
        this.setTabIndexes(this.tabElements);
      }
    });
  }
}

class Utils extends FieldsAndMethods {
  generateNewTabsSelectors() {
    const { tabSelectors, tabPanelSelectors } = this.props;
    const initialTabSelector = tabSelectors[0].split(":eq")[0];
    const initialPanelSelector = tabPanelSelectors[0].split(":eq")[0];
    this.props.tabSelectors = Array.from(
      document.querySelectorAll(`${this.context} ${initialTabSelector}`)
    ).map((_, i) => `${initialTabSelector}:eq(${i})`);
    this.props.tabPanelSelectors = Array.from(
      document.querySelectorAll(`${this.context} ${initialTabSelector}`)
    ).map((_, i) => `${initialPanelSelector}:eq(${i})`);
  }
  getElement(selector) {
    const element = this.domProvider.querySelector(selector, this.context);
    if (element === null) {
      throw new Error(`Selector '${selector}' returned null.`);
    }
    return element;
  }
  setRole(role) {
    return (element) => this.domProvider.setAttribute(element, "role", role);
  }
  setTabIndexes(...elementsArray) {
    elementsArray.forEach((elements) => {
      const isElementsSame = elements.every((element) => element === elements[0]);
      elements.forEach((element, i) => {
        this.domProvider.setAttribute(element, AVOID_AUTO_TABBABLE_ATTRIBUTE, "true");
        this.domProvider.setAttribute(
          element,
          "tabindex",
          isElementsSame ? "0" : i === this.activeTabIndex ? "0" : "-1"
        );
      });
    });
  }
  setTabControlStates() {
    this.setAriaSelected();
    this.setAriaHidden();
  }
  setAriaHidden() {
    this.tabPanelElements.forEach((panelElement) => {
      const isVisible = this.elementVisibility.isVisible(panelElement);
      this.domProvider.setAttribute(panelElement, ARIA.HIDDEN, `${!isVisible}`);
    });
  }
  setAriaSelected() {
    this.tabElements.forEach(
      (tabElement, i) => this.domProvider.setAttribute(tabElement, ARIA.SELECTED, `${i === this.activeTabIndex}`)
    );
  }
  getParentTabElement(element) {
    const [tabElement] = this.tabElements.filter((tabElement2) => tabElement2.contains(element));
    return tabElement;
  }
}

var __defProp$1 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __decorateClass$1 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$1(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$1(target, key, result);
  return result;
};
class TestService {
}
let TabsFixer = class extends FixerAbstract {
  constructor(context, tabsProps, domProvider = new DomProvider(), changeDetection = ChangeDetection.getInstance(new ElementVisibility(), domProvider)) {
    super(context, domProvider, changeDetection, tabsProps, tabsProps.tabListSelector);
    this.activeTabIndex = 0;
    this.lastTabIndex = 0;
    this.isDelayedUpdate = false;
    this.tabElements = [];
    this.tabPanelElements = [];
    this.tabListElement = {};
    this.descriptorElement = {};
    this.isConditionalRendering = false;
    this.elementVisibility = new ElementVisibility();
    const { tabSelectors, tabPanelSelectors, isVertical } = tabsProps;
    if (tabSelectors.length !== tabPanelSelectors.length) {
      throw new Error(
        "There must be the same number of Tab element selectors as Tab Panel element selectors."
      );
    }
    this.isVertical = isVertical != null ? isVertical : false;
  }
  initializeElements() {
    const { tabSelectors, tabPanelSelectors, tabListSelector, descriptorElementSelector } = this.props;
    this.tabElements = tabSelectors.map(this.getElement.bind(this));
    tabPanelSelectors.forEach((tabPanelSelector, idx) => {
      const el = this.domProvider.querySelector(tabPanelSelector);
      if (!el && idx >= 1)
        this.isConditionalRendering = true;
    });
    this.tabPanelElements = this.isConditionalRendering ? [this.getElement(tabPanelSelectors[0])] : tabPanelSelectors.map(this.getElement.bind(this));
    this.tabListElement = this.getElement(tabListSelector);
    if (descriptorElementSelector !== void 0) {
      this.descriptorElement = this.getElement(descriptorElementSelector);
    }
  }
  activateMutationObserver() {
    const targetNodes = this.domProvider.querySelectorAll(this.props.tabListSelector);
    const config = { childList: true, subtree: true };
    const callback = (mutationList) => {
      let hasChildMutation = false;
      for (const mutation of mutationList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          hasChildMutation = true;
        }
      }
      if (hasChildMutation) {
        this.generateNewTabsSelectors();
        this.handleTabs();
      }
    };
    targetNodes == null ? void 0 : targetNodes.forEach((node) => {
      const observer = new MutationObserver(callback);
      observer.observe(node, config);
    });
  }
  handleTabs() {
    this.activeTabIndex = 0;
    this.activeTabPanelIndex = this.activeTabIndex;
    this.lastTabIndex = this.props.tabSelectors.length - 1;
    this.isDelayedUpdate = this.props.isDelayedUpdate;
    this.initializeElements();
    this.initializeIds();
    this.initializeRoles();
    this.initializeTabListLabel();
    this.initializeActiveElementStates();
    this.initializeTabRelationships(this.isConditionalRendering, this.activeTabIndex);
    this.addClickHandler(this.isDelayedUpdate);
    this.addKeyDownKeyboardHandler();
    this.addFocusOutHandler();
  }
  fix() {
    this.handleTabs();
    if (this.props.isDynamic) {
      this.activateMutationObserver();
    }
  }
};
TabsFixer = __decorateClass$1([
  partials(AttributeInitialization, KeyboardNavigation, Utils),
  inject(TestService)
], TabsFixer);
var TabsFixer$1 = TabsFixer;

const mapToOldProps = (props, context) => {
  if ("selectors" in props) {
    const { tab, tabPanel, tabList, descriptorElement } = props.selectors;
    return {
      tabSelectors: Array.from(document.querySelectorAll(`${context} ${tab}`)).map(
        (_, i) => `${tab}:eq(${i})`
      ),
      tabPanelSelectors: Array.from(document.querySelectorAll(`${context} ${tab}`)).map(
        (_, i) => `${tabPanel}:eq(${i})`
      ),
      tabListSelector: tabList,
      descriptorElementSelector: descriptorElement,
      isVertical: props.isVertical,
      isDynamic: props.isDynamic,
      isDelayedUpdate: props.isDelayedUpdate
    };
  }
  return props;
};

function TabsApi(context = "body") {
  return (props) => {
    props = mapToOldProps(props, context);
    return new TabsFixer$1(context, props);
  };
}

var EventType = /* @__PURE__ */ ((EventType2) => {
  EventType2["KEYDOWN"] = "keydown";
  EventType2["KEYUP"] = "keyup";
  EventType2["KEYPRESS"] = "keypress";
  EventType2["CLICK"] = "click";
  EventType2["MOUSEENTER"] = "mouseenter";
  EventType2["MOUSELEAVE"] = "mouseleave";
  EventType2["MOUSEOVER"] = "mouseover";
  EventType2["MOUSEOUT"] = "mouseout";
  return EventType2;
})(EventType || {});
const CounterEvent = {
  mouseenter: "mouseleave",
  mouseleave: "mouseenter",
  mouseover: "mouseout",
  mouseout: "mouseover"
};

class TooltipFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection) {
    super(context, domProvider, changeDetection, props, props.selectors.trigger);
    this.changeDetection = changeDetection;
  }
  fix() {
    var _a;
    const { trigger, tooltip } = this.props.selectors;
    const customEvent = (_a = this.props.customEvent) != null ? _a : EventType.MOUSEOVER;
    const triggerEls = this.domProvider.querySelectorAll(trigger);
    triggerEls == null ? void 0 : triggerEls.forEach((el) => this.setButton(el, customEvent));
    this.changeDetection.subscribe(
      this.context,
      "tooltip",
      tooltip,
      (visibleTooltip) => this.onTooltipShow(visibleTooltip, triggerEls)
    );
  }
  onTooltipShow(tooltip, triggers) {
    this.domProvider.setAttribute(tooltip, ATTRIBUTES.ROLE, ROLE$1.TOOLTIP);
    this.domProvider.setAttribute(tooltip, STATUS_ATTR, "true");
    Utils$1.getOrCreateId(tooltip);
    triggers == null ? void 0 : triggers.forEach(
      (trigger) => this.domProvider.setAttributeWithID(tooltip, trigger, ARIA.DESCRIBEDBY)
    );
  }
  setButton(element, customEvent) {
    if (!this.domProvider.matches(element, COMMON_SELECTORS.FOCUSABLE_ELEMENTS_SELECTOR)) {
      this.domProvider.setAttribute(element, ATTRIBUTES.ROLE, ROLE$1.BUTTON);
      this.domProvider.setAttribute(element, ATTRIBUTES.TABINDEX, "0");
    }
    this.handleButtonEvents(element, customEvent);
  }
  handleButtonEvents(element, customEvent) {
    const events = [EVENT_TYPES.KEYDOWN, EVENT_TYPES.FOCUS_IN, EVENT_TYPES.FOCUS_OUT];
    events.forEach((evt) => {
      this.domProvider.addEventListener(
        element,
        evt,
        (e) => this.addMouseEvent(e, element, customEvent)
      );
    });
  }
  addMouseEvent(e, el, customEvent) {
    const evt = e;
    if (evt.type == EVENT_TYPES.FOCUS_IN) {
      this.domProvider.dispatchMouseEvent(el, customEvent);
    }
    if (evt.code === KEYBOARD_EVENT_CODE$1.ESC || evt.type == EVENT_TYPES.FOCUS_OUT) {
      this.domProvider.dispatchMouseEvent(el, CounterEvent[customEvent]);
    }
  }
}
const TooltipApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return (props) => new TooltipFixer(context, props, domProvider, changeDetection);
};

var BUTTON_TYPE = /* @__PURE__ */ ((BUTTON_TYPE2) => {
  BUTTON_TYPE2[BUTTON_TYPE2["PAGE_BTN"] = 0] = "PAGE_BTN";
  BUTTON_TYPE2[BUTTON_TYPE2["NEXT_BTN"] = 1] = "NEXT_BTN";
  BUTTON_TYPE2[BUTTON_TYPE2["PREV_BTN"] = 2] = "PREV_BTN";
  BUTTON_TYPE2[BUTTON_TYPE2["SKIP_NEXT"] = 3] = "SKIP_NEXT";
  BUTTON_TYPE2[BUTTON_TYPE2["SKIP_PREV"] = 4] = "SKIP_PREV";
  return BUTTON_TYPE2;
})(BUTTON_TYPE || {});
const PAGINATION = {
  PAGE_INDEX: "u1-page-index"
};

class ButtonsHandler {
  constructor(context, changeDetection, domProvider, translateService, paginationButton) {
    this.context = context;
    this.changeDetection = changeDetection;
    this.domProvider = domProvider;
    this.translateService = translateService;
    this.paginationButton = paginationButton;
  }
  handleButtons(props) {
    const { pageButtons, prevBtn, nextBtn, prevSkip, nextSkip } = props.selectors;
    if (pageButtons) {
      const pageButtonsEls = this.domProvider.querySelectorAll(pageButtons);
      if (pageButtons.length) {
        this.domProvider.setAttribute(pageButtonsEls[0], ARIA.CURRENT, "true");
      } else {
        console.error(`pageButtons Elements are not found`);
      }
      this.changeDetection.subscribe(
        this.context,
        "pageBtn",
        pageButtons,
        () => this.handlePageButtons(BUTTON_TYPE.PAGE_BTN, pageButtons, props)
      );
    } else {
      console.error(`pageButtons Selector is mandatory`);
    }
    if (prevBtn) {
      this.handleArrowButton(BUTTON_TYPE.PREV_BTN, props, prevBtn, TranslateText.PREV_PAGE);
    }
    if (nextBtn) {
      this.handleArrowButton(BUTTON_TYPE.NEXT_BTN, props, nextBtn, TranslateText.NEXT_PAGE);
    }
    if (prevSkip) {
      this.changeDetection.subscribe(
        this.context,
        "prevSkipBtn",
        prevSkip,
        (BtnEl) => this.handleSkipButton(BUTTON_TYPE.SKIP_PREV, props, BtnEl, TranslateText.PREV_SKIP)
      );
    }
    if (nextSkip) {
      this.changeDetection.subscribe(
        this.context,
        "prevSkipBtn",
        nextSkip,
        (BtnEl) => this.handleSkipButton(BUTTON_TYPE.SKIP_NEXT, props, BtnEl, TranslateText.NEXT_SKIP)
      );
    }
  }
  handlePageButtons(direction, pageButtons, props) {
    const pageButtonsEls = this.domProvider.querySelectorAll(pageButtons);
    pageButtonsEls == null ? void 0 : pageButtonsEls.forEach((btn, i) => {
      var _a, _b;
      this.domProvider.setAttribute(btn, PAGINATION.PAGE_INDEX, String(i));
      const pageNumber = (_b = (_a = this.domProvider.getTextContent(btn)) == null ? void 0 : _a.match(/\d+\.\d+|\d+/)) != null ? _b : i + 1;
      this.paginationButton.handlePaginationButton(
        btn,
        `${this.translateService.getValue(TranslateText.PAGE)} ${pageNumber}`,
        props,
        direction
      );
    });
  }
  handleArrowButton(type, props, btnSelector, text) {
    const btnEl = this.domProvider.querySelector(btnSelector);
    if (btnEl) {
      this.paginationButton.handlePaginationButton(
        btnEl,
        this.translateService.getValue(text),
        props,
        type
      );
    } else {
      console.error(`Element with the following selector not found: ${btnSelector}`);
    }
  }
  handleSkipButton(type, props, btnEl, text) {
    var _a;
    if (btnEl) {
      if (!props.skipAmount) {
        console.error("Skip amount must be provided");
      }
      const skipBtnText = this.translateService.getValue(text).replace(
        "{{skipAmount}}",
        String((_a = props.skipAmount) != null ? _a : this.translateService.getValue(TranslateText.FEW))
      );
      this.paginationButton.handlePaginationButton(btnEl, skipBtnText, props, type);
    }
  }
}

class PaginationButton {
  constructor(domProvider, statusFixer, currentPageHandler, focusToFixer, elementVisibility) {
    this.domProvider = domProvider;
    this.statusFixer = statusFixer;
    this.currentPageHandler = currentPageHandler;
    this.focusToFixer = focusToFixer;
    this.elementVisibility = elementVisibility;
  }
  handlePaginationButton(buttonEl, label, props, type) {
    if (buttonEl) {
      this.domProvider.setAttribute(buttonEl, ARIA.LABEL, label);
      this.domProvider.setAttribute(buttonEl, ATTRIBUTES.ROLE, ROLE$1.BUTTON);
      this.domProvider.setAttribute(buttonEl, ATTRIBUTES.TABINDEX, "0");
      this.domProvider.addEventListener(
        buttonEl,
        "keydown",
        (e) => this.addMouseEvent(e, buttonEl)
      );
      this.domProvider.addEventListener(buttonEl, "click", (e) => {
        const isNotDisabled = this.domProvider.getAttribute(e.target, ARIA.DISABLED) !== "true";
        if (isNotDisabled) {
          this.handlePaginationClicked(e, props, type);
        }
      });
    }
  }
  addMouseEvent(e, el) {
    const evt = e;
    if (evt.code === "Enter" || evt.code === "Space") {
      this.domProvider.dispatchMouseClickEvent(el);
    }
  }
  handlePaginationClicked(e, props, type) {
    const prevPage = this.domProvider.querySelector(
      `[${ARIA.CURRENT}]`,
      props.selectors.container
    );
    let newIndex = 0;
    if (type === BUTTON_TYPE.PAGE_BTN) {
      newIndex = this.currentPageHandler.getClickedPageIndex(e.target);
    } else {
      newIndex = this.currentPageHandler.getNextPageIndex(prevPage, type, props.skipAmount);
    }
    this.domProvider.setTimeout(() => {
      this.currentPageHandler.setCurrentPage(newIndex, prevPage);
      this.setPageStatus(props.selectors.container, props.selectors.results);
      if (props.autoFocus) {
        this.focusOnTop(props.selectors.container);
      }
    }, 500);
  }
  focusOnTop(containerSelector) {
    const container = this.domProvider.querySelector(containerSelector);
    container && this.focusToFixer.createAnchorAndFocusTo(container);
  }
  setPageStatus(containerSelector, resultsSelector) {
    let statusText = "Page changed";
    const currentPage = this.domProvider.querySelector(`[${ARIA.CURRENT}]`, containerSelector);
    const label = currentPage && this.domProvider.getAttribute(currentPage, ARIA.LABEL);
    if (label) {
      statusText = `${label}`;
    }
    const results = resultsSelector && this.domProvider.querySelectorAll(resultsSelector);
    if (results == null ? void 0 : results.length) {
      const visibleResults = Array.prototype.slice.call(results, 0).filter((res) => this.elementVisibility.isVisibleOnPage(res));
      statusText += `. Showing ${visibleResults.length} results.`;
    }
    this.statusFixer.announceStatus(statusText);
  }
}

class CurrentPageHandler {
  constructor(domProvider) {
    this.domProvider = domProvider;
  }
  setCurrentPage(index, prevPage) {
    this.domProvider.removeAttribute(prevPage, ARIA.CURRENT);
    const newCurrent = this.domProvider.querySelector(`[${PAGINATION.PAGE_INDEX}="${index}"]`);
    if (newCurrent) {
      this.domProvider.setAttribute(newCurrent, ARIA.CURRENT, "true");
    }
  }
  getClickedPageIndex(clickedEl) {
    return Number(this.domProvider.getAttribute(clickedEl, PAGINATION.PAGE_INDEX));
  }
  getNextPageIndex(prevPage, type, skipAmount) {
    const prevIndex = Number(this.domProvider.getAttribute(prevPage, PAGINATION.PAGE_INDEX));
    return prevIndex + this.getDirection(type, skipAmount);
  }
  getDirection(type, skipAmount) {
    const skip = skipAmount != null ? skipAmount : 0;
    switch (type) {
      case BUTTON_TYPE.NEXT_BTN:
        return 1;
      case BUTTON_TYPE.PREV_BTN:
        return -1;
      case BUTTON_TYPE.SKIP_NEXT:
        return skip;
      case BUTTON_TYPE.SKIP_PREV:
        return -skip;
      case BUTTON_TYPE.PAGE_BTN:
      default:
        return 0;
    }
  }
}

class PaginationFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, buttonsHandler) {
    super(context, domProvider, changeDetection, props, props.selectors.container);
    this.buttonsHandler = buttonsHandler;
  }
  fix() {
    Utils$1.addContextToSelectors(this.props, this.props.selectors.container);
    this.buttonsHandler.handleButtons(this.props);
  }
}
const PaginationApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const stateManagement = StateManagement.getInstance();
  const translateService = new Translate(stateManagement);
  const statusFixer = new StatusFixer(context, domProvider, changeDetection);
  const currentPageHandler = new CurrentPageHandler(domProvider);
  const focusToFixer = new FocusToFixer(context, domProvider, changeDetection);
  const paginationButton = new PaginationButton(
    domProvider,
    statusFixer,
    currentPageHandler,
    focusToFixer,
    elementVisibility
  );
  const buttonsHandler = new ButtonsHandler(
    context,
    changeDetection,
    domProvider,
    translateService,
    paginationButton
  );
  return (props) => {
    new PaginationFixer(context, props, domProvider, changeDetection, buttonsHandler);
  };
};

class LoadingFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection, statusFixer, translateService) {
    super(context, domProvider, changeDetection, props, [props.selectors.loadingBar]);
    this.statusFixer = statusFixer;
    this.translateService = translateService;
  }
  fix(visibleBar) {
    this.domProvider.setAttribute(visibleBar, ATTRIBUTES.ROLE, ROLE$1.METER);
    const loadingText = this.translateService.getValue(TranslateText.LOADING);
    this.statusFixer.announceStatus(loadingText);
  }
}
const LoadingApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  const stateManagement = StateManagement.getInstance();
  const translateService = new Translate(stateManagement);
  const statusFixer = new StatusFixer(context, domProvider, changeDetection);
  return (props) => {
    new LoadingFixer(context, props, domProvider, changeDetection, statusFixer, translateService);
  };
};

class StateFixer extends FixerAbstract {
  constructor(context, attribute, stateProps, domProvider, changeDetection) {
    super(context, domProvider, changeDetection, stateProps, stateProps.selectors.element);
    this.attribute = attribute;
    this.stateProps = stateProps;
  }
  fix(visibleElement) {
    const stateService = new ToggleStateService(this.domProvider, false);
    stateService.fix(visibleElement, this.stateProps, this.attribute);
  }
}
const StateApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return {
    checked: (stateProps) => new StateFixer(context, ARIA.CHECKED, stateProps, domProvider, changeDetection),
    expanded: (stateProps) => new StateFixer(context, ARIA.EXPANDED, stateProps, domProvider, changeDetection),
    pressed: (stateProps) => new StateFixer(context, ARIA.PRESSED, stateProps, domProvider, changeDetection)
  };
};

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
class Localization {
  get() {
    var _a;
    const stateManagement = StateManagement.getInstance();
    const localization = (_a = stateManagement.localization) != null ? _a : window.u1st.localization;
    return (key) => {
      var _a2;
      return (_a2 = localization[key]) != null ? _a2 : "";
    };
  }
}
__decorateClass([
  catchable
], Localization.prototype, "get", 1);
const GetLocalizationApi = () => {
  return new Localization().get();
};

function MenuAdapter(context = "body") {
  return (props) => {
    if ("selectors" in props) {
      MenuApi(context)(props);
    } else {
      MenuApi$1(context)(props);
    }
  };
}

const LandmarkSelectorsSchema = z.object({
  landmark: z.string()
});
const optionalNeverSchema = z.never().optional();
const landmarkSelectorsWithNoLabelForSchema = LandmarkSelectorsSchema.extend({
  labelFor: z.never().optional()
});
const UnlabeledLandmarkSchema = z.object({
  selectors: landmarkSelectorsWithNoLabelForSchema,
  label: optionalNeverSchema
});
const LandmarkWithLabelPropsSchema = z.object({
  selectors: landmarkSelectorsWithNoLabelForSchema,
  label: z.string()
});
const LandmarkWithLabeledByIdsPropsSchema = z.object({
  selectors: LandmarkSelectorsSchema.extend({
    labelFor: z.string()
  }),
  label: optionalNeverSchema
});
const LandmarkPropsSchema = z.union([
  UnlabeledLandmarkSchema,
  LandmarkWithLabelPropsSchema,
  LandmarkWithLabeledByIdsPropsSchema
]);
const LandmarksPropsSchema = z.object({
  banner: LandmarkPropsSchema.optional(),
  complementary: z.array(LandmarkPropsSchema).optional(),
  contentinfo: LandmarkPropsSchema.optional(),
  form: z.array(LandmarkPropsSchema).optional(),
  main: LandmarkPropsSchema.optional(),
  navigation: z.array(LandmarkPropsSchema).optional(),
  search: z.array(LandmarkPropsSchema).optional(),
  application: z.array(LandmarkPropsSchema).optional()
});

const U1_LANDMARKS_ATTRIBUTES = {
  LABEL_FOR: `u1-landmark-label-for`
};

const getLandmarkSelector = (landmarkProps) => landmarkProps.selectors.landmark;
class LandmarksFixer extends FixerAbstract {
  constructor(context, props, domProvider = new DomProvider()) {
    const { error } = LandmarksPropsSchema.safeParse(props);
    if (error) {
      console.error(`Error: `, error.errors);
      throw new Error(`Invalid Landmarks props provided.`);
    }
    const waitForSelectors = Object.values(props).reduce(
      (selectors, propsObjectOrArray) => [
        ...selectors,
        ...Array.isArray(propsObjectOrArray) ? propsObjectOrArray.map(getLandmarkSelector) : [getLandmarkSelector(propsObjectOrArray)]
      ],
      []
    );
    super(
      context,
      domProvider,
      ChangeDetection.getInstance(new ElementVisibility(), domProvider),
      props,
      waitForSelectors
    );
    this.fix = (landmarkElement) => {
      Object.entries(this.props).forEach(([role, propsObjectOrArray]) => {
        if (Array.isArray(propsObjectOrArray)) {
          propsObjectOrArray.forEach(
            (landmarkProps) => this.handleFix(landmarkElement, role, landmarkProps)
          );
        } else {
          const props = propsObjectOrArray;
          this.handleFix(landmarkElement, role, props);
        }
      });
    };
    this.handleFix = (landmarkElement, role, landmarkProps) => {
      const landmarkSelector = getLandmarkSelector(landmarkProps);
      if (landmarkElement.matches(landmarkSelector)) {
        this.addLandmarkRole(role, landmarkProps);
        const landmarkWithLabelValidation = LandmarkWithLabelPropsSchema.safeParse(landmarkProps);
        if (landmarkWithLabelValidation.success) {
          this.addLabel(landmarkWithLabelValidation.data);
        }
        const landmarkWithLabeledByIdsValidation = LandmarkWithLabeledByIdsPropsSchema.safeParse(landmarkProps);
        if (landmarkWithLabeledByIdsValidation.success) {
          this.addLabeledByIds(landmarkWithLabeledByIdsValidation.data);
        }
      }
    };
    this.getLandmarkElement = (landmarkProps) => {
      const landmarkSelector = getLandmarkSelector(landmarkProps);
      const element = this.domProvider.querySelector(landmarkSelector, this.context);
      if (!element) {
        throw new Error(
          `Landmark element with selector \`${landmarkProps.selectors.landmark}\` not found on page.`
        );
      }
      return element;
    };
    this.addLandmarkRole = (role, landmarkProps) => {
      const element = this.getLandmarkElement(landmarkProps);
      element.setAttribute("role", role);
    };
    this.addLabel = (landmarkWithLabelProps) => {
      const element = this.getLandmarkElement(landmarkWithLabelProps);
      element.setAttribute("aria-label", landmarkWithLabelProps.label);
    };
    this.addLabeledByIds = (landmarkWithLabeledByIdsProps) => {
      const element = this.getLandmarkElement(landmarkWithLabeledByIdsProps);
      const labelForSelector = landmarkWithLabeledByIdsProps.selectors.labelFor;
      const labelForElements = this.domProvider.querySelectorAll(labelForSelector);
      if (labelForElements.length === 0) {
        if (labelForSelector.includes(U1_LANDMARKS_ATTRIBUTES.LABEL_FOR)) {
          return;
        }
        throw new Error(
          `Label element with selector \`${landmarkWithLabeledByIdsProps.selectors.labelFor}\` not found on page.`
        );
      }
      if (labelForElements.length > 1) {
        console.info(
          `Multiple label elements found with selector \`${landmarkWithLabeledByIdsProps.selectors.labelFor}\` on page. Please double check that this selector choice was intentional.`
        );
      }
      const [labelForElement] = labelForElements;
      const labeledById = Utils$1.getOrCreateId(labelForElement);
      element.setAttribute(ARIA.LABELLEDBY, labeledById);
    };
  }
}

const LandmarksApi = (context = "body") => (props) => {
  new LandmarksFixer(context, props);
};

class HeadingFixer extends FixerAbstract {
  constructor(context, props, domProvider, changeDetection) {
    super(context, domProvider, changeDetection, props, props.selectors.heading);
  }
  fix(headingElement) {
    this.domProvider.setAttribute(headingElement, ATTRIBUTES.ROLE, ROLE$1.HEADING);
    if (this.props.level) {
      this.domProvider.setAttribute(headingElement, ARIA.LEVEL, this.props.level.toString());
    } else {
      console.error(`props.level is not set: ${this.props}`);
    }
  }
}
const HeadingApi = (context = "body") => {
  const domProvider = new DomProvider();
  const elementVisibility = new ElementVisibility();
  const changeDetection = ChangeDetection.getInstance(
    elementVisibility,
    domProvider
  );
  return (props) => new HeadingFixer(context, props, domProvider, changeDetection);
};

const mutationSubject = new ReplaySubject();
if (RuntimeUtils.isClientSide()) {
  const mutation = new MutationObserver((mutations) => {
    mutations.forEach((mutation2) => mutationSubject.next(mutation2));
  });
  const observeMutations = () => mutation.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true
  });
  if (document.readyState === "complete") {
    observeMutations();
  } else {
    fromEvent(window, "DOMContentLoaded").subscribe(observeMutations);
  }
}
const getMutations$ = () => mutationSubject.asObservable();
const elementTypeGuard = (target) => target instanceof HTMLElement;
const filterByHTMLElement = () => filter(elementTypeGuard);
const fromSelector = (selector) => {
  const elements$ = iif(
    () => document.readyState === "complete",
    from(document.querySelectorAll(selector)),
    fromEvent(window, "load").pipe(mergeMap(() => document.querySelectorAll(selector)))
  ).pipe(filter(elementTypeGuard));
  const childListMutationElementsMatchingSelector$ = getMutations$().pipe(
    mergeMap(
      ({ type, addedNodes, target }) => iif(() => type === "childList", from(Array.from(addedNodes)), of(target))
    ),
    filterByHTMLElement(),
    filter((target) => target.matches(selector))
  );
  return merge(elements$, childListMutationElementsMatchingSelector$).pipe(distinct());
};

if (RuntimeUtils.isClientSide()) {
  Promise.resolve().then(function () { return AccessibilityButton$1; }).then(
    ({ AccessibilityButton: AccessibilityButton2 }) => AccessibilityButton2.initialize()
  );
}
const DEFAULT_U1_CONFIGURATION = {
  visualFocus: {
    style: {
      color: "black"
    }
  }
};
const { isClientSide } = RuntimeUtils;

export { AccessibilityButton, AccordionApi, ButtonApi, CanvasApi, CarouselApi, ChangeDetection, CheckboxApi, ComboboxApi, DEFAULT_U1_CONFIGURATION, DatepickerApi, DialogApi, DialogType, DomProvider, ElementVisibility, FormApi, GetLocalizationApi, GridApi, HeadingApi, LandmarksApi, LinkApi, ListboxApi, LoadingApi, MenuAdapter, MenuApi$1 as MenuApiV1, MenuApi as MenuApiV2, PaginationApi, RadioApi, StateApi, TableApi, TabsApi, ToggleButtonApi, TooltipApi, U1_LANDMARKS_ATTRIBUTES, Utils$1 as Utils, excludeElApi, filterByHTMLElement, focusToApi, fromSelector, getMutations$, isClientSide, setConfiguration as setU1Configuration, setLanguage as setU1Language, skipLinkApi, statusApi, testIsVisibile, visualFocusApi };
